import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api, { plantsAPI, plantPlacementsAPI, projectsAPI, projectPlansAPI } from './services/api';
import { compressImage, formatFileSize } from './utils/imageCompression';
import { convertHeicToJpeg, isHeicFile } from './utils/convertHeic';
import { coerceImageUrl, isValidImageUrl, resolvePlanImageUrlForServerFetch } from './utils/imageUrl';
import ProjectMessages from './components/ProjectMessages';
import ProjectSchedule from './components/ProjectSchedule';
import ProjectFiles from './components/ProjectFiles';
import InviteCollaboratorModal from './components/InviteCollaboratorModal';
import { PlantDetailModal } from './Plants';
import { Upload, Link as LinkIcon, FileText, MessageSquare, Calendar, Users, File, X, Sparkles, Search as SearchIcon, Loader, AlertCircle, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import jsPDF from 'jspdf';
import { generateBaseMapSvg } from './utils/baseMap';
import { getSymbolViewBox, renderSymbolSvgChildren } from './symbols/registry';
import ZoneManager from './handoff/ZoneManager';
import HandoffModal from './handoff/HandoffModal';
import ProjectGardenTab from './handoff/ProjectGardenTab';
import { planZonesApi } from './handoff/zonesApi';

// Debug instrumentation removed; keep no-op to avoid churn in call sites.
function __agentDebugIngest() {}

/** Plan zone overlay on canvas: match ZoneManager normalized 0–100 coords */
function parsePlanZoneShapeJson(raw) {
  if (raw == null) return {};
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return {};
}

const PLAN_ZONE_OVERLAY_STROKES = ['#16a34a', '#2563eb', '#7c3aed', '#ea580c', '#db2777'];

function planZoneHexToFillRgba(hex, alpha = 0.18) {
  const m = /^#([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex || '');
  if (!m) return `rgba(22,163,74,${alpha})`;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${alpha})`;
}

// Client Assignment Form Component
const ClientAssignmentForm = ({ onAssign, existingClient }) => {
  const [clientName, setClientName] = useState(existingClient?.name || '');
  const [clientEmail, setClientEmail] = useState(existingClient?.email || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!clientEmail.trim()) {
      alert('Please enter a client email');
      return;
    }
    onAssign(clientEmail.trim(), clientName.trim() || clientEmail.trim());
    setClientName('');
    setClientEmail('');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <input
          type="text"
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          placeholder="Client Name (optional)"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
        />
      </div>
      <div>
        <input
          type="email"
          value={clientEmail}
          onChange={(e) => setClientEmail(e.target.value)}
          placeholder="Client Email *"
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
        />
      </div>
      <button
        type="submit"
        className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
      >
        {existingClient ? 'Update Client' : 'Assign Client'}
      </button>
    </form>
  );
};

const PlanDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const DEFAULT_SHEET = useMemo(() => ({ width_in: 24, height_in: 36, units: 'in', ppi: 40 }), []);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Project sheet settings (canonical inches; units is display/input preference)
  const [sheet, setSheet] = useState(DEFAULT_SHEET);
  const [sheetDraft, setSheetDraft] = useState(null); // editing buffer for Project Settings modal

  const projectUnits = (sheet?.units || 'in').toString().trim().toLowerCase() === 'cm' ? 'cm' : 'in';
  const inchesToDisplay = (inches) => {
    const n = Number(inches);
    if (!Number.isFinite(n)) return '';
    return projectUnits === 'cm' ? (n * 2.54) : n;
  };
  const displayToInches = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return projectUnits === 'cm' ? (n / 2.54) : n;
  };

  const sheetPx = useMemo(() => {
    const ppi = Number(sheet?.ppi) || 40;
    const wIn = Number(sheet?.width_in) || 24;
    const hIn = Number(sheet?.height_in) || 36;
    return {
      w: Math.round(wIn * ppi),
      h: Math.round(hIn * ppi),
      ppi
    };
  }, [sheet]);

  const autoFitSigRef = useRef('');
  const focusOnceRef = useRef('');
  
  // Plant placement + Project Plant List (project-scoped)
  const [masterListItems, setMasterListItems] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [selectedPlants, setSelectedPlants] = useState([]); // multi-select (project list items, or copied placements)
  const [showPlantSidebar, setShowPlantSidebar] = useState(true); // Default to true
  const [searchTerm, setSearchTerm] = useState(''); // filter Project Plant List
  const [plantTypeFilter, setPlantTypeFilter] = useState(''); // '' | '__missing__' | '<plant_type>'
  const [showPlantMarkers, setShowPlantMarkers] = useState(true); // Toggle to show/hide plant markers on plan
  const [showPlanZoneOverlays, setShowPlanZoneOverlays] = useState(true);

  const availableProjectPlantTypes = useMemo(() => {
    const uniq = new Map(); // norm -> original
    (Array.isArray(masterListItems) ? masterListItems : []).forEach((it) => {
      const raw = (it?.plant_type ?? '').toString().trim();
      if (!raw) return;
      const key = raw.toLowerCase();
      if (!uniq.has(key)) uniq.set(key, raw);
    });
    return Array.from(uniq.values()).sort((a, b) => a.localeCompare(b));
  }, [masterListItems]);

  const filteredMasterListItems = useMemo(() => {
    const norm = (v) => (v == null ? '' : String(v)).trim().toLowerCase();
    const isMissing = (v) => v == null || String(v).trim().length === 0;
    const q = (searchTerm || '').toString().trim().toLowerCase();
    let next = Array.isArray(masterListItems) ? masterListItems : [];

    if (q) {
      next = next.filter((it) => {
        const hay = [
          it?.common_name,
          it?.botanical_name,
          it?.code,
          it?.pot_size,
          it?.notes
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }

    if (plantTypeFilter) {
      next = next.filter((it) => {
        if (plantTypeFilter === '__missing__') return isMissing(it?.plant_type);
        return norm(it?.plant_type) === norm(plantTypeFilter);
      });
    }

    return next;
  }, [masterListItems, searchTerm, plantTypeFilter]);

  // Canvas and zoom state
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [isWorkScale, setIsWorkScale] = useState(false); // set by close-up work mode
  const panGestureRef = useRef(null); // { startX, startY, moved }
  const suppressNextCanvasClickRef = useRef(false);
  const MIN_ZOOM = 0.1;
  const MAX_ZOOM = 6;
  const [rotation, setRotation] = useState(0); // Image rotation in degrees
  const [isPlacingPlant, setIsPlacingPlant] = useState(false);
  const [isPlacingNote, setIsPlacingNote] = useState(false);
  const [isSpotlightPicking, setIsSpotlightPicking] = useState(false);
  const [spotlightTarget, setSpotlightTarget] = useState(null); // {x,y} in percent
  const [selectedPlacementId, setSelectedPlacementId] = useState(null); // For keyboard actions
  const [copiedPlacement, setCopiedPlacement] = useState(null);
  const [editingPlacement, setEditingPlacement] = useState(null);
  const [showPlantSheet, setShowPlantSheet] = useState(false);

  // For the in-plan Project Plant List drawer: how many of each project item is placed on this drawing.
  // Uses the active drawing's placements (already filtered by plan_id).
  const placedByMasterListId = useMemo(() => {
    const byMaster = {};
    for (const p of Array.isArray(placements) ? placements : []) {
      const masterId = p?.master_list_id || null;
      if (!masterId) continue;
      const q = Number(p?.quantity) || 1;
      byMaster[masterId] = (byMaster[masterId] || 0) + q;
    }
    return byMaster;
  }, [placements]);

  // Add from Master Plant List (designer library) -> Project Plant List
  const [showAddFromMasterModal, setShowAddFromMasterModal] = useState(false);
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryItems, setLibraryItems] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [selectedLibraryItems, setSelectedLibraryItems] = useState(new Set());
  const [libraryTypeFilter, setLibraryTypeFilter] = useState(''); // '' | '__missing__' | value
  const [libraryExposureFilter, setLibraryExposureFilter] = useState(''); // '' | '__missing__' | value
  const [addingFromLibrary, setAddingFromLibrary] = useState(false);

  const availableLibraryTypes = useMemo(() => {
    const uniq = new Map(); // norm -> original
    (Array.isArray(libraryItems) ? libraryItems : []).forEach((it) => {
      const raw = (it?.plant_type ?? '').toString().trim();
      if (!raw) return;
      const key = raw.toLowerCase();
      if (!uniq.has(key)) uniq.set(key, raw);
    });
    return Array.from(uniq.values()).sort((a, b) => a.localeCompare(b));
  }, [libraryItems]);

  const availableLibraryExposures = useMemo(() => {
    const uniq = new Map(); // norm -> original
    (Array.isArray(libraryItems) ? libraryItems : []).forEach((it) => {
      const raw = (it?.exposure ?? '').toString().trim();
      if (!raw) return;
      const key = raw.toLowerCase();
      if (!uniq.has(key)) uniq.set(key, raw);
    });
    return Array.from(uniq.values()).sort((a, b) => a.localeCompare(b));
  }, [libraryItems]);

  const visibleLibraryItems = useMemo(() => {
    const norm = (v) => (v == null ? '' : String(v)).trim().toLowerCase();
    const isMissing = (v) => v == null || String(v).trim().length === 0;
    let next = Array.isArray(libraryItems) ? libraryItems : [];
    if (libraryTypeFilter) {
      next = next.filter((it) => {
        if (libraryTypeFilter === '__missing__') return isMissing(it?.plant_type);
        return norm(it?.plant_type) === norm(libraryTypeFilter);
      });
    }
    if (libraryExposureFilter) {
      next = next.filter((it) => {
        if (libraryExposureFilter === '__missing__') return isMissing(it?.exposure);
        return norm(it?.exposure) === norm(libraryExposureFilter);
      });
    }
    return next;
  }, [libraryItems, libraryTypeFilter, libraryExposureFilter]);

  const getApiBaseUrl = () =>
    process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

  const fetchDesignerLibrary = async (query = '') => {
    try {
      setLibraryLoading(true);
      const token = localStorage.getItem('authToken');
      const q = (query || '').toString().trim();
      const qs = q ? `?search=${encodeURIComponent(q)}&limit=200` : `?limit=200`;
      const resp = await fetch(`${getApiBaseUrl()}/designer-master-plant-list${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || 'Failed to load master library');
      setLibraryItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      console.error('fetchDesignerLibrary error:', e);
      setLibraryItems([]);
      alert(e.message || 'Failed to load master library');
    } finally {
      setLibraryLoading(false);
    }
  };

  // Designer Symbol Library (custom SVG symbols)
  const [designerSymbols, setDesignerSymbols] = useState([]);
  const customSymbolsById = useMemo(() => {
    const map = {};
    for (const s of Array.isArray(designerSymbols) ? designerSymbols : []) {
      const key = (s?.slug || '').toString().trim().toLowerCase();
      if (!key) continue;
      map[key] = s;
    }
    return map;
  }, [designerSymbols]);

  const fetchDesignerSymbols = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${getApiBaseUrl()}/designer-symbols`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || 'Failed to load symbols');
      setDesignerSymbols(Array.isArray(data.symbols) ? data.symbols : []);
    } catch (e) {
      console.error('fetchDesignerSymbols error:', e);
      setDesignerSymbols([]);
    }
  };

  useEffect(() => {
    fetchDesignerSymbols();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showAddFromMasterModal) return;
    const t = setTimeout(() => {
      fetchDesignerLibrary(librarySearch);
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddFromMasterModal, librarySearch]);

  const toggleSelectLibraryItem = (itemId) => {
    setSelectedLibraryItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const addSelectedFromLibrary = async () => {
    if (selectedLibraryItems.size === 0) return;
    setAddingFromLibrary(true);
    try {
      const token = localStorage.getItem('authToken');
      const selected = libraryItems.filter((i) => selectedLibraryItems.has(i.id));
      const payloadItems = selected.map((i) => ({
        plant_id: i.plant_id || null,
        designer_master_id: i.id,
        common_name: i.common_name || '',
        botanical_name: i.botanical_name || '',
        quantity: 1,
        pot_size: i.pot_size || '',
        color: i.color || '',
        unit_price: i.unit_price ?? null,
        code: i.code || '',
        width: i.width ?? null,
        height: i.height ?? null,
        exposure: i.exposure || '',
        plant_type: i.plant_type || '',
        notes: i.notes || '',
        image_url: i.image_url || null,
        symbol: i.symbol || null,
        symbol_size_in: i.symbol_size_in ?? null,
        status: 'pending'
      }));

      const resp = await fetch(`${getApiBaseUrl()}/plans/${id}/master-list/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ items: payloadItems })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || data.details || 'Failed to add plants');

      const createdCount = data.created_count || 0;
      const updatedCount = data.updated_count || 0;
      const failedCount = data.failed_count || 0;
      if (failedCount > 0) {
        alert(`Added from Master Plant List.\n✅ Created: ${createdCount}\n🟰 Updated: ${updatedCount}\n❌ Failed: ${failedCount}`);
      }

      setShowAddFromMasterModal(false);
      setLibrarySearch('');
      setLibraryItems([]);
      setSelectedLibraryItems(new Set());
      await loadMasterList();
    } catch (e) {
      console.error('addSelectedFromLibrary error:', e);
      alert(e.message || 'Failed to add plants');
    } finally {
      setAddingFromLibrary(false);
    }
  };

  // Multi-plan drawings (concept/working/planting + custom tags)
  const [projectPlans, setProjectPlans] = useState([]);
  const [activePlanId, setActivePlanId] = useState(null);
  const [planNotes, setPlanNotes] = useState([]); // notes for active plan (loaded later)

  const activePlan =
    projectPlans.find((p) => p.id === activePlanId) ||
    projectPlans[0] ||
    null;
  const effectivePlanImageUrl = activePlan?.file_url || project?.plan_image_url || null;
  const effectivePlanImageUrlIsPdf = useMemo(() => {
    const url = String(effectivePlanImageUrl || '');
    return (
      String(activePlan?.file_type || '').toLowerCase() === 'application/pdf' ||
      /\.pdf(\?|#|$)/i.test(url)
    );
  }, [activePlan?.file_type, effectivePlanImageUrl]);

  // Drag and resize state
  const [draggingPlacement, setDraggingPlacement] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [resizingPlacement, setResizingPlacement] = useState(null);
  const [resizeStart, setResizeStart] = useState({ size: 0, x: 0, y: 0 });
  const [hoveringCorner, setHoveringCorner] = useState(null); // Track which corner is being hovered
  const [showControls, setShowControls] = useState(false); // Only show controls when not dragging

  // Plan note interactions (free-form notes not tied to plants)
  const [draggingNoteId, setDraggingNoteId] = useState(null);
  const [noteDragOffset, setNoteDragOffset] = useState({ x: 0, y: 0 }); // percentage offset inside note box
  const [resizingNoteId, setResizingNoteId] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteEditorValue, setNoteEditorValue] = useState('');
  
  // Sidebar touch drag state (for mobile drag from plant list to canvas)
  const [touchDraggingPlant, setTouchDraggingPlant] = useState(null);
  const [touchDragPosition, setTouchDragPosition] = useState(null);
  
  // Mobile UX state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [plantSheetExpanded, setPlantSheetExpanded] = useState(false);
  const [showControlsHint, setShowControlsHint] = useState(true);

  // Closeable "Tip" overlay (persisted per project)
  const [showInstructionsTip, setShowInstructionsTip] = useState(() => {
    try {
      return localStorage.getItem(`planInstructionsTipDismissed:v1:${id}`) !== '1';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(`planInstructionsTipDismissed:v1:${id}`, showInstructionsTip ? '0' : '1');
    } catch {
      // ignore
    }
  }, [id, showInstructionsTip]);
  
  // Collaboration state
  const [showComments, setShowComments] = useState(false);
  
  // Collaboration drawer sizing (desktop)
  const COLLAPSE_WIDTH = 384; // ~w-96
  const EXPAND_WIDTH = 620;
  const MIN_WIDTH = 320;
  const MAX_WIDTH = 840;
  const WIDTH_STORAGE_KEY = 'collabDrawerWidthV1';
  const [collabDrawerWidth, setCollabDrawerWidth] = useState(() => {
    try {
      const raw = localStorage.getItem(WIDTH_STORAGE_KEY);
      const n = raw ? Number(raw) : NaN;
      if (!Number.isFinite(n)) return COLLAPSE_WIDTH;
      return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, n));
    } catch {
      return COLLAPSE_WIDTH;
    }
  });
  const collabResizeRef = useRef({ resizing: false, startX: 0, startW: COLLAPSE_WIDTH });

  useEffect(() => {
    try {
      localStorage.setItem(WIDTH_STORAGE_KEY, String(collabDrawerWidth));
    } catch {
      // ignore
    }
  }, [collabDrawerWidth]);

  useEffect(() => {
    const onMove = (e) => {
      if (!collabResizeRef.current.resizing) return;
      const x = typeof e.clientX === 'number' ? e.clientX : 0;
      const delta = collabResizeRef.current.startX - x; // drag left => larger
      const viewportMax = Math.max(MIN_WIDTH, window.innerWidth - 320);
      const max = Math.min(MAX_WIDTH, viewportMax);
      const next = Math.max(MIN_WIDTH, Math.min(max, collabResizeRef.current.startW + delta));
      setCollabDrawerWidth(next);
    };
    const onUp = () => {
      if (!collabResizeRef.current.resizing) return;
      collabResizeRef.current.resizing = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleCollabDrawerWidth = () => {
    const viewportMax = Math.max(MIN_WIDTH, window.innerWidth - 320);
    const max = Math.min(MAX_WIDTH, viewportMax);
    setCollabDrawerWidth((prev) => {
      const isExpanded = prev >= 520;
      return isExpanded ? COLLAPSE_WIDTH : Math.min(max, EXPAND_WIDTH);
    });
  };

  const [activeTab, setActiveTab] = useState('notes'); // 'notes', 'messages', 'schedule', 'collaborators', 'files'
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentFilter, setCommentFilter] = useState('all'); // 'all', 'general', 'plants', 'designer', 'client', 'trade'
  const [selectedPlantForComment, setSelectedPlantForComment] = useState(null);
  const [selectedNoteForComment, setSelectedNoteForComment] = useState(null);
  const [hideComments, setHideComments] = useState(false);
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [showProjectSettings, setShowProjectSettings] = useState(false); // For project settings modal
  const [editingProjectName, setEditingProjectName] = useState(''); // For editing project name in settings
  const [showPlanLinkModal, setShowPlanLinkModal] = useState(false); // For plan link modal
  const [planLinkInput, setPlanLinkInput] = useState(''); // Plan link input
  const [newPlanTitle, setNewPlanTitle] = useState('');
  const [newPlanCategory, setNewPlanCategory] = useState('concept');
  const [newPlanTagsInput, setNewPlanTagsInput] = useState(''); // comma-separated tags
  const [creatingPlan, setCreatingPlan] = useState(false);

  // Optional transfer when adding a new drawing (never automatic)
  const [showPlanTransferModal, setShowPlanTransferModal] = useState(false);
  const [transferSourcePlanId, setTransferSourcePlanId] = useState(null);
  const [transferTargetPlanId, setTransferTargetPlanId] = useState(null);
  const [transferCopyNotes, setTransferCopyNotes] = useState(false);
  const [transferCopyPlants, setTransferCopyPlants] = useState(false);
  const [transferSourceNotesCount, setTransferSourceNotesCount] = useState(0);
  const [transferSourcePlantsCount, setTransferSourcePlantsCount] = useState(0);
  const [transferringPlanContent, setTransferringPlanContent] = useState(false);
  const [projectStatus, setProjectStatus] = useState('draft'); // Project status
  const [assignedClient, setAssignedClient] = useState(null); // Assigned client info
  const [showViewMenu, setShowViewMenu] = useState(false); // For View dropdown menu
  const [showDrawingMenu, setShowDrawingMenu] = useState(false); // For Drawings dropdown menu
  const [showPdfPreview, setShowPdfPreview] = useState(false); // For PDF preview modal
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null); // PDF blob URL for preview
  const [pdfFileName, setPdfFileName] = useState(''); // PDF filename for download
  
  // Invite Collaborator Modal state (using shared modal)
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteMode, setInviteMode] = useState('manual'); // 'manual' or 'search'
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteTypeModal, setInviteTypeModal] = useState('trade'); // Default to trade
  const [tradeCategory, setTradeCategory] = useState('contractor');
  const [messageVisibility, setMessageVisibility] = useState('all');
  const [invitedCollaborators, setInvitedCollaborators] = useState([]);
  const [loadingCollaborators, setLoadingCollaborators] = useState(false);
  const [scheduleAccess, setScheduleAccess] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [designerLocation, setDesignerLocation] = useState('');
  
  // Plant card modal state
  const [showPlantCard, setShowPlantCard] = useState(false);
  const [selectedPlantCard, setSelectedPlantCard] = useState(null);

  const [editingPlantCard, setEditingPlantCard] = useState(false);
  const [plantCardEdits, setPlantCardEdits] = useState({});
  const [swappingPlant, setSwappingPlant] = useState(false);
  const [plantSearchTerm, setPlantSearchTerm] = useState('');
  const [plantSearchResults, setPlantSearchResults] = useState([]);
  const [currentPlacementId, setCurrentPlacementId] = useState(null); // Track which placement is being viewed
  
  // AI OCR state
  const [ocrPlotFilter, setOcrPlotFilter] = useState('all'); // 'all', 'matched', 'unmatched'
  const [showOCRModal, setShowOCRModal] = useState(false);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrResults, setOcrResults] = useState(null);
  const [ocrError, setOcrError] = useState('');
  /** Pass project plant names to Gemini for better abbreviation mapping */
  const [ocrUseProjectListContext, setOcrUseProjectListContext] = useState(true);
  /** Restrict candidate matches to Project Plant List rows only */
  const [ocrUseProjectListOnly, setOcrUseProjectListOnly] = useState(true);
  /** Low match-score gate: { detection, match } before calling apply-detections */
  const [ocrPlaceConfirm, setOcrPlaceConfirm] = useState(null);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  // Designer -> homeowner garden handoff
  const [showZoneManager, setShowZoneManager] = useState(false);
  const [showHandoffModal, setShowHandoffModal] = useState(false);
  const [showProjectGardenTab, setShowProjectGardenTab] = useState(false);

  // Auto-hide controls hint after 5 seconds
  useEffect(() => {
    if (showControlsHint && placements.length > 0) {
      const timer = setTimeout(() => {
        setShowControlsHint(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showControlsHint, placements.length]);

  // Keyboard event handler for delete and copy/paste
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Delete selected placement
      if (e.key === 'Delete' && selectedPlacementId) {
        handleDeletePlacement(selectedPlacementId);
      }
      
      // Copy selected placement (Ctrl/Cmd + C)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selectedPlacementId) {
        const placement = placements.find(p => p.id === selectedPlacementId);
        if (placement) {
          setCopiedPlacement(placement);
          console.log('Copied plant:', placement.common_name);
        }
      }
      
      // Paste copied placement (Ctrl/Cmd + V)
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && copiedPlacement && !editingPlacement) {
        e.preventDefault();
        setIsPlacingPlant(true);
        setSelectedPlants([copiedPlacement]); // Use the copied plant for placement
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPlacementId, copiedPlacement, placements, editingPlacement]);

  useEffect(() => {
    loadProject();
    loadProjectPlans();
    loadComments();
    loadMasterList();
  }, [id]);

  useEffect(() => {
    loadPlacements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, activePlanId]);

  const [planZonesList, setPlanZonesList] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!id) return undefined;
    (async () => {
      try {
        const data = await planZonesApi.list(id, { planId: activePlanId || null });
        if (!cancelled) setPlanZonesList(Array.isArray(data.zones) ? data.zones : []);
      } catch {
        if (!cancelled) setPlanZonesList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, activePlanId]);

  const planZoneOverlayItems = useMemo(() => {
    return planZonesList.map((z, i) => {
      const shape = parsePlanZoneShapeJson(z.shape_json);
      const stroke =
        typeof z.color === 'string' && /^#[\da-f]{6}$/i.test(z.color.trim())
          ? z.color.trim()
          : PLAN_ZONE_OVERLAY_STROKES[i % PLAN_ZONE_OVERLAY_STROKES.length];
      const fill = planZoneHexToFillRgba(stroke, 0.18);
      return {
        id: z.id,
        shape_type: z.shape_type === 'polygon' ? 'polygon' : 'rect',
        shape,
        stroke,
        fill,
      };
    });
  }, [planZonesList]);

  const manualGardenZoneName = useCallback(
    (manualPlanZoneId) => {
      if (!manualPlanZoneId) return '';
      const z = planZonesList.find((x) => String(x.id) === String(manualPlanZoneId));
      return z?.name || 'Deleted zone';
    },
    [planZonesList]
  );

  // Detect mobile viewport
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close View menu when clicking outside
  useEffect(() => {
    if (!showViewMenu) return;
    
    const handleClickOutside = (event) => {
      const target = event.target;
      // Check if click is outside the View menu button and dropdown
      if (!target.closest('.view-menu-container')) {
        setShowViewMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showViewMenu]);

  // Close Drawings menu when clicking outside
  useEffect(() => {
    if (!showDrawingMenu) return;

    const handleClickOutside = (event) => {
      const target = event.target;
      if (!target.closest('.drawing-menu-container')) {
        setShowDrawingMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDrawingMenu]);

  const loadProject = async () => {
    try {
      setLoading(true);
      const response = await api.getProject(id);
      setProject(response.project);
      // Set status and client from project
      if (response.project) {
        setProjectStatus(response.project.status || 'draft');
        setEditingProjectName(response.project.name || '');
        // Extract client info and rotation from project_data
        if (response.project.project_data) {
          try {
            const projectData = typeof response.project.project_data === 'string' 
              ? JSON.parse(response.project.project_data) 
              : response.project.project_data;
            if (projectData.client) {
              setAssignedClient(projectData.client);
            }
            if (projectData.sheet) {
              const incoming = projectData.sheet || {};
              const widthIn = Number(incoming.width_in) || 24;
              const heightIn = Number(incoming.height_in) || 36;
              const units = (incoming.units || '').toString().trim().toLowerCase() === 'cm' ? 'cm' : 'in';
              const ppi = Number(incoming.ppi) || 40;
              setSheet({ width_in: widthIn, height_in: heightIn, units, ppi });
            } else {
              setSheet(DEFAULT_SHEET);
            }
            // Load saved rotation
            if (projectData.rotation !== undefined) {
              setRotation(projectData.rotation);
            }
          } catch (e) {
            console.error('Error parsing project_data:', e);
          }
        }
      }
    } catch (err) {
      setError('Failed to load project');
      console.error('Error loading project:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveSheetToProject = async (nextSheet) => {
    try {
      const normalized = {
        width_in: Number(nextSheet?.width_in) || 24,
        height_in: Number(nextSheet?.height_in) || 36,
        units: (nextSheet?.units || '').toString().trim().toLowerCase() === 'cm' ? 'cm' : 'in',
        ppi: Number(nextSheet?.ppi) || 40
      };
      const resp = await projectsAPI.update(id, { project_data: { sheet: normalized } });
      if (resp?.project) setProject(resp.project);
      setSheet(normalized);
      return true;
    } catch (e) {
      console.error('saveSheetToProject error:', e);
      alert(e.message || 'Failed to save sheet settings');
      return false;
    }
  };

  const loadProjectPlans = async () => {
    try {
      const data = await projectPlansAPI.list(id);
      const plans = data?.plans || [];
      setProjectPlans(plans);

      // Restore previously selected plan for this project (if any)
      let preferred = null;
      try {
        preferred = localStorage.getItem(`activePlanId:${id}`) || null;
      } catch {
        preferred = null;
      }

      const firstId = plans[0]?.id || null;
      const nextActive =
        (preferred && plans.some((p) => p.id === preferred) && preferred) ||
        (activePlanId && plans.some((p) => p.id === activePlanId) && activePlanId) ||
        firstId;

      if (nextActive && nextActive !== activePlanId) {
        setActivePlanId(nextActive);
      }
    } catch (e) {
      // If the project_plans table isn't present yet, keep legacy single-plan behavior
      console.warn('loadProjectPlans failed:', e?.message || e);
      setProjectPlans([]);
    }
  };

  const deleteActiveDrawing = async () => {
    const planId = activePlanId || null;
    if (!planId) {
      alert('Please select a drawing first.');
      return;
    }

    const active = (projectPlans || []).find((p) => String(p?.id) === String(planId)) || null;
    const label = (active?.category ? `[${active.category}] ` : '') + (active?.title || 'Drawing');
    const countPlans = Array.isArray(projectPlans) ? projectPlans.length : 0;
    const idx = (projectPlans || []).findIndex((p) => String(p?.id) === String(planId));

    const ok = window.confirm(
      `Delete ${label}?\n\nThis cannot be undone. Notes and plant markers placed on this drawing will also be removed.`
        + (countPlans <= 1 ? `\n\n(This is your last drawing in the project.)` : '')
    );
    if (!ok) return;

    try {
      await projectPlansAPI.delete(planId);

      // Clear preferred plan if it was the deleted one
      try {
        const key = `activePlanId:${id}`;
        const preferred = localStorage.getItem(key);
        if (String(preferred || '') === String(planId)) localStorage.removeItem(key);
      } catch {
        // ignore
      }

      const data = await projectPlansAPI.list(id);
      const plans = data?.plans || [];
      setProjectPlans(plans);

      if (plans.length === 0) {
        setActivePlanId(null);
      } else {
        const nextIndex = Math.max(0, Math.min(Number.isFinite(idx) ? idx : 0, plans.length - 1));
        setActivePlanId(plans[nextIndex]?.id || plans[0]?.id || null);
      }

      setShowDrawingMenu(false);
    } catch (e) {
      console.error('deleteActiveDrawing error:', e);
      alert(e?.message || 'Failed to delete drawing');
    }
  };

  useEffect(() => {
    if (!activePlanId) return;
    try {
      localStorage.setItem(`activePlanId:${id}`, activePlanId);
    } catch {
      // ignore
    }
  }, [id, activePlanId]);

  useEffect(() => {
    const run = async () => {
      if (!activePlanId) {
        setPlanNotes([]);
        return;
      }
      try {
        const data = await projectPlansAPI.listNotes(activePlanId);
        setPlanNotes(data?.notes || []);
      } catch (e) {
        console.warn('loadPlanNotes failed:', e?.message || e);
        setPlanNotes([]);
      }
    };
    run();
  }, [activePlanId]);

  // Project plant list only: GET /plans/:projectId/master-list → rows in
  // master_plant_list for this project (OCR, imports, manual — not the designer-wide library).
  const loadMasterList = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiBaseUrl()}/plans/${id}/master-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load project plant list');
      const data = await response.json();
      setMasterListItems(data.items || []);
    } catch (error) {
      console.error('Error loading project plant list:', error);
    }
  };

  const loadPlacements = async () => {
    try {
      // #region agent log
      __agentDebugIngest({runId:'placeSymbol_run1',hypothesisId:'H3',location:'src/PlanDetail.js:loadPlacements:entry',message:'Loading placements for project/plan',data:{projectId:id,activePlanId:activePlanId||null},timestamp:Date.now()});
      // #endregion

      const data = await plantPlacementsAPI.getByProject(id, { planId: activePlanId });
      // #region agent log
      __agentDebugIngest({runId:'placeSymbol_run1',hypothesisId:'H3',location:'src/PlanDetail.js:loadPlacements:success',message:'Loaded placements',data:{count:Array.isArray(data)?data.length:null,sample:Array.isArray(data)?data.slice(0,3).map((p)=>({id:p?.id,plan_id:p?.plan_id,master_list_id:p?.master_list_id,position_x:p?.position_x,position_y:p?.position_y,notesType:typeof p?.notes})):null},timestamp:Date.now()});
      // #endregion
      setPlacements(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading placements:', error);
      setPlacements([]);
    }
  };

  useEffect(() => {
    // #region agent log
    const uniqueMaster = new Set((Array.isArray(placements) ? placements : []).map((p) => p?.master_list_id).filter(Boolean));
    __agentDebugIngest({runId:'placeSymbol_run1',hypothesisId:'H4',location:'src/PlanDetail.js:placements:effect',message:'Placements state updated',data:{placementsCount:(Array.isArray(placements)?placements.length:null),uniqueMasterListIds:uniqueMaster.size,showPlantMarkers,activePlanId:activePlanId||null},timestamp:Date.now()});
    // #endregion
  }, [placements, showPlantMarkers, activePlanId]);

  useEffect(() => {
    if (!showPlantSheet) return;
    // #region agent log
    const placedCount = (Array.isArray(masterListItems) ? masterListItems : []).filter((it) => (placedByMasterListId[it?.id] || 0) > 0).length;
    __agentDebugIngest({runId:'placeSymbol_run1',hypothesisId:'H3',location:'src/PlanDetail.js:plantSheet:open',message:'Plant Sheet opened',data:{placedItemsCount:placedCount,masterListItemsCount:Array.isArray(masterListItems)?masterListItems.length:null,placementsCount:Array.isArray(placements)?placements.length:null,activePlanId:activePlanId||null},timestamp:Date.now()});
    // #endregion
  }, [showPlantSheet, masterListItems, placements, placedByMasterListId, activePlanId]);

  const loadComments = async () => {
    try {
      setLoadingComments(true);
      const token = localStorage.getItem('authToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      
      const response = await fetch(`${API_BASE_URL}/collaboration/comments/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to load comments');
      const data = await response.json();
      setComments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading comments:', error);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const handlePostComment = async (placementId = null) => {
    if (!newComment.trim() && commentAttachments.length === 0) return;
    
    try {
      const token = localStorage.getItem('authToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      
      const response = await fetch(`${API_BASE_URL}/collaboration/comment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          projectId: id,
          commentText: newComment || '',
          placementId: placementId,
          planNoteId: selectedNoteForComment?.id || null,
          attachments: commentAttachments
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
        throw new Error(errorData.error || 'Failed to post comment');
      }
      
      setNewComment('');
      setCommentAttachments([]);
      setSelectedPlantForComment(null);
      setSelectedNoteForComment(null);
      loadComments(); // Reload comments
    } catch (error) {
      console.error('Error posting comment:', error);
      alert(`Failed to post comment: ${error.message}`);
    }
  };

  // Note visibility controls removed (Client Chat is always homeowner + designer).

  // Invite Collaborator handlers (using shared modal)
  const handleInviteCollaborator = async (projectId) => {
    if (!inviteName.trim() || !inviteEmail.trim()) {
      alert('Please enter both name and email');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('You must be logged in to invite collaborators');
        return;
      }

      const pid = projectId || id;
      if (!pid) {
        alert('Project ID not found');
        return;
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

      const url = inviteTypeModal === 'trade'
        ? `${API_BASE_URL}/collaboration/invite-trade`
        : `${API_BASE_URL}/collaboration/share`;

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      const body = inviteTypeModal === 'trade'
        ? {
            projectId: pid,
            tradeEmail: inviteEmail,
            tradeName: inviteName,
            tradeType: tradeCategory,
            accessLevel: 'comment',
            message: 'You have been invited to collaborate on this project',
            messageVisibility: messageVisibility,
            scheduleAccess: scheduleAccess
          }
        : {
            projectId: pid,
            recipientEmail: inviteEmail,
            recipientName: inviteName,
            accessLevel: 'comment',
            message: 'You have been invited to view and collaborate on this project',
            messageVisibility: messageVisibility,
            scheduleAccess: true
          };

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to invite collaborator');
      }
      
      const data = await response.json();
      const shareToken = data.share?.shareToken || data.trade?.share_token || data.share_token;
      const shareUrl =
        inviteTypeModal === 'trade'
          ? `${window.location.origin}/shared/${shareToken}/communication`
          : `${window.location.origin}/shared/${shareToken}`;
      
      // Reset form
      setInviteEmail('');
      setInviteName('');
      setInviteTypeModal('trade');
      setTradeCategory('contractor');
      setMessageVisibility('all');
      setScheduleAccess(true);
      setShowInviteModal(false);
      
      // Show success
      const emailBody = `Hi ${inviteName},\n\nYou have been invited to collaborate on a project.\n\nPlease click the link below to access:\n\n${shareUrl}\n\nThank you!`;
      const mailtoLink = `mailto:${inviteEmail}?subject=Project Collaboration Invitation&body=${encodeURIComponent(emailBody)}`;
      
      const userChoice = window.confirm(
        `Invitation created!\n\n` +
        `Link: ${shareUrl}\n\n` +
        `Click OK to open your email client with the invitation, or Cancel to just copy the link.`
      );
      
      if (userChoice) {
        window.location.href = mailtoLink;
      } else {
        navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error inviting collaborator:', error);
      alert(error.message || 'Failed to invite collaborator. Please try again.');
    }
  };

  const loadDesignerLocation = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const token = localStorage.getItem('authToken');
      if (token) {
        const response = await fetch(`${API_BASE_URL}/designers/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const profile = await response.json();
          const location = [];
          if (profile.city) location.push(profile.city);
          if (profile.provinceState) location.push(profile.provinceState);
          if (profile.country) location.push(profile.country);
          if (location.length > 0) {
            setDesignerLocation(location.join(', '));
            setSearchLocation(location.join(', '));
          }
        }
      }
    } catch (err) {
      console.error('Error loading designer location:', err);
    }
  };

  const loadInvitedCollaborators = async () => {
    try {
      setLoadingCollaborators(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const authToken = localStorage.getItem('authToken');
      if (!authToken || !id) return;

      // Get both clients and trades
      const [clientsRes, tradesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/collaboration/shares/${id}`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }),
        fetch(`${API_BASE_URL}/collaboration/trades/${id}`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        })
      ]);

      // Get all shares (needed to match with trades and filter clients)
      const allSharesRes = await fetch(`${API_BASE_URL}/collaboration/shares/${id}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const allShares = allSharesRes.ok ? (await allSharesRes.json()).shares || [] : [];
      
      const trades = tradesRes.ok ? (await tradesRes.json()).trades || [] : [];
      
      // Filter clients: only shares that are NOT trades
      const clientShares = allShares.filter(s => s.share_type !== 'trade');
      
      // Get trade share tokens to avoid duplicates
      const tradeShareTokens = new Set(trades.map(t => t.share_token));

      const collaborators = [
        // Clients: only shares that are not trades
        ...clientShares
          .filter(c => !tradeShareTokens.has(c.share_token))
          .map(c => ({
            ...c,
            type: 'client',
            shareToken: c.share_token,
            id: c.id,
            message_visibility: c.message_visibility || 'all',
            schedule_access: c.schedule_access !== undefined ? c.schedule_access : true,
            phone_number: c.phone_number || null,
            notification_method: c.notification_method || 'email'
          })),
        // Trades: match with their shares
        ...trades
          .map(t => {
            let matchingShare = allShares.find(s => s.share_token === t.share_token);
            if (!matchingShare && t.share_token) {
              matchingShare = {
                id: null,
                share_token: t.share_token,
                message_visibility: 'all',
                schedule_access: true
              };
            }
            if (!matchingShare) {
              console.warn(`No matching share found for trade ${t.trade_name} with token ${t.share_token}`);
              return null;
            }
            return {
              ...t,
              type: 'trade',
              recipient_name: t.trade_name,
              recipient_email: t.trade_email,
              shareToken: t.share_token,
              id: matchingShare.id || t.share_id || null,
              message_visibility: matchingShare.message_visibility || t.message_visibility || 'all',
              schedule_access: matchingShare.schedule_access !== undefined ? matchingShare.schedule_access : (t.schedule_access !== undefined ? t.schedule_access : true),
              phone_number: t.phone_number || null,
              notification_method: t.notification_method || 'email'
            };
          })
          .filter(t => t !== null)
      ];

      setInvitedCollaborators(collaborators);
    } catch (error) {
      console.error('Error loading collaborators:', error);
    } finally {
      setLoadingCollaborators(false);
    }
  };

  const handleSearchBusinesses = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      alert('Please enter a business name or type');
      return;
    }

    setSearching(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const token = localStorage.getItem('authToken');
      
      const params = new URLSearchParams({
        query: searchQuery,
        ...(searchLocation && { location: searchLocation }),
        ...(searchType !== 'all' && { type: searchType })
      });

      const response = await fetch(`${API_BASE_URL}/google-business/search?${params}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok) throw new Error('Search failed');
      const data = await response.json();
      setSearchResults(data.results || []);
    } catch (error) {
      console.error('Error searching businesses:', error);
      alert('Failed to search businesses. Please try again.');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleInviteFromSearch = (business) => {
    setInviteName(business.name || '');
    setInviteEmail(business.email || '');
    setInviteTypeModal('trade');
    if (business.business_type || business.type) {
      setTradeCategory(business.business_type || business.type || 'contractor');
    } else if (searchType && searchType !== 'all') {
      setTradeCategory(searchType);
    } else {
      setTradeCategory('contractor');
    }
    setInviteMode('manual');
  };

  const handleAddBusiness = async (businessData) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/google-business/local`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(businessData)
      });

      if (!response.ok) {
        throw new Error('Failed to add business');
      }

      alert('Business added! It will now appear in search results.');
      
      // Refresh search if there was a query
      if (searchQuery.trim()) {
        setSearching(true);
        try {
          const params = new URLSearchParams({
            query: searchQuery,
            ...(searchLocation && { location: searchLocation }),
            ...(searchType !== 'all' && { type: searchType })
          });

          const refreshResponse = await fetch(`${API_BASE_URL}/google-business/search?${params}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });

          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            setSearchResults(refreshData.results || []);
          }
        } catch (err) {
          console.error('Error refreshing search:', err);
        } finally {
          setSearching(false);
        }
      }
    } catch (error) {
      alert('Failed to add business. Please try again.');
      console.error('Error adding business:', error);
      throw error;
    }
  };

  const getPlantComments = (placementId) => {
    return comments.filter(c => c.placement_id === placementId);
  };

  const filteredComments = () => {
    if (commentFilter === 'general') {
      return comments.filter(c => !c.placement_id);
    } else if (commentFilter === 'plants') {
      return comments.filter(c => c.placement_id);
    } else if (commentFilter === 'designer' || commentFilter === 'client' || commentFilter === 'trade') {
      return comments.filter(c => c.comment_type === commentFilter);
    }
    return comments; // 'all'
  };

  const handleFileSelect = async (e) => {
    let files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingAttachments(true);
    try {
      files = await Promise.all(files.map(async (f) => {
        if (!isHeicFile(f)) return f;
        try {
          return await convertHeicToJpeg(f);
        } catch (err) {
          console.error('HEIC conversion failed:', err);
          return f;
        }
      }));
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('You must be logged in to upload images. Please refresh the page and log in again.');
        return;
      }

      // Compress images - handle errors for each file individually
      const compressedFiles = await Promise.all(
        files.map(async (file) => {
          try {
            return await compressImage(file, 1920, 1920, 0.8);
          } catch (error) {
            console.error('Error compressing file:', file.name, error);
            // If compression fails, use original file
            return file;
          }
        })
      );

      const uploadPromises = compressedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_BASE_URL}/upload/comment-attachment`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
            // Don't set Content-Type header - let browser set it with boundary for FormData
          },
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
          throw new Error(errorData.error || `Failed to upload file: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        if (!data.url) {
          throw new Error('Upload succeeded but no URL returned');
        }
        
        return {
          url: data.url,
          filename: data.filename || file.name,
          type: data.type || file.type,
          size: data.size || file.size
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      setCommentAttachments([...commentAttachments, ...uploadedFiles]);
      console.log('Successfully uploaded', uploadedFiles.length, 'file(s)');
    } catch (error) {
      console.error('Error uploading files:', error);
      const errorMessage = error.message || 'Failed to upload files';
      alert(`Failed to upload image: ${errorMessage}\n\nPlease check:\n- You are logged in\n- The file is an image (JPG, PNG, WebP)\n- The file is under 5MB\n- Your internet connection is working`);
    } finally {
      setUploadingAttachments(false);
      if (e.target) {
        e.target.value = ''; // Reset file input
      }
    }
  };

  const removeAttachment = (index) => {
    setCommentAttachments(commentAttachments.filter((_, i) => i !== index));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Zoom controls
  const handleZoomIn = () => {
    setZoom(prev => Math.min((prev || 1) + 0.25, MAX_ZOOM));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max((prev || 1) - 0.25, MIN_ZOOM));
  };

  const getFitZoom = () => {
    const viewport = containerRef.current?.getBoundingClientRect?.();
    if (!viewport) return NaN;
    const pad = 40; // keep some breathing room for overlays
    if (!Number.isFinite(viewport.width) || !Number.isFinite(viewport.height) || viewport.width <= 0 || viewport.height <= 0) {
      return NaN;
    }
    const availW = Math.max(200, viewport.width - pad);
    const availH = Math.max(200, viewport.height - pad);
    const z = Math.min(availW / (sheetPx.w || 1), availH / (sheetPx.h || 1));
    // Allow fitting above 1 if the viewport is huge, but cap for sanity.
    return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
  };

  const getFillWidthZoom = () => {
    const viewport = containerRef.current?.getBoundingClientRect?.();
    if (!viewport) return NaN;
    const pad = 56;
    if (!Number.isFinite(viewport.width) || viewport.width <= 0) {
      return NaN;
    }
    const availW = Math.max(200, viewport.width - pad);
    const z = availW / (sheetPx.w || 1);
    return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
  };

  // Close-up work zoom: keeps the scale useful even when browser/screen DPI is unknown.
  const ASSUMED_SCREEN_PPI = 96;
  const getWorkZoom = () => {
    const fit = getFitZoom();
    if (Number.isFinite(fit) && fit > 0) {
      return Math.max(1, Math.min(MAX_ZOOM, fit * 2.25));
    }
    const ppi = Number(sheetPx.ppi) || ASSUMED_SCREEN_PPI;
    const z = ASSUMED_SCREEN_PPI / ppi;
    return Math.max(1, Math.min(MAX_ZOOM, z));
  };

  const handleFitToScreen = () => {
    const z = getFitZoom();
    if (!Number.isFinite(z) || z <= 0) return false;
    setZoom(z);
    setPanOffset({ x: 0, y: 0 });
    setIsWorkScale(false);
    return true;
  };

  const handleScaleToSheet = () => {
    // Sheet scale (raw pixels-per-inch defined by sheet.ppi)
    setZoom(1);
    setPanOffset({ x: 0, y: 0 });
    setIsWorkScale(false);
  };

  const handleFillWidth = () => {
    const z = getFillWidthZoom();
    if (!Number.isFinite(z) || z <= 0) return false;
    setZoom(z);
    setPanOffset({ x: 0, y: 0 });
    setIsWorkScale(false);
    return true;
  };

  const handleFullSizeWorkScale = () => {
    // Work close-up scale for placing plants and reading notes.
    setZoom(getWorkZoom());
    setPanOffset({ x: 0, y: 0 });
    setIsWorkScale(true);
  };

  const spotlightToPercent = (x, y) => {
    const next = {
      x: Math.max(0, Math.min(100, Number(x) || 0)),
      y: Math.max(0, Math.min(100, Number(y) || 0))
    };
    setSpotlightTarget(next);
    setZoom(getWorkZoom());
    setIsWorkScale(true);

    // After render at zoom=1, pan so the target is centered in the viewport.
    requestAnimationFrame(() => {
      const viewport = containerRef.current?.getBoundingClientRect?.();
      const el = canvasRef.current?.querySelector?.('[data-spotlight-target="1"]');
      if (!viewport || !el) return;
      const r = el.getBoundingClientRect();
      const viewportCx = viewport.left + viewport.width / 2;
      const viewportCy = viewport.top + viewport.height / 2;
      const elCx = r.left + r.width / 2;
      const elCy = r.top + r.height / 2;
      const dx = viewportCx - elCx;
      const dy = viewportCy - elCy;
      setPanOffset((prev) => ({ x: (prev?.x || 0) + dx, y: (prev?.y || 0) + dy }));
      setSpotlightTarget(null);
    });
  };

  const handleResetZoom = async () => {
    setZoom(1);
    setPanOffset({ x: 0, y: 0 });
    setRotation(0);
    await saveRotationToProject(0);
    setIsWorkScale(false);
  };

  // Fill width once per drawing/sheet/url signature (larger default view than full fit).
  useEffect(() => {
    const sig = `${activePlanId || 'legacy'}|${sheetPx.w}x${sheetPx.h}|${effectivePlanImageUrl || ''}`;
    if (autoFitSigRef.current === sig) return;
    // The container may not be measurable on the first tick (esp. during route transitions).
    // Only lock the signature once we successfully apply zoom.
    const tryFillWidth = (triesLeft) => {
      const ok = handleFillWidth();
      if (ok) {
        autoFitSigRef.current = sig;
        return;
      }
      if (triesLeft <= 0) return;
      requestAnimationFrame(() => tryFillWidth(triesLeft - 1));
    };
    tryFillWidth(8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlanId, sheetPx.w, sheetPx.h, effectivePlanImageUrl]);

  // Allow ESC to cancel pick modes.
  useEffect(() => {
    if (!isSpotlightPicking) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsSpotlightPicking(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isSpotlightPicking]);

  const focusPlacementById = (placementId) => {
    const pid = placementId ? String(placementId) : '';
    if (!pid) return;

    // Ensure at least 100% scale for "to-scale" placement review.
    setZoom((prev) => Math.max(prev || 1, 1));

    // Give React a tick to render at the new zoom, then measure + pan.
    requestAnimationFrame(() => {
      const viewport = containerRef.current?.getBoundingClientRect?.();
      const el = canvasRef.current?.querySelector?.(`[data-placement-id="${pid}"]`);
      if (!viewport || !el) return;
      const r = el.getBoundingClientRect();
      const viewportCx = viewport.left + viewport.width / 2;
      const viewportCy = viewport.top + viewport.height / 2;
      const elCx = r.left + r.width / 2;
      const elCy = r.top + r.height / 2;
      const dx = viewportCx - elCx;
      const dy = viewportCy - elCy;
      setPanOffset((prev) => ({ x: (prev?.x || 0) + dx, y: (prev?.y || 0) + dy }));
      setSelectedPlacementId(pid);
      setShowControls(true);
      setShowPlantMarkers(true);
    });
  };

  const focusFirstPlacementForMasterListId = (masterListId) => {
    const mid = masterListId ? String(masterListId) : '';
    if (!mid) return;
    const p = (placements || []).find((x) => String(x?.master_list_id || '') === mid) || null;
    if (!p?.id) {
      alert('That plant has no placement on this drawing yet.');
      return;
    }
    focusPlacementById(p.id);
  };

  // Support deep-link focusing from Plant Sheet PDF: ?planId=...&focusMasterListId=...
  useEffect(() => {
    const planIdParam = (searchParams.get('planId') || '').toString().trim();
    if (planIdParam && projectPlans.length > 0 && projectPlans.some((p) => String(p.id) === planIdParam)) {
      if (String(activePlanId || '') !== planIdParam) {
        setActivePlanId(planIdParam);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectPlans, searchParams]);

  useEffect(() => {
    const focusMid = (searchParams.get('focusMasterListId') || '').toString().trim();
    if (!focusMid) return;
    const sig = `${activePlanId || 'legacy'}|${focusMid}|${placements.length}`;
    if (focusOnceRef.current === sig) return;
    if (placements.length === 0) return;
    focusOnceRef.current = sig;
    focusFirstPlacementForMasterListId(focusMid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placements, activePlanId, searchParams]);

  // Rotation controls - save to project when changed
  const handleRotateLeft = async () => {
    const newRotation = (rotation - 90) % 360;
    setRotation(newRotation);
    await saveRotationToProject(newRotation);
  };

  const handleRotateRight = async () => {
    const newRotation = (rotation + 90) % 360;
    setRotation(newRotation);
    await saveRotationToProject(newRotation);
  };

  // Save rotation to project_data
  const saveRotationToProject = async (rotationValue) => {
    try {
      const token = localStorage.getItem('authToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      
      // Get current project_data
      const currentProjectData = project?.project_data 
        ? (typeof project.project_data === 'string' 
            ? JSON.parse(project.project_data) 
            : project.project_data)
        : {};
      
      // Update rotation in project_data
      const updatedProjectData = {
        ...currentProjectData,
        rotation: rotationValue
      };
      
      // Update project with new rotation
      const response = await fetch(`${API_BASE_URL}/projects/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          project_data: updatedProjectData
        })
      });
      
      if (!response.ok) {
        console.error('Failed to save rotation');
      } else {
        // Update local project state
        setProject(prev => ({
          ...prev,
          project_data: updatedProjectData
        }));
      }
    } catch (error) {
      console.error('Error saving rotation:', error);
    }
  };

  // Transform coordinates from rotated image space back to original image space
  // Coordinates are in percentage (0-100)
  // When we rotate the image before sending to AI, the AI returns coordinates in the rotated image's coordinate system
  // We need to transform these back to the original image's coordinate system
  // 
  // For 90° clockwise rotation:
  // - Original image: width W, height H
  // - Rotated image: width H, height W
  // - Point at (x%, y%) in rotated means: x% from left, y% from top of rotated image
  // - In original: left edge of rotated = top edge of original, top edge of rotated = right edge of original
  // - So: (x_rot%, y_rot%) → (y_rot%, 100 - x_rot%) in original
  const transformCoordinates = (x, y, rotationDegrees) => {
    // Normalize rotation to 0-360 range
    const normalizedRotation = ((rotationDegrees % 360) + 360) % 360;
    
    // If no rotation, return original coordinates
    if (normalizedRotation === 0) {
      return { x, y };
    }
    
    let transformedX, transformedY;
    
    if (normalizedRotation === 90) {
      // 90° clockwise rotation of image
      // (x_rot%, y_rot%) in rotated → (y_rot%, 100 - x_rot%) in original
      transformedX = y;
      transformedY = 100 - x;
    } else if (normalizedRotation === 180) {
      // 180° rotation
      // (x_rot%, y_rot%) → (100 - x_rot%, 100 - y_rot%)
      transformedX = 100 - x;
      transformedY = 100 - y;
    } else if (normalizedRotation === 270) {
      // 270° clockwise rotation (or -90°)
      // (x_rot%, y_rot%) → (100 - y_rot%, x_rot%)
      transformedX = 100 - y;
      transformedY = x;
    } else {
      // For other angles, return original (shouldn't happen with our UI)
      transformedX = x;
      transformedY = y;
    }
    
    return {
      x: Math.max(0, Math.min(100, transformedX)),
      y: Math.max(0, Math.min(100, transformedY))
    };
  };

  const OCR_MATCH_CONFIRM_THRESHOLD = 0.95;

  async function applyOcrDetectionPlacement(detection, match) {
    const token = localStorage.getItem('authToken');
    const transformedCoords = transformCoordinates(detection.x, detection.y, rotation);
    const body = {
      plan_id: activePlanId || null,
      detections: [
        {
          text: detection.text,
          x: transformedCoords.x,
          y: transformedCoords.y,
          confidence: detection.confidence,
          chosen_plant_id: match.id,
          chosen_master_list_id: match.master_list_id || undefined,
          inferred_zone_id: detection.inferred_zone_id || undefined,
          quantity: 1,
        },
      ],
    };

    const resp = await fetch(`/api/plans/${id}/apply-detections`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data.error || 'Apply failed');

    const newPlacements = (data.placements || []).map((p) => ({
      ...p,
      common_name: match.common_name,
      botanical_name: match.botanical_name,
      image_url: match.image_url,
    }));
    setPlacements((prev) => [...prev, ...newPlacements]);
    await loadPlacements();
    if (Array.isArray(data.manual_zone_updates) && data.manual_zone_updates.length > 0) {
      await loadMasterList();
    }
    return data;
  }

  // Transform screen coordinates to original image coordinates accounting for rotation
  // This is used when dragging plants - we need to convert mouse/touch position
  // from screen space to the original image's coordinate system
  // The image container is rotated with CSS transform, so we need to apply inverse rotation
  const transformScreenToImageCoords = (screenX, screenY, imgRect, rotationDegrees) => {
    // Get position relative to image center (where rotation happens)
    const centerX = imgRect.left + imgRect.width / 2;
    const centerY = imgRect.top + imgRect.height / 2;
    const relX = screenX - centerX;
    const relY = screenY - centerY;
    
    // Normalize rotation
    const normalizedRotation = ((rotationDegrees % 360) + 360) % 360;
    
    // Apply inverse rotation to get coordinates in original image space
    // The rotation is applied around the center, so we rotate the relative coordinates back
    let transformedRelX, transformedRelY;
    
    if (normalizedRotation === 90) {
      // Inverse of 90° clockwise rotation = 90° counter-clockwise
      transformedRelX = relY;
      transformedRelY = -relX;
    } else if (normalizedRotation === 180) {
      transformedRelX = -relX;
      transformedRelY = -relY;
    } else if (normalizedRotation === 270) {
      // Inverse of 270° clockwise rotation = 270° counter-clockwise (or -90°)
      transformedRelX = -relY;
      transformedRelY = relX;
    } else {
      transformedRelX = relX;
      transformedRelY = relY;
    }
    
    // Get the image element to access natural dimensions
    const img = canvasRef.current?.querySelector('img');
    if (!img) {
      // Fallback: use rect dimensions
      const x = ((transformedRelX + imgRect.width / 2) / imgRect.width) * 100;
      const y = ((transformedRelY + imgRect.height / 2) / imgRect.height) * 100;
      return {
        x: Math.max(0, Math.min(100, x)),
        y: Math.max(0, Math.min(100, y))
      };
    }
    
    // The image is displayed with zoom, so we need to account for that
    // The rect dimensions reflect the zoomed image
    // For rotation, we need to consider if dimensions are swapped
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;
    
    // Calculate effective dimensions after rotation
    // When rotated 90/270, the displayed width/height are swapped
    let displayWidth, displayHeight;
    if (normalizedRotation === 90 || normalizedRotation === 270) {
      displayWidth = imgRect.height;
      displayHeight = imgRect.width;
    } else {
      displayWidth = imgRect.width;
      displayHeight = imgRect.height;
    }
    
    // Calculate scale (accounting for zoom)
    const scaleX = naturalWidth / displayWidth;
    const scaleY = naturalHeight / displayHeight;
    
    // Convert to original image coordinates (percentage)
    // Add half the natural dimension to convert from center-relative to top-left-relative
    const origX = ((transformedRelX * scaleX + naturalWidth / 2) / naturalWidth) * 100;
    const origY = ((transformedRelY * scaleY + naturalHeight / 2) / naturalHeight) * 100;
    
    return {
      x: Math.max(0, Math.min(100, origX)),
      y: Math.max(0, Math.min(100, origY))
    };
  };

  // Generate PDF plant list for printing - uses Project Plant List (plan master-list) data
  const generatePlantListPDF = async () => {
    try {
      // Fetch master list and sync from placements to ensure it's up to date
      const token = localStorage.getItem('authToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      
      // Sync from placements only if there's a plan, otherwise just fetch master list
      const syncParam = effectivePlanImageUrl ? '?sync=true' : '';
      const masterListResponse = await fetch(`${API_BASE_URL}/plans/${id}/master-list${syncParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!masterListResponse.ok) {
        throw new Error('Failed to fetch Project Plant List');
      }
      
      const masterListData = await masterListResponse.json();
      const masterListItems = masterListData.items || [];
      
      if (masterListItems.length === 0) {
        alert('No plants on this Project Plant List yet. Add plants on the Project Plant List page first.');
        return;
      }

      // Get designer name and business name
      let designerName = 'Designer';
      let businessName = '';
      try {
        const designerResponse = await fetch(`${API_BASE_URL}/designers/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (designerResponse.ok) {
          const designerData = await designerResponse.json();
          if (designerData.profile) {
            businessName = designerData.profile.businessName || designerData.profile.business_name || '';
            designerName = designerData.profile.displayName || 
                          designerData.profile.full_name || 
                          'Designer';
          }
        }
      } catch (err) {
        console.error('Error fetching designer name:', err);
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);
      
      let yPos = margin;
      const lineHeight = 8;
      const imageSize = 30; // 30mm for plant images
      const spacing = 5;

      // Title - Project Name
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text(project.name || 'Plant List', margin, yPos);
      yPos += lineHeight * 1.5;

      // Business Name (if available)
      if (businessName) {
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(businessName, margin, yPos);
        yPos += lineHeight * 1.5;
      }

      // Designer Name
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Designer: ${designerName}`, margin, yPos);
      yPos += lineHeight * 1.5;

      // Date
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, margin, yPos);
      yPos += lineHeight * 2;

      // Get plant images from placements for master list items
      // Use the image shown on the plan (from placements), fallback to plant database primary image
      const plantList = await Promise.all(masterListItems.map(async (item) => {
        // Find placements with this plant_id - use the image_url from placement (what's shown on plan)
        const matchingPlacements = placements.filter(p => p.plant_id === item.plant_id);
        let imageUrl = null;
        
        // Use image from placement if available (this is what's shown on the plan)
        if (matchingPlacements.length > 0) {
          // Get image from first placement (they should all have the same image if set)
          imageUrl = matchingPlacements[0].image_url || null;
        }
        
        // If no placement image, fetch from plant database
        if (!imageUrl && item.plant_id) {
          try {
            const plantResponse = await fetch(`${API_BASE_URL}/plants/${item.plant_id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (plantResponse.ok) {
              const plantData = await plantResponse.json();
              imageUrl = plantData.image_url || null;
            }
          } catch (err) {
            console.error('Error fetching plant image:', err);
          }
        }
        
        return {
          ...item,
          image_url: imageUrl,
          pot_size: item.pot_size || '',
          notes: item.notes || ''
        };
      }));
      let currentPage = 1;

      for (let i = 0; i < plantList.length; i++) {
        const plant = plantList[i];
        
        // Check if we need a new page
        if (yPos + imageSize + lineHeight * 4 > pageHeight - margin) {
          pdf.addPage();
          currentPage++;
          yPos = margin;
        }

        // Load and add plant image (skip invalid URLs to avoid console noise)
        const safePlantImgUrl = coerceImageUrl(plant.image_url);
        if (safePlantImgUrl) {
          try {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            
            await new Promise((resolve, reject) => {
              img.onload = () => {
                try {
                  // Convert image to base64
                  const canvas = document.createElement('canvas');
                  canvas.width = img.width;
                  canvas.height = img.height;
                  const ctx = canvas.getContext('2d');
                  ctx.drawImage(img, 0, 0);
                  
                  const imgData = canvas.toDataURL('image/jpeg', 0.8);
                  
                  // Add image to PDF (30mm x 30mm)
                  pdf.addImage(imgData, 'JPEG', margin, yPos, imageSize, imageSize);
                  resolve();
                } catch (error) {
                  console.error('Error processing image:', error);
                  resolve(); // Continue without image
                }
              };
              img.onerror = () => {
                console.error('Error loading image:', safePlantImgUrl);
                resolve(); // Continue without image
              };
              img.src = safePlantImgUrl;
            });
          } catch (error) {
            console.error('Error adding image to PDF:', error);
          }
        }

        // Plant information (to the right of image)
        const textX = margin + imageSize + spacing;
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        
        // Add clickable link for plant name
        const plantUrl = `https://plantaday.com/plants?plantId=${plant.plant_id || ''}`;
        const plantNameWidth = pdf.getTextWidth(plant.common_name);
        pdf.text(plant.common_name, textX, yPos + lineHeight);
        pdf.link(textX, yPos + lineHeight - 4, plantNameWidth, lineHeight, { url: plantUrl });
        
        if (plant.botanical_name) {
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'italic');
          const botanicalWidth = pdf.getTextWidth(plant.botanical_name);
          pdf.text(plant.botanical_name, textX, yPos + lineHeight * 2);
          pdf.link(textX, yPos + lineHeight * 2 - 4, botanicalWidth, lineHeight, { url: plantUrl });
        }

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Quantity: ${plant.quantity || 1}`, textX, yPos + lineHeight * 3);
        
        if (plant.pot_size) {
          pdf.text(`Pot Size: ${plant.pot_size}`, textX, yPos + lineHeight * 4);
        }

        // Move to next plant position
        yPos += Math.max(imageSize, lineHeight * 4) + spacing * 2;

        // Add a subtle line separator
        if (i < plantList.length - 1) {
          pdf.setDrawColor(200, 200, 200);
          pdf.line(margin, yPos - spacing, pageWidth - margin, yPos - spacing);
          yPos += spacing;
        }
      }

      // Footer with page numbers
      const totalPages = pdf.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(
          `Page ${i} of ${totalPages}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }

      // Generate PDF blob and show preview modal
      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const fileName = `${project.name || 'plant-list'}-${new Date().toISOString().split('T')[0]}.pdf`;
      
      setPdfBlobUrl(url);
      setPdfFileName(fileName);
      setShowPdfPreview(true);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // Generate a "Plant Sheet" PDF: grid of images + name + qty, with deep-links back to focused plan areas.
  const generatePlantSheetPDF = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL ||
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

      const syncParam = effectivePlanImageUrl ? '?sync=true' : '';
      const masterListResponse = await fetch(`${API_BASE_URL}/plans/${id}/master-list${syncParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!masterListResponse.ok) throw new Error('Failed to fetch project plant list');

      const masterListData = await masterListResponse.json();
      const items = masterListData.items || [];
      if (items.length === 0) {
        alert('No plants in this project yet.');
        return;
      }

      // Only include plants that have been placed (dragged/added) onto the current drawing.
      // placements[] is already scoped to the active drawing in this view.
      const placedCounts = {};
      for (const p of Array.isArray(placements) ? placements : []) {
        const masterId = p?.master_list_id || null;
        if (!masterId) continue;
        const q = Number(p?.quantity) || 1;
        placedCounts[masterId] = (placedCounts[masterId] || 0) + q;
      }

      const placedItems = (Array.isArray(items) ? items : [])
        .filter((it) => (placedCounts[it?.id] || 0) > 0)
        .sort((a, b) => String(a?.common_name || '').localeCompare(String(b?.common_name || ''), undefined, { sensitivity: 'base' }));

      if (placedItems.length === 0) {
        alert('No plants have been placed on this drawing yet.');
        return;
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;
      const gap = 4;
      const cols = 3;
      const cellW = (pageWidth - margin * 2 - gap * (cols - 1)) / cols;
      const imageSize = cellW; // square
      const cellH = imageSize + 18; // image + text

      const origin = window.location.origin;
      const planQs = activePlanId ? `planId=${encodeURIComponent(activePlanId)}` : '';

      // Header
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text(project?.name || 'Plant Sheet', margin, margin);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, margin, margin + 6);

      let x = margin;
      let y = margin + 14;
      let col = 0;

      const addImage = async (url, xMm, yMm, wMm, hMm) => {
        const safeUrl = coerceImageUrl(url);
        if (!safeUrl) return;
        try {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          await new Promise((resolve) => {
            img.onload = () => {
              try {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                const imgData = canvas.toDataURL('image/jpeg', 0.8);
                pdf.addImage(imgData, 'JPEG', xMm, yMm, wMm, hMm);
              } catch (e) {
                // ignore
              }
              resolve();
            };
            img.onerror = () => resolve();
            img.src = safeUrl;
          });
        } catch {
          // ignore
        }
      };

      for (const it of placedItems) {
        // Page break
        if (y + cellH > pageHeight - margin) {
          pdf.addPage();
          x = margin;
          y = margin;
          col = 0;
        }

        // Cell background
        pdf.setDrawColor(220, 220, 220);
        pdf.rect(x, y, cellW, cellH);

        // Image (safeUrl coercion handled inside addImage)
        await addImage(it.image_url || null, x, y, imageSize, imageSize);

        // Text + link
        const name = (it.common_name || it.botanical_name || 'Unknown').toString();
        const qty = placedCounts[it.id] || 1;
        const focusUrl = `${origin}/plan/${encodeURIComponent(id)}?${planQs}${planQs ? '&' : ''}focusMasterListId=${encodeURIComponent(it.id)}`;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        const textY = y + imageSize + 6;
        const nameTrim = name.length > 26 ? `${name.slice(0, 26)}…` : name;
        pdf.text(nameTrim, x + 2, textY);
        pdf.link(x + 2, textY - 4, cellW - 4, 6, { url: focusUrl });

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.text(`Qty: ${qty}`, x + 2, textY + 6);

        // Next cell
        col += 1;
        if (col >= cols) {
          col = 0;
          x = margin;
          y += cellH + gap;
        } else {
          x += cellW + gap;
        }
      }

      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const fileName = `${project?.name || 'plant-sheet'}-${new Date().toISOString().split('T')[0]}.pdf`;
      setPdfBlobUrl(url);
      setPdfFileName(fileName);
      setShowPdfPreview(true);
    } catch (e) {
      console.error('Error generating Plant Sheet PDF:', e);
      alert(e.message || 'Failed to generate Plant Sheet PDF');
    }
  };

  const downloadPDF = () => {
    if (pdfBlobUrl && pdfFileName) {
      const link = document.createElement('a');
      link.href = pdfBlobUrl;
      link.download = pdfFileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const closePdfPreview = () => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
    }
    setShowPdfPreview(false);
    setPdfBlobUrl(null);
    setPdfFileName('');
  };

  // Rotate image using canvas and return as base64
  const rotateImage = async (imageUrl, rotationDegrees) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      // Normalize rotation to 0-360 range
      const normalizedRotation = ((rotationDegrees % 360) + 360) % 360;
      
      // Handle CORS - try anonymous first, fallback if needed
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate new dimensions based on rotation
          let width = img.width;
          let height = img.height;
          
          if (normalizedRotation === 90 || normalizedRotation === 270) {
            // Swap dimensions for 90/270 degree rotations
            width = img.height;
            height = img.width;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Set white background
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
          
          // Translate to center, rotate, then translate back
          ctx.translate(width / 2, height / 2);
          ctx.rotate((normalizedRotation * Math.PI) / 180);
          ctx.translate(-img.width / 2, -img.height / 2);
          
          // Draw rotated image
          ctx.drawImage(img, 0, 0);
          
          // Convert to base64
          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob'));
              return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve(reader.result);
            };
            reader.onerror = () => reject(new Error('Failed to read blob'));
            reader.readAsDataURL(blob);
          }, 'image/jpeg', 0.95);
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = (error) => {
        // If CORS fails, try without crossOrigin
        if (imageUrl.startsWith('http') && !imageUrl.includes(window.location.hostname)) {
          const imgRetry = new Image();
          imgRetry.onload = () => {
            // Use a proxy or fetch the image differently
            // For now, reject and let it fall back to original
            reject(new Error('CORS error: Cannot rotate external image'));
          };
          imgRetry.onerror = () => reject(new Error('Failed to load image'));
          imgRetry.src = imageUrl;
        } else {
          reject(new Error('Failed to load image'));
        }
      };
      
      img.src = imageUrl;
    });
  };

  // Pan controls
  const startPan = (clientX, clientY) => {
    setIsPanning(true);
    setPanStart({ x: clientX - panOffset.x, y: clientY - panOffset.y });
    panGestureRef.current = { startX: clientX, startY: clientY, moved: false };
    suppressNextCanvasClickRef.current = false;
  };

  const handleMouseDown = (e) => {
    // Middle mouse or Shift+Click to pan (always)
    if (e.button === 1 || e.shiftKey) {
      startPan(e.clientX, e.clientY);
      e.preventDefault();
      return;
    }

    // Full-size mode: left-click drag to pan anywhere on empty canvas
    if (
      isWorkScale &&
      e.button === 0 &&
      !draggingPlacement &&
      !resizingPlacement &&
      !isPlacingPlant &&
      !isPlacingNote &&
      !isSpotlightPicking
    ) {
      startPan(e.clientX, e.clientY);
      e.preventDefault();
    }
  };

  const handleTouchStart = (e) => {
    // Full-size mode: one-finger drag to pan (when not placing/dragging)
    if (
      isWorkScale &&
      e.touches.length === 1 &&
      !draggingPlacement &&
      !resizingPlacement &&
      !isPlacingPlant &&
      !isPlacingNote &&
      !isSpotlightPicking
    ) {
      const touch = e.touches[0];
      startPan(touch.clientX, touch.clientY);
      e.preventDefault();
      return;
    }

    // Two-finger touch for panning (always)
    if (e.touches.length === 2) {
      const touch = e.touches[0];
      startPan(touch.clientX, touch.clientY);
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning) {
      const g = panGestureRef.current;
      if (g && !g.moved) {
        const dx = e.clientX - g.startX;
        const dy = e.clientY - g.startY;
        if (Math.abs(dx) + Math.abs(dy) > 3) g.moved = true;
      }
      setPanOffset({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    } else if (resizingPlacement) {
      handleResizeDrag(e.clientX, e.clientY);
    } else if (draggingPlacement || (dragStart && dragStart.isDragIntent)) {
      // Call handlePlantDrag for both: active drag OR intent to drag (threshold check)
      handlePlantDrag(e);
    }
  };

  const handleTouchMove = (e) => {
    const isPanTouch = isWorkScale ? (e.touches.length === 1) : (e.touches.length === 2);
    if (isPanning && isPanTouch) {
      const touch = e.touches[0];
      const g = panGestureRef.current;
      if (g && !g.moved) {
        const dx = touch.clientX - g.startX;
        const dy = touch.clientY - g.startY;
        if (Math.abs(dx) + Math.abs(dy) > 3) g.moved = true;
      }
      setPanOffset({
        x: touch.clientX - panStart.x,
        y: touch.clientY - panStart.y
      });
      e.preventDefault();
    } else if (resizingPlacement) {
      const touch = e.touches[0];
      handleResizeDrag(touch.clientX, touch.clientY);
      e.preventDefault();
    } else if (draggingPlacement || (dragStart && dragStart.isDragIntent)) {
      // Call handlePlantTouchMove for both: active drag OR intent to drag (threshold check)
      handlePlantTouchMove(e);
    }
  };

  const handleMouseUp = () => {
    suppressNextCanvasClickRef.current = Boolean(panGestureRef.current?.moved);
    panGestureRef.current = null;
    setIsPanning(false);
    if (resizingPlacement) {
      handleResizeMouseUp();
    } else {
      handlePlantMouseUp();
    }
  };

  const handleTouchEnd = () => {
    suppressNextCanvasClickRef.current = Boolean(panGestureRef.current?.moved);
    panGestureRef.current = null;
    setIsPanning(false);
    if (resizingPlacement) {
      handleResizeMouseUp();
    } else {
      handlePlantTouchEnd();
    }
  };

  // Plant placement
  const handleSelectPlant = (plant) => {
    // Toggle selection for multi-select
    setSelectedPlants(prev => {
      const key = String(plant?.master_list_id || plant?.masterListId || plant?.plant_id || plant?.id || '');
      const isSelected = prev.some((p) => String(p?.master_list_id || p?.masterListId || p?.plant_id || p?.id || '') === key);
      if (isSelected) {
        return prev.filter((p) => String(p?.master_list_id || p?.masterListId || p?.plant_id || p?.id || '') !== key);
      } else {
        return [...prev, plant];
      }
    });
  };

  const armPlacementWithSinglePlant = (plant) => {
    setSelectedPlants([plant]);
    setIsPlacingPlant(true);
  };

  const handleCanvasClick = async (e) => {
    // #region agent log
    __agentDebugIngest({
      runId: 'placeSymbol_run2',
      hypothesisId: 'H5',
      location: 'src/PlanDetail.js:handleCanvasClick:entry',
      message: 'Canvas click',
      data: {
        suppressNext: Boolean(suppressNextCanvasClickRef.current),
        isPlacingPlant,
        selectedPlantsCount: Array.isArray(selectedPlants) ? selectedPlants.length : null,
        isPlacingNote,
        isSpotlightPicking,
        activePlanId: activePlanId || null
      },
      timestamp: Date.now()
    });
    // #endregion

    // If we just panned, suppress the click that follows mouseup/touchend.
    if (suppressNextCanvasClickRef.current) {
      suppressNextCanvasClickRef.current = false;
      return;
    }

    // Spotlight pick: click an area to jump to 100% scale centered there.
    if (isSpotlightPicking) {
      const img = canvasRef.current?.querySelector('img');
      if (!img) return;
      const rect = img.getBoundingClientRect();
      const coords = transformScreenToImageCoords(e.clientX, e.clientY, rect, rotation);
      setIsSpotlightPicking(false);
      spotlightToPercent(coords.x, coords.y);
      return;
    }

    // Close controls if clicking on empty canvas
    if (!isPlacingPlant) {
      setSelectedPlacementId(null);
      setShowControls(false);
    }

    // Place a plan note (annotation)
    if (isPlacingNote) {
      if (!activePlanId) {
        alert('Please select a drawing first.');
        return;
      }

      const img = canvasRef.current?.querySelector('img');
      if (!img) return;

      const rect = img.getBoundingClientRect();
      const coords = transformScreenToImageCoords(e.clientX, e.clientY, rect, rotation);

      try {
        const resp = await projectPlansAPI.createNote(activePlanId, {
          position_x: coords.x,
          position_y: coords.y,
          width: 14,
          height: 10,
          note_text: ''
        });

        const note = resp?.note;
        if (note) {
          setPlanNotes((prev) => [...prev, note]);
          setEditingNoteId(note.id);
          setNoteEditorValue(note.note_text || '');
          setSelectedNoteForComment(note);
          setSelectedPlantForComment(null);
          setShowComments(true);
        }
        setIsPlacingNote(false);
      } catch (error) {
        console.error('Error creating note:', error);
        alert('Failed to create note. Please try again.');
      }
      return;
    }

    if (!isPlacingPlant || selectedPlants.length === 0) {
      // #region agent log
      __agentDebugIngest({
        runId: 'placeSymbol_run2',
        hypothesisId: 'H5',
        location: 'src/PlanDetail.js:handleCanvasClick:skip',
        message: 'Skip click place (not in placing mode or nothing selected)',
        data: { isPlacingPlant, selectedPlantsCount: selectedPlants.length },
        timestamp: Date.now()
      });
      // #endregion
      return;
    }

    if (!activePlanId) {
      // #region agent log
      __agentDebugIngest({
        runId: 'placeSymbol_run2',
        hypothesisId: 'H5',
        location: 'src/PlanDetail.js:handleCanvasClick:noActivePlan',
        message: 'Cannot place plant without activePlanId',
        data: { selectedPlantsCount: selectedPlants.length },
        timestamp: Date.now()
      });
      // #endregion
      alert('Please select a drawing first.');
      return;
    }

    const img = canvasRef.current?.querySelector('img');
    if (!img) return;
    
    const rect = img.getBoundingClientRect();
    
    // Transform screen coordinates to original image coordinates accounting for rotation
    const coords = transformScreenToImageCoords(e.clientX, e.clientY, rect, rotation);
    
    // Convert to percentage (0-100)
    const position_x = coords.x;
    const position_y = coords.y;

    try {
      // Place all selected plants at this location
      const newPlacements = [];
      // #region agent log
      __agentDebugIngest({
        runId: 'placeSymbol_run2',
        hypothesisId: 'H5',
        location: 'src/PlanDetail.js:handleCanvasClick:preCreate',
        message: 'Placing selected plants by click',
        data: {
          activePlanId: activePlanId || null,
          position_x,
          position_y,
          selected: (selectedPlants || []).slice(0, 3).map((p) => ({
            master_list_id: p?.master_list_id || p?.masterListId || null,
            plant_id: p?.plant_id || p?.id || null,
            symbol: p?.symbol || null,
            symbol_size_in: p?.symbol_size_in ?? null
          }))
        },
        timestamp: Date.now()
      });
      // #endregion
      for (const plant of selectedPlants) {
        const resolvedPlantId = plant?.plant_id || plant?.id || null;
        const manualCommonName = !resolvedPlantId
          ? (plant?.manual_common_name || plant?.common_name || plant?.botanical_name || null)
          : null;
        const manualBotanicalName = !resolvedPlantId ? (plant?.manual_botanical_name || plant?.botanical_name || null) : null;
        const manualImageUrl = !resolvedPlantId ? (plant?.manual_image_url || plant?.image_url || null) : null;

        if (!resolvedPlantId && !manualCommonName) {
          console.warn('Skipping plant placement: missing plant_id and manual_common_name', plant);
          continue;
        }

        const newPlacement = await plantPlacementsAPI.create({
          project_id: id,
          plan_id: activePlanId || null,
          plant_id: resolvedPlantId,
          master_list_id: plant?.master_list_id || plant?.masterListId || null,
          manual_common_name: manualCommonName,
          manual_botanical_name: manualBotanicalName,
          manual_image_url: manualImageUrl,
          notes: JSON.stringify({
            symbol: plant?.symbol || 'circle',
            symbol_size_in: plant?.symbol_size_in ?? 0.5
          }),
          position_x,
          position_y,
          quantity: plant.quantity || 1 // Use copied quantity if pasting
        });
        newPlacements.push(newPlacement);
      }

      // The API returns the placement with plant details already joined
      setPlacements(prev => [...prev, ...newPlacements]);
      setIsPlacingPlant(false);
      setSelectedPlants([]);
      // #region agent log
      __agentDebugIngest({
        runId: 'placeSymbol_run2',
        hypothesisId: 'H5',
        location: 'src/PlanDetail.js:handleCanvasClick:success',
        message: 'Placed plants by click (success)',
        data: {
          createdCount: newPlacements.length,
          sample: newPlacements.slice(0, 2).map((p) => ({ id: p?.id, plan_id: p?.plan_id, master_list_id: p?.master_list_id }))
        },
        timestamp: Date.now()
      });
      // #endregion
    } catch (error) {
      console.error('Error placing plant:', error);
      // #region agent log
      __agentDebugIngest({
        runId: 'placeSymbol_run2',
        hypothesisId: 'H5',
        location: 'src/PlanDetail.js:handleCanvasClick:error',
        message: 'Error placing plants by click (caught)',
        data: { message: error?.message || String(error) },
        timestamp: Date.now()
      });
      // #endregion
      alert('Failed to place plant. Please try again.');
    }
  };

  const clampPercent = (n) => Math.max(0, Math.min(100, Number(n) || 0));

  const updateNoteLocal = (noteId, patch) => {
    setPlanNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, ...patch } : n)));
  };

  const getImageRectForNotes = () => {
    const img = canvasRef.current?.querySelector('img');
    if (!img) return null;
    return img.getBoundingClientRect();
  };

  const getNoteById = (noteId) => planNotes.find((n) => n.id === noteId) || null;

  const handleNotePointerDown = (e, note) => {
    // Drag note (top-left anchored)
    e.stopPropagation();
    if (!note) return;
    if (resizingNoteId) return;

    const rect = getImageRectForNotes();
    if (!rect) return;
    const coords = transformScreenToImageCoords(e.clientX, e.clientY, rect, rotation);

    setDraggingNoteId(note.id);
    setNoteDragOffset({
      x: (coords.x - (note.position_x ?? 0)) || 0,
      y: (coords.y - (note.position_y ?? 0)) || 0
    });

    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  const handleNotePointerMove = (e) => {
    if (!draggingNoteId) return;
    e.stopPropagation();

    const rect = getImageRectForNotes();
    if (!rect) return;
    const coords = transformScreenToImageCoords(e.clientX, e.clientY, rect, rotation);

    const nextX = clampPercent(coords.x - (noteDragOffset?.x || 0));
    const nextY = clampPercent(coords.y - (noteDragOffset?.y || 0));
    updateNoteLocal(draggingNoteId, { position_x: nextX, position_y: nextY });
  };

  const handleNotePointerUp = async (e) => {
    if (!draggingNoteId) return;
    e.stopPropagation();

    const noteId = draggingNoteId;
    setDraggingNoteId(null);

    const note = getNoteById(noteId);
    if (!note) return;

    try {
      await projectPlansAPI.updateNote(noteId, {
        position_x: note.position_x,
        position_y: note.position_y
      });
    } catch (err) {
      console.error('Failed to save note position:', err);
      // Reload notes from server to resync
      try {
        const data = await projectPlansAPI.listNotes(activePlanId);
        setPlanNotes(data?.notes || []);
      } catch {
        // ignore
      }
    }
  };

  const handleNoteResizePointerDown = (e, note) => {
    e.stopPropagation();
    if (!note) return;
    setResizingNoteId(note.id);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  const handleNoteResizePointerMove = (e) => {
    if (!resizingNoteId) return;
    e.stopPropagation();

    const note = getNoteById(resizingNoteId);
    if (!note) return;

    const rect = getImageRectForNotes();
    if (!rect) return;
    const coords = transformScreenToImageCoords(e.clientX, e.clientY, rect, rotation);

    const baseX = Number(note.position_x) || 0;
    const baseY = Number(note.position_y) || 0;
    const nextW = Math.max(2, clampPercent(coords.x - baseX));
    const nextH = Math.max(2, clampPercent(coords.y - baseY));
    updateNoteLocal(resizingNoteId, { width: nextW, height: nextH });
  };

  const handleNoteResizePointerUp = async (e) => {
    if (!resizingNoteId) return;
    e.stopPropagation();

    const noteId = resizingNoteId;
    setResizingNoteId(null);

    const note = getNoteById(noteId);
    if (!note) return;

    try {
      await projectPlansAPI.updateNote(noteId, { width: note.width, height: note.height });
    } catch (err) {
      console.error('Failed to save note size:', err);
      try {
        const data = await projectPlansAPI.listNotes(activePlanId);
        setPlanNotes(data?.notes || []);
      } catch {
        // ignore
      }
    }
  };

  const saveNoteText = async (noteId, text) => {
    updateNoteLocal(noteId, { note_text: text });
    try {
      await projectPlansAPI.updateNote(noteId, { note_text: text });
    } catch (err) {
      console.error('Failed to save note text:', err);
      try {
        const data = await projectPlansAPI.listNotes(activePlanId);
        setPlanNotes(data?.notes || []);
      } catch {
        // ignore
      }
    }
  };

  // Handle drop from sidebar (desktop)
  const handleCanvasDrop = async (e) => {
    e.preventDefault();
    
    try {
      const plantData = e.dataTransfer.getData('plant');
      // #region agent log
      __agentDebugIngest({
        runId: 'placeSymbol_run2',
        hypothesisId: 'H0',
        location: 'src/PlanDetail.js:handleCanvasDrop:entry',
        message: 'Canvas drop event received',
        data: {
          hasPlantData: Boolean(plantData),
          dataTransferTypes: (() => {
            try { return Array.from(e?.dataTransfer?.types || []); } catch { return null; }
          })(),
          activePlanId: activePlanId || null
        },
        timestamp: Date.now()
      });
      // #endregion

      if (!plantData) {
        // #region agent log
        __agentDebugIngest({
          runId: 'placeSymbol_run2',
          hypothesisId: 'H0',
          location: 'src/PlanDetail.js:handleCanvasDrop:noPlantData',
          message: 'Drop ignored (no dataTransfer plant payload)',
          data: { activePlanId: activePlanId || null },
          timestamp: Date.now()
        });
        // #endregion
        return;
      }
      
      const plant = JSON.parse(plantData);
      
      const img = canvasRef.current?.querySelector('img');
      if (!img) {
        // #region agent log
        __agentDebugIngest({
          runId: 'placeSymbol_run2',
          hypothesisId: 'H0',
          location: 'src/PlanDetail.js:handleCanvasDrop:noCanvasImg',
          message: 'Drop ignored (canvas image not found)',
          data: { activePlanId: activePlanId || null },
          timestamp: Date.now()
        });
        // #endregion
        return;
      }
      
      const rect = img.getBoundingClientRect();
      
      // Transform screen coordinates to original image coordinates accounting for rotation
      const coords = transformScreenToImageCoords(e.clientX, e.clientY, rect, rotation);
      
      const position_x = coords.x;
      const position_y = coords.y;

      const resolvedPlantId = plant?.plant_id || plant?.id || null;
      const manualCommonName = !resolvedPlantId
        ? (plant?.manual_common_name || plant?.common_name || plant?.botanical_name || null)
        : null;
      const manualBotanicalName = !resolvedPlantId ? (plant?.manual_botanical_name || plant?.botanical_name || null) : null;
      const manualImageUrl = !resolvedPlantId ? (plant?.manual_image_url || plant?.image_url || null) : null;

      // #region agent log
      __agentDebugIngest({runId:'placeSymbol_run1',hypothesisId:'H1',location:'src/PlanDetail.js:handleCanvasDrop:preCreate',message:'Drop parsed; creating placement',data:{activePlanId:activePlanId||null,master_list_id:plant?.master_list_id||plant?.masterListId||null,plant_id:resolvedPlantId,symbol:plant?.symbol,symbol_size_in:plant?.symbol_size_in,coords:{x:position_x,y:position_y},coordsFinite:{x:Number.isFinite(position_x),y:Number.isFinite(position_y)},rotation,zoom,isWorkScale},timestamp:Date.now()});
      // #endregion

      const newPlacement = await plantPlacementsAPI.create({
        project_id: id,
        plan_id: activePlanId || null,
        plant_id: resolvedPlantId,
        master_list_id: plant?.master_list_id || plant?.masterListId || null,
        symbol_id: plant?.symbol_id || plant?.symbol || 'circle',
        symbol_size_in: plant?.symbol_size_in ?? 0.5,
        manual_common_name: manualCommonName,
        manual_botanical_name: manualBotanicalName,
        manual_image_url: manualImageUrl,
        notes: JSON.stringify({
          symbol: plant?.symbol || 'circle',
          symbol_size_in: plant?.symbol_size_in ?? 0.5
        }),
        position_x,
        position_y,
        quantity: 1
      });

      // #region agent log
      let parsedSymbol = null;
      try { parsedSymbol = newPlacement?.notes ? JSON.parse(newPlacement.notes)?.symbol : null; } catch {}
      __agentDebugIngest({runId:'placeSymbol_run1',hypothesisId:'H2',location:'src/PlanDetail.js:handleCanvasDrop:postCreate',message:'Placement created (response)',data:{id:newPlacement?.id,plan_id:newPlacement?.plan_id||null,master_list_id:newPlacement?.master_list_id||null,position_x:newPlacement?.position_x,position_y:newPlacement?.position_y,coordsFinite:{x:Number.isFinite(Number(newPlacement?.position_x)),y:Number.isFinite(Number(newPlacement?.position_y))},notesType:typeof newPlacement?.notes,notesSymbol:parsedSymbol},timestamp:Date.now()});
      // #endregion

      setPlacements(prev => [...prev, newPlacement]);
    } catch (error) {
      console.error('Error placing plant:', error);
      // #region agent log
      __agentDebugIngest({
        runId: 'placeSymbol_run2',
        hypothesisId: 'H0',
        location: 'src/PlanDetail.js:handleCanvasDrop:error',
        message: 'Error placing plant (caught)',
        data: { message: error?.message || String(error) },
        timestamp: Date.now()
      });
      // #endregion
      alert('Failed to place plant. Please try again.');
    }
  };

  // Handle touch drag from sidebar (mobile)
  const handlePlantCardTouchStart = (e, plant) => {
    // Don't interfere with scrolling initially
    const touch = e.touches[0];
    setTouchDraggingPlant({
      plant,
      startX: touch.clientX,
      startY: touch.clientY,
      hasMoved: false
    });
  };

  const handlePlantCardTouchMove = (e, plant) => {
    if (!touchDraggingPlant) return;
    
    const touch = e.touches[0];
    const movedX = Math.abs(touch.clientX - touchDraggingPlant.startX);
    const movedY = Math.abs(touch.clientY - touchDraggingPlant.startY);
    
    // Start dragging after 10px movement
    if (!touchDraggingPlant.hasMoved && (movedX > 10 || movedY > 10)) {
      e.preventDefault(); // Prevent scrolling once we start dragging
      setTouchDraggingPlant({...touchDraggingPlant, hasMoved: true});
    }
    
    if (touchDraggingPlant.hasMoved) {
      e.preventDefault();
      setTouchDragPosition({ x: touch.clientX, y: touch.clientY });
    }
  };

  const handlePlantCardTouchEnd = async (e) => {
    if (!touchDraggingPlant || !touchDraggingPlant.hasMoved) {
      setTouchDraggingPlant(null);
      setTouchDragPosition(null);
      return;
    }
    
    const touch = e.changedTouches[0];
    const canvasRect = canvasRef.current.getBoundingClientRect();
    
    // Check if touch ended over the canvas
    if (touch.clientX >= canvasRect.left && touch.clientX <= canvasRect.right &&
        touch.clientY >= canvasRect.top && touch.clientY <= canvasRect.bottom) {
      
      try {
        const img = canvasRef.current?.querySelector('img');
        if (!img) return;
        
        const rect = img.getBoundingClientRect();
        
        // Transform screen coordinates to original image coordinates accounting for rotation
        const coords = transformScreenToImageCoords(touch.clientX, touch.clientY, rect, rotation);
        
        const position_x = coords.x;
        const position_y = coords.y;

        const plant = touchDraggingPlant?.plant || null;
        const resolvedPlantId = plant?.plant_id || plant?.id || null;
        const manualCommonName = !resolvedPlantId
          ? (plant?.manual_common_name || plant?.common_name || plant?.botanical_name || null)
          : null;
        const manualBotanicalName = !resolvedPlantId ? (plant?.manual_botanical_name || plant?.botanical_name || null) : null;
        const manualImageUrl = !resolvedPlantId ? (plant?.manual_image_url || plant?.image_url || null) : null;

        const newPlacement = await plantPlacementsAPI.create({
          project_id: id,
          plan_id: activePlanId || null,
          plant_id: resolvedPlantId,
          master_list_id: plant?.master_list_id || plant?.masterListId || null,
          symbol_id: plant?.symbol_id || plant?.symbol || 'circle',
          symbol_size_in: plant?.symbol_size_in ?? 0.5,
          manual_common_name: manualCommonName,
          manual_botanical_name: manualBotanicalName,
          manual_image_url: manualImageUrl,
          notes: JSON.stringify({
            symbol: plant?.symbol || 'circle',
            symbol_size_in: plant?.symbol_size_in ?? 0.5
          }),
          position_x,
          position_y,
          quantity: 1
        });

        setPlacements(prev => [...prev, newPlacement]);
      } catch (error) {
        console.error('Error placing plant:', error);
        alert('Failed to place plant. Please try again.');
      }
    }
    
    setTouchDraggingPlant(null);
    setTouchDragPosition(null);
  };

  const handleDeletePlacement = async (placementId) => {
    try {
      await plantPlacementsAPI.delete(placementId);
      setPlacements(prev => prev.filter(p => p.id !== placementId));
      setSelectedPlacementId(null);
      setPlacements(prev => prev.filter(p => p.id !== placementId));
    } catch (error) {
      console.error('Error deleting placement:', error);
      alert('Failed to delete placement.');
    }
  };

  const handleUpdateQuantity = async (placementId, newQuantity) => {
    try {
      await plantPlacementsAPI.update(placementId, { quantity: newQuantity });
      setPlacements(prev => prev.map(p => 
        p.id === placementId ? { ...p, quantity: newQuantity } : p
      ));
      setEditingPlacement(null);
      // Also hide controls and deselect after editing
      setShowControls(false);
      setSelectedPlacementId(null);
    } catch (error) {
      console.error('Error updating quantity:', error);
      alert('Failed to update quantity.');
    }
  };

  const savePlantCardEdits = async () => {
    if (!selectedPlantCard || !plantCardEdits) return;
    
    try {
      const token = localStorage.getItem('authToken');
      
      // Update placement with new data
      const response = await fetch(`/api/plant-placements/${selectedPlantCard.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          quantity: plantCardEdits.quantity || selectedPlantCard.quantity,
          notes: plantCardEdits.notes
        })
      });
      
      if (!response.ok) throw new Error('Failed to update placement');
      
      const nextImageUrl = (plantCardEdits.image_url || '').toString().trim();
      if (nextImageUrl && !isValidImageUrl(nextImageUrl)) {
        throw new Error('That image link doesn’t look valid. Please paste a full http(s) URL (or a /uploads/... link).');
      }

      // If image_url was changed, update the plant in database
      if (nextImageUrl && nextImageUrl !== selectedPlantCard.image_url && selectedPlantCard.plant_id) {
        const plantResponse = await fetch(`/api/plants/${selectedPlantCard.plant_id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            image_url: nextImageUrl
          })
        });
        
        if (!plantResponse.ok) {
          console.error('Failed to update plant image in database');
        }
      }
      
      // Update local state
      setPlacements(prev => prev.map(p => 
        p.id === selectedPlantCard.id 
          ? { ...p, ...plantCardEdits } 
          : p
      ));
      
      // Update selected card with new data
      setSelectedPlantCard(prev => ({ ...prev, ...plantCardEdits, image_url: nextImageUrl || prev?.image_url }));
      setEditingPlantCard(false);
      setPlantCardEdits({});
      
    } catch (error) {
      console.error('Error saving plant card edits:', error);
      alert('Failed to save changes');
    }
  };

  const searchPlantsForSwap = async (query) => {
    if (!query || query.length < 2) {
      setPlantSearchResults([]);
      return;
    }
    
    try {
      const results = await plantsAPI.getAll({ search: query, limit: 20 });
      setPlantSearchResults(results);
    } catch (error) {
      console.error('Error searching plants:', error);
      setPlantSearchResults([]);
    }
  };

  const swapPlantInPlacement = async (newPlant) => {
    if (!selectedPlantCard || !newPlant) return;
    
    try {
      const token = localStorage.getItem('authToken');
      
      // Update placement with new plant
      const response = await fetch(`/api/plant-placements/${selectedPlantCard.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plant_id: newPlant.id
        })
      });
      
      if (!response.ok) throw new Error('Failed to swap plant');
      
      // Update local state with new plant details
      const updatedPlacement = {
        ...selectedPlantCard,
        plant_id: newPlant.id,
        common_name: newPlant.common_name,
        botanical_name: newPlant.botanical_name,
        category: newPlant.category,
        size: newPlant.size,
        image_url: newPlant.image_url,
        plant_data: newPlant.plant_data
      };
      
      setPlacements(prev => prev.map(p => 
        p.id === selectedPlantCard.id ? updatedPlacement : p
      ));
      
      // Update selected card
      setSelectedPlantCard(updatedPlacement);
      setSwappingPlant(false);
      setPlantSearchTerm('');
      setPlantSearchResults([]);
      
    } catch (error) {
      console.error('Error swapping plant:', error);
      alert('Failed to swap plant');
    }
  };

  // Drag handlers for moving plants
  const handlePlantMouseDown = (e, placement) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Select the plant
    setSelectedPlacementId(placement.id);
    
    // Right-click - show controls immediately
    if (e.button === 2) {
      setShowControls(true);
      setDragStart(null);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      return;
    }
    
    setShowControls(false); // Hide controls initially
    
    // Clear any existing timeout
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    if (e.button === 0) { // Left click only
      // Store the start position but don't start dragging yet
      const imgRect = canvasRef.current.querySelector('img').getBoundingClientRect();
      const newDragStart = {
        x: e.clientX,
        y: e.clientY,
        startX: placement.position_x,
        startY: placement.position_y,
        placementId: placement.id,
        isDragIntent: true, // Flag that we might drag
        wasCancelled: false
      };
      
      setDragStart(newDragStart);
      
      // On desktop, also use long-press (500ms) to show controls
      if (!isMobile) {
        controlsTimeoutRef.current = setTimeout(() => {
          // Check if still has drag intent (hasn't moved yet)
          if (newDragStart.isDragIntent && !newDragStart.wasCancelled) {
            setShowControls(true);
          }
        }, 500);
      }
    }
  };

  const handlePlantTouchStart = (e, placement) => {
    e.stopPropagation();
    
    // Select the plant
    setSelectedPlacementId(placement.id);
    setShowControls(false); // Hide controls initially on mobile
    
    // Clear any existing timeout
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    const touch = e.touches[0];
    const rect = canvasRef.current.querySelector('img').getBoundingClientRect();
    
    const newDragStart = {
      x: touch.clientX,
      y: touch.clientY,
      startX: placement.position_x,
      startY: placement.position_y,
      placementId: placement.id,
      isDragIntent: true,
      wasCancelled: false // Track if drag was cancelled
    };
    
    setDragStart(newDragStart);
    
    // On mobile, use long-press (500ms) to show controls
    if (isMobile) {
      controlsTimeoutRef.current = setTimeout(() => {
        // Check if still has drag intent (hasn't moved yet)
        if (newDragStart.isDragIntent && !newDragStart.wasCancelled) {
          setShowControls(true);
          // Haptic feedback if available
          if (navigator.vibrate) {
            navigator.vibrate(50);
          }
        }
      }, 500);
    }
  };

  const handlePlantDrag = (e) => {
    if (!dragStart) return;

    // Check if we've moved enough to start dragging (5px threshold)
    const movedX = Math.abs(e.clientX - dragStart.x);
    const movedY = Math.abs(e.clientY - dragStart.y);
    
    if (dragStart.isDragIntent && (movedX > 5 || movedY > 5)) {
      // Cancel controls timeout - user is dragging
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
        controlsTimeoutRef.current = null;
      }
      // Mark as cancelled for the timeout check
      dragStart.wasCancelled = true;
      // Start actual dragging
      setDraggingPlacement(dragStart.placementId);
      setDragStart({...dragStart, isDragIntent: false, wasCancelled: true});
      setShowControls(false);
      return;
    }
    
    if (!draggingPlacement) return;

    const img = canvasRef.current.querySelector('img');
    if (!img) return;

    const rect = img.getBoundingClientRect();
    
    // Transform current mouse position to original image coordinates
    const currentCoords = transformScreenToImageCoords(e.clientX, e.clientY, rect, rotation);
    const startCoords = transformScreenToImageCoords(dragStart.x, dragStart.y, rect, rotation);
    
    // Calculate delta in percentage
    const deltaXPercent = currentCoords.x - startCoords.x;
    const deltaYPercent = currentCoords.y - startCoords.y;
    
    // Calculate new position
    const newX = Math.max(0, Math.min(100, dragStart.startX + deltaXPercent));
    const newY = Math.max(0, Math.min(100, dragStart.startY + deltaYPercent));
    
    // Update placement position locally
    setPlacements(prev => prev.map(p => 
      p.id === draggingPlacement ? { ...p, position_x: newX, position_y: newY } : p
    ));
  };

  const handlePlantTouchMove = (e) => {
    if (!dragStart) return;

    const touch = e.touches[0];
    
    // Check if we've moved enough to start dragging (10px threshold for touch)
    const movedX = Math.abs(touch.clientX - dragStart.x);
    const movedY = Math.abs(touch.clientY - dragStart.y);
    
    if (dragStart.isDragIntent && (movedX > 10 || movedY > 10)) {
      // Cancel controls timeout - user is dragging
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
        controlsTimeoutRef.current = null;
      }
      // Mark as cancelled so timeout doesn't show controls
      if (dragStart.wasCancelled !== undefined) {
        dragStart.wasCancelled = true;
      }
      // Start actual dragging
      setDraggingPlacement(dragStart.placementId);
      setDragStart({...dragStart, isDragIntent: false, wasCancelled: true});
      setShowControls(false);
      e.preventDefault();
      return;
    }
    
    if (!draggingPlacement) return;
    e.preventDefault(); // Prevent scrolling while dragging

    const img = canvasRef.current.querySelector('img');
    if (!img) return;

    const rect = img.getBoundingClientRect();
    
    // Transform current touch position to original image coordinates
    const currentCoords = transformScreenToImageCoords(touch.clientX, touch.clientY, rect, rotation);
    const startCoords = transformScreenToImageCoords(dragStart.x, dragStart.y, rect, rotation);
    
    // Calculate delta in percentage
    const deltaXPercent = currentCoords.x - startCoords.x;
    const deltaYPercent = currentCoords.y - startCoords.y;
    
    const newX = Math.max(0, Math.min(100, dragStart.startX + deltaXPercent));
    const newY = Math.max(0, Math.min(100, dragStart.startY + deltaYPercent));
    
    setPlacements(prev => prev.map(p => 
      p.id === draggingPlacement ? { ...p, position_x: newX, position_y: newY } : p
    ));
  };

  const handlePlantMouseUp = async () => {
    // Clear any pending controls timeout
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = null;
    }
    
    if (draggingPlacement) {
      // User was dragging
      const placement = placements.find(p => p.id === draggingPlacement);
      if (placement) {
        try {
          // Save new position to database
          await plantPlacementsAPI.update(draggingPlacement, {
            position_x: placement.position_x,
            position_y: placement.position_y
          });
        } catch (error) {
          console.error('Error updating position:', error);
          // Reload placements if save failed
          loadPlacements();
        }
      }
      
      setDraggingPlacement(null);
    }
    
    // Always clear dragStart on mouse up
    // Don't show controls on simple click - only on long-press
    setDragStart(null);
  };

  const handlePlantTouchEnd = async () => {
    if (resizingPlacement) {
      await handleResizeMouseUp();
    } else {
      await handlePlantMouseUp();
    }
  };

  // Detect if mouse is over a corner of the marker
  const handleMarkerMouseMove = (e, placement, markerSize) => {
    if (draggingPlacement || resizingPlacement) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Define corner detection area (last 8px of each edge)
    const cornerSize = 8;
    const isNearLeft = x < cornerSize;
    const isNearRight = x > markerSize - cornerSize;
    const isNearTop = y < cornerSize;
    const isNearBottom = y > markerSize - cornerSize;
    
    // Determine which corner (or edge)
    if ((isNearTop || isNearBottom) && (isNearLeft || isNearRight)) {
      // Hovering over a corner
      setHoveringCorner(placement.id);
      // Set appropriate cursor based on corner
      if ((isNearTop && isNearLeft) || (isNearBottom && isNearRight)) {
        e.currentTarget.style.cursor = 'nwse-resize';
      } else {
        e.currentTarget.style.cursor = 'nesw-resize';
      }
    } else {
      // Not hovering over a corner
      if (hoveringCorner === placement.id) {
        setHoveringCorner(null);
        e.currentTarget.style.cursor = '';
      }
    }
  };

  const handleMarkerMouseLeave = (e) => {
    if (hoveringCorner) {
      setHoveringCorner(null);
      e.currentTarget.style.cursor = '';
    }
  };

  // Resize by dragging from corner
  const handleResizeMouseDown = (e, placement) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Get current size from notes
    let currentSize = 36;
    try {
      const notesData = placement.notes ? JSON.parse(placement.notes) : {};
      currentSize = notesData.marker_size || placement.marker_size || 36;
    } catch (err) {
      currentSize = placement.marker_size || 36;
    }
    
    setResizingPlacement(placement.id);
    setResizeStart({
      size: currentSize,
      x: e.clientX,
      y: e.clientY
    });
  };

  const handleResizeTouchStart = (e, placement) => {
    e.stopPropagation();
    
    // Get current size from notes
    let currentSize = 36;
    try {
      const notesData = placement.notes ? JSON.parse(placement.notes) : {};
      currentSize = notesData.marker_size || placement.marker_size || 36;
    } catch (err) {
      currentSize = placement.marker_size || 36;
    }
    
    const touch = e.touches[0];
    setResizingPlacement(placement.id);
    setResizeStart({
      size: currentSize,
      x: touch.clientX,
      y: touch.clientY
    });
  };

  const handleResizeDrag = (clientX, clientY) => {
    if (!resizingPlacement || !resizeStart) return;

    // Calculate distance moved in screen coordinates
    const deltaX = clientX - resizeStart.x;
    const deltaY = clientY - resizeStart.y;
    
    // Transform the movement direction to account for rotation
    // The resize handle is at bottom-right, so we need to determine
    // if the drag is moving away from or toward the center
    const normalizedRotation = ((rotation % 360) + 360) % 360;
    
    let effectiveDeltaX = deltaX;
    let effectiveDeltaY = deltaY;
    
    // Rotate the delta vector to account for plan rotation
    // This ensures resize works correctly regardless of plan orientation
    if (normalizedRotation === 90) {
      // 90° rotation: swap and negate
      effectiveDeltaX = -deltaY;
      effectiveDeltaY = deltaX;
    } else if (normalizedRotation === 180) {
      // 180° rotation: negate both
      effectiveDeltaX = -deltaX;
      effectiveDeltaY = -deltaY;
    } else if (normalizedRotation === 270) {
      // 270° rotation: swap and negate
      effectiveDeltaX = deltaY;
      effectiveDeltaY = -deltaX;
    }
    
    // Calculate distance moved
    const distance = Math.sqrt(effectiveDeltaX * effectiveDeltaX + effectiveDeltaY * effectiveDeltaY);
    
    // Determine resize direction: positive = larger, negative = smaller
    // Use the diagonal distance (both X and Y) to make resize smoother
    // Dragging outward (away from center) should increase size
    // Use average of X and Y deltas for smoother, less sticky resizing
    const avgDelta = (effectiveDeltaX + effectiveDeltaY) / 2;
    const sizeChange = avgDelta * 0.5; // Increased multiplier for more responsive resizing
    const newSize = Math.max(16, Math.min(120, resizeStart.size + sizeChange));

    // Update size locally in both marker_size and notes
    setPlacements(prev => prev.map(p => {
      if (p.id === resizingPlacement) {
        const notesData = p.notes ? JSON.parse(p.notes) : {};
        notesData.marker_size = Math.round(newSize);
        return {
          ...p,
          marker_size: Math.round(newSize),
          notes: JSON.stringify(notesData)
        };
      }
      return p;
    }));
  };

  const handleResizeMouseUp = async () => {
    if (!resizingPlacement) return;

    const placement = placements.find(p => p.id === resizingPlacement);
    if (placement) {
      try {
        // Save new size to database
        await plantPlacementsAPI.update(resizingPlacement, {
          notes: JSON.stringify({ ...JSON.parse(placement.notes || '{}'), marker_size: placement.marker_size })
        });
      } catch (error) {
        console.error('Error updating size:', error);
        loadPlacements();
      }
    }
    
    setResizingPlacement(null);
    setResizeStart(null);
  };

  // Scroll to resize (when hovering over a plant)
  const handlePlantWheel = (e, placement) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Get current size from notes or default
    let notesData = {};
    try {
      notesData = placement.notes ? JSON.parse(placement.notes) : {};
    } catch (e) {}
    
    const currentSize = notesData.marker_size || placement.marker_size || 36;
    const delta = e.deltaY > 0 ? -4 : 4;
    const newSize = Math.max(16, Math.min(120, currentSize + delta));
    
    const updatedNotes = { ...notesData, marker_size: newSize };
    
    // Update size locally
    setPlacements(prev => prev.map(p => 
      p.id === placement.id ? { 
        ...p, 
        marker_size: newSize,
        notes: JSON.stringify(updatedNotes)
      } : p
    ));
    
    // Debounce save to database
    clearTimeout(window.resizeTimeout);
    window.resizeTimeout = setTimeout(async () => {
      try {
        await plantPlacementsAPI.update(placement.id, {
          notes: JSON.stringify(updatedNotes)
        });
      } catch (error) {
        console.error('Error saving size:', error);
      }
    }, 500);
  };

  // Resize with buttons (for trackpad users)
  const handleResizePlant = async (placementId, change) => {
    const placement = placements.find(p => p.id === placementId);
    if (!placement) return;

    // Parse notes to get current marker_size
    let notesData = {};
    try {
      notesData = placement.notes ? JSON.parse(placement.notes) : {};
    } catch (e) {
      console.error('Error parsing notes:', e);
    }

    const currentSize = notesData.marker_size || placement.marker_size || 36;
    const newSize = Math.max(16, Math.min(120, currentSize + change));

    // Update notes with new marker_size
    const updatedNotes = { ...notesData, marker_size: newSize };

    // Update size locally - store in both places for compatibility
    setPlacements(prev => prev.map(p => 
      p.id === placementId ? { 
        ...p, 
        marker_size: newSize,
        notes: JSON.stringify(updatedNotes)
      } : p
    ));

    // Save to database
    try {
      await plantPlacementsAPI.update(placementId, {
        notes: JSON.stringify(updatedNotes)
      });
    } catch (error) {
      console.error('Error saving size:', error);
      // Reload on error
      loadPlacements();
    }
  };

  const handleAddPlants = () => {
    setShowPlantSidebar(!showPlantSidebar);
  };

  const handleAutoDetectPlants = async () => {
    const planUrlForDisplay = coerceImageUrl(effectivePlanImageUrl) || effectivePlanImageUrl;
    if (!planUrlForDisplay) {
      setOcrError('No plan image available for analysis');
      return;
    }

    if (effectivePlanImageUrlIsPdf) {
      setShowOCRModal(true);
      setOcrResults(null);
      setOcrProcessing(false);
      setOcrError(
        'Auto-detect works on image plans (JPG, PNG, etc.). Switch to an image drawing or export your PDF to an image, then try again.'
      );
      return;
    }

    setShowOCRModal(true);
    setOcrProcessing(true);
    setOcrError('');
    setOcrResults(null);

    try {
      const token = localStorage.getItem('authToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      
      // If rotation is not 0, rotate the image before sending to AI
      const imageToSendServer = resolvePlanImageUrlForServerFetch(effectivePlanImageUrl);
      let imageToSend = imageToSendServer;
      if (rotation !== 0) {
        try {
          // Rotate the image and get base64 (browser loads via same-origin or proxy)
          const rotatedImageBase64 = await rotateImage(planUrlForDisplay, rotation);
          // Send base64 instead of URL
          const response = await fetch(`${API_BASE_URL}/plans/${id}/recognize`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              image_base64: rotatedImageBase64,
              plan_id: activePlanId || undefined,
              use_project_list_only: ocrUseProjectListOnly,
              use_project_list_context: ocrUseProjectListContext,
            })
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || 'Failed to analyze plan');
          }

          setOcrResults(data);
          return;
        } catch (rotateError) {
          console.error('Error rotating image:', rotateError);
          // Fall back to original image if rotation fails
          console.warn('Falling back to original image orientation');
        }
      }
      
      // Use original image (no rotation or rotation failed)
      const response = await fetch(`${API_BASE_URL}/plans/${id}/recognize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          image_url: imageToSend,
          plan_id: activePlanId || undefined,
          use_project_list_only: ocrUseProjectListOnly,
          use_project_list_context: ocrUseProjectListContext,
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to analyze plan');
      }

      setOcrResults(data);
    } catch (err) {
      console.error('OCR error:', err);
      setOcrError(err.message);
    } finally {
      setOcrProcessing(false);
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const maybeOfferTransferToNewPlan = ({ sourcePlanId, targetPlanId, sourceNotesCount, sourcePlantsCount }) => {
    if (!sourcePlanId || !targetPlanId) return;
    if (String(sourcePlanId) === String(targetPlanId)) return;
    if ((sourceNotesCount || 0) <= 0 && (sourcePlantsCount || 0) <= 0) return;

    setTransferSourcePlanId(sourcePlanId);
    setTransferTargetPlanId(targetPlanId);
    setTransferSourceNotesCount(sourceNotesCount || 0);
    setTransferSourcePlantsCount(sourcePlantsCount || 0);
    setTransferCopyNotes(false);
    setTransferCopyPlants(false);
    setShowPlanTransferModal(true);
  };

  const runTransferToNewPlan = async () => {
    if (!transferSourcePlanId || !transferTargetPlanId) {
      setShowPlanTransferModal(false);
      return;
    }
    if (!transferCopyNotes && !transferCopyPlants) {
      setShowPlanTransferModal(false);
      return;
    }

    try {
      setTransferringPlanContent(true);
      const resp = await projectPlansAPI.transferFrom(transferTargetPlanId, {
        fromPlanId: transferSourcePlanId,
        copyNotes: !!transferCopyNotes,
        copyPlants: !!transferCopyPlants
      });

      setShowPlanTransferModal(false);

      // Refresh the new plan view (notes + placements)
      try {
        const notesData = await projectPlansAPI.listNotes(transferTargetPlanId);
        setPlanNotes(notesData?.notes || []);
      } catch {
        // ignore
      }
      await loadPlacements();

      const n = resp?.notesCopied ?? 0;
      const p = resp?.plantsCopied ?? 0;
      alert(`Copied ${n} note(s) and ${p} plant(s) to the new drawing.`);
    } catch (e) {
      console.error('Transfer to new plan failed:', e);
      alert(`Could not copy to new drawing: ${e?.message || 'Unknown error'}`);
    } finally {
      setTransferringPlanContent(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    const prevStatus = projectStatus || 'draft';
    try {
      // Optimistic UI update (rollback on failure)
      setProjectStatus(newStatus);
      setProject((prev) => (prev ? { ...prev, status: newStatus } : prev));
      await projectsAPI.update(id, { status: newStatus });
      alert(`Project status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      // Roll back
      setProjectStatus(prevStatus);
      setProject((prev) => (prev ? { ...prev, status: prevStatus } : prev));
      alert(`Failed to update project status: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleAssignClient = async (clientEmail, clientName) => {
    try {
      // Update project_data with client info
      const projectData = typeof project.project_data === 'string' 
        ? JSON.parse(project.project_data || '{}') 
        : (project.project_data || {});
      
      projectData.client = {
        email: clientEmail,
        name: clientName
      };

      // Note: The backend PUT endpoint doesn't currently support updating project_data
      // For now, we'll use the collaboration invite system to assign the client
      // This creates a share link for the client
      const token = localStorage.getItem('authToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      
      const response = await fetch(`${API_BASE_URL}/collaboration/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          projectId: id,
          recipientEmail: clientEmail,
          recipientName: clientName,
          accessLevel: 'view',
          shareType: 'client',
          messageVisibility: 'all'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to assign client');
      }

      const data = await response.json();
      setAssignedClient({ email: clientEmail, name: clientName });
      alert(`Client ${clientName} has been assigned and invited to the project!`);
      setShowProjectSettings(false);
    } catch (error) {
      console.error('Error assigning client:', error);
      alert(`Failed to assign client: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️</div>
          <p className="text-gray-900">{error || 'Project not found'}</p>
          <button
            onClick={handleBack}
            className="mt-4 text-green-600 hover:text-green-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-full px-4 sm:px-6 lg:px-8 py-4">
          {/* Top Row: Project Info */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="text-gray-600 hover:text-gray-900"
                title="Back to Dashboard"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    projectStatus === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                    projectStatus === 'final' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {projectStatus || 'draft'}
                  </span>
                  {assignedClient && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                      Client: {assignedClient.name || assignedClient.email}
                    </span>
                  )}
                  <button
                    onClick={() => {
                      setSheetDraft(sheet);
                      setShowProjectSettings(true);
                    }}
                    className="ml-auto text-gray-600 hover:text-gray-900"
                    title="Project Settings"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm text-gray-600">{placements.length} plants placed</p>
              </div>
            </div>
          </div>

          {/* Bottom Row: Action Buttons - Grouped by Function */}
          <div className="flex flex-wrap items-start gap-x-4 gap-y-3 w-full">
            {/* Left Side: View, Drawings, Plants — wraps so buttons never overlap */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 flex-1 min-w-0">
              {/* View Controls - Dropdown Menu */}
              <div className="relative view-menu-container">
                <button
                  onClick={() => setShowViewMenu(!showViewMenu)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  title="View Controls"
                >
                  <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View
                  <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {showViewMenu && (
                  <div className="absolute left-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-[70] p-4">
                  <div className="space-y-4">
                    {/* Zoom Controls */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">View Size</label>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between gap-3 bg-gray-100 rounded-lg px-3 py-2">
                          <button
                            onClick={handleZoomOut}
                            className="text-gray-700 hover:text-gray-900"
                            title="Zoom Out"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                            </svg>
                          </button>
                          <span className="text-sm font-semibold text-gray-700 min-w-[4rem] text-center">
                            {Math.round(zoom * 100)}%
                          </span>
                          <button
                            onClick={handleZoomIn}
                            className="text-gray-700 hover:text-gray-900"
                            title="Zoom In"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                          </button>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => {
                              handleFitToScreen();
                              setShowViewMenu(false);
                            }}
                            className="text-left px-3 py-2 rounded-md border border-green-200 bg-green-50 hover:bg-green-100"
                            title="Show the whole drawing"
                          >
                            <span className="block text-sm font-semibold text-green-800">Fit drawing</span>
                            <span className="block text-xs text-green-700">See everything</span>
                          </button>
                          <button
                            onClick={() => {
                              handleFillWidth();
                              setShowViewMenu(false);
                            }}
                            className="text-left px-3 py-2 rounded-md border border-blue-200 bg-blue-50 hover:bg-blue-100"
                            title="Make the drawing easy to read across the available width"
                          >
                            <span className="block text-sm font-semibold text-blue-800">Fill width</span>
                            <span className="block text-xs text-blue-700">Bigger view</span>
                          </button>
                          <button
                            onClick={() => {
                              handleFullSizeWorkScale();
                              setShowViewMenu(false);
                            }}
                            className="text-left px-3 py-2 rounded-md border border-purple-200 bg-purple-50 hover:bg-purple-100"
                            title="Zoom in for placing plants and reading plan details"
                          >
                            <span className="block text-sm font-semibold text-purple-800">Work close-up</span>
                            <span className="block text-xs text-purple-700">Place and read</span>
                          </button>
                          <button
                            onClick={() => {
                              handleResetZoom();
                              setShowViewMenu(false);
                            }}
                            className="text-left px-3 py-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50"
                            title="Reset view, pan, and rotation"
                          >
                            <span className="block text-sm font-semibold text-gray-800">Reset</span>
                            <span className="block text-xs text-gray-500">Start fresh</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Spotlight tool */}
                    <div className="space-y-2">
                      <button
                        onClick={() => {
                          setIsSpotlightPicking(true);
                          setShowViewMenu(false);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 border rounded-md text-sm font-medium bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                        title="Click an area of the plan to jump to a close-up view centered there"
                      >
                        <span>Spotlight (pick area)</span>
                        <span className="text-xs text-gray-500">Close-up</span>
                      </button>
                      {isSpotlightPicking && (
                        <div className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded px-3 py-2">
                          Click the plan where you want to work. Press Esc to cancel.
                        </div>
                      )}
                    </div>

                    {/* Rotation Controls */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Rotation</label>
                      <div className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm">
                        <button
                          onClick={handleRotateLeft}
                          className="text-gray-700 hover:text-gray-900"
                          title="Rotate Left 90°"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                          </svg>
                        </button>
                        <span className="text-sm font-medium text-gray-700 w-12 text-center">
                          {rotation}°
                        </span>
                        <button
                          onClick={handleRotateRight}
                          className="text-gray-700 hover:text-gray-900"
                          title="Rotate Right 90°"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a8 8 0 00-8 8v2m18-10l-6 6m6-6l-6-6" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Toggle Options */}
                    <div className="space-y-2">
                      <button
                        onClick={() => {
                          setHideComments(!hideComments);
                          setShowViewMenu(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 border rounded-md text-sm font-medium ${
                          hideComments ? 'bg-gray-100 text-gray-500 border-gray-300' : 'bg-white text-gray-700 border-gray-300'
                        } hover:bg-gray-50`}
                        title={hideComments ? 'Show comment badges' : 'Hide comment badges (for printing)'}
                      >
                        <div className="flex items-center">
                          <svg className={`h-5 w-5 mr-2 ${hideComments ? 'opacity-50' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {hideComments ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            )}
                          </svg>
                          <span>Comment Badges</span>
                        </div>
                        <span className="text-xs text-gray-500">{hideComments ? 'Hidden' : 'Visible'}</span>
                      </button>

                      {/* Hide/Show Markers - Only show if plan exists */}
                      {effectivePlanImageUrl && (
                        <button
                          onClick={() => {
                            setShowPlantMarkers(!showPlantMarkers);
                            setShowViewMenu(false);
                          }}
                          className={`w-full flex items-center justify-between px-3 py-2 border rounded-md text-sm font-medium ${
                            showPlantMarkers 
                              ? 'border-green-300 text-green-700 bg-green-50' 
                              : 'border-gray-300 text-gray-700 bg-white'
                          } hover:bg-gray-50`}
                          title={showPlantMarkers ? "Hide plant markers on plan" : "Show plant markers on plan"}
                        >
                          <div className="flex items-center">
                            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {showPlantMarkers ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                              )}
                            </svg>
                            <span>Plant Markers</span>
                          </div>
                          <span className="text-xs text-gray-500">{showPlantMarkers ? 'Visible' : 'Hidden'}</span>
                        </button>
                      )}

                      {effectivePlanImageUrl && (
                        <button
                          onClick={() => {
                            setShowPlanZoneOverlays(!showPlanZoneOverlays);
                            setShowViewMenu(false);
                          }}
                          className={`w-full flex items-center justify-between px-3 py-2 border rounded-md text-sm font-medium ${
                            showPlanZoneOverlays
                              ? 'border-emerald-300 text-emerald-800 bg-emerald-50'
                              : 'border-gray-300 text-gray-700 bg-white'
                          } hover:bg-gray-50`}
                          title={
                            showPlanZoneOverlays
                              ? 'Hide zone outlines drawn in Zones (handoff)'
                              : 'Show zone outlines from plan_zones on the sheet'
                          }
                        >
                          <div className="flex items-center">
                            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                              />
                            </svg>
                            <span>Zone overlays on plan</span>
                          </div>
                          <span className="text-xs text-gray-500">{showPlanZoneOverlays ? 'On' : 'Off'}</span>
                        </button>
                      )}

                      {/* Refresh Markers - Only show if plan exists */}
                      {effectivePlanImageUrl && (
                        <button
                          onClick={async () => {
                            await loadPlacements();
                            setShowViewMenu(false);
                            alert('Plant markers refreshed!');
                          }}
                          className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white text-gray-700 hover:bg-gray-50"
                          title="Refresh plant markers to see updated images"
                        >
                          <div className="flex items-center">
                            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            <span>Refresh Markers</span>
                          </div>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
              </div>

              {/* Drawings - Dropdown Menu (keeps selector off the plan) */}
              {projectPlans.length > 0 && (
                <div className="relative drawing-menu-container">
                  <button
                    onClick={() => setShowDrawingMenu(!showDrawingMenu)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    title="Choose drawing"
                  >
                    <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h10" />
                    </svg>
                    Drawings
                    <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showDrawingMenu && (
                    <div className="absolute left-0 mt-2 w-[28rem] bg-white rounded-lg shadow-lg border border-gray-200 z-[70] p-4">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">Drawing</label>
                          <select
                            value={activePlanId || ''}
                            onChange={(e) => setActivePlanId(e.target.value)}
                            className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 bg-white"
                          >
                            {projectPlans.map((p) => (
                              <option key={p.id} value={p.id}>
                                {(p.category ? `[${p.category}] ` : '') + (p.title || 'Plan')}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const svg = generateBaseMapSvg({
                                  widthIn: sheet?.width_in || 24,
                                  heightIn: sheet?.height_in || 36,
                                  ppi: sheetPx.ppi || 40
                                });
                                const blob = new Blob([svg], { type: 'image/svg+xml' });
                                const file = new File(
                                  [blob],
                                  `base-map-${sheet?.width_in || 24}x${sheet?.height_in || 36}.svg`,
                                  { type: 'image/svg+xml' }
                                );
                                const formData = new FormData();
                                formData.append('planFile', file);
                                formData.append('title', `Base Map (${sheet?.width_in || 24}×${sheet?.height_in || 36})`);
                                formData.append('category', 'base');
                                formData.append('tags', JSON.stringify(['base-map']));
                                const resp = await projectPlansAPI.create(id, formData);
                                await loadProjectPlans();
                                if (resp?.plan?.id) setActivePlanId(resp.plan.id);
                                setShowDrawingMenu(false);
                              } catch (err) {
                                console.error('Load Base Map error:', err);
                                alert(`Failed to load Base Map: ${err.message}`);
                              }
                            }}
                            className="text-sm px-3 py-2 rounded-md bg-sky-600 text-white hover:bg-sky-700"
                            title="Create a new Base Map drawing with a 1-inch grid"
                          >
                            Base Map
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'image/jpeg,image/png,image/jpg,image/heic,image/heif,.heic,.heif,application/pdf';
                              input.onchange = async (ev) => {
                                let file = ev.target.files?.[0];
                                if (!file) return;

                                const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence', 'application/pdf'];
                                if (!validTypes.includes(file.type) && !isHeicFile(file)) {
                                  alert('Please upload a JPG, PNG, HEIC, or PDF file');
                                  return;
                                }
                                if (isHeicFile(file)) {
                                  try {
                                    file = await convertHeicToJpeg(file);
                                  } catch (err) {
                                    alert(err.message || 'Could not convert HEIC image. Please try a JPG or PNG.');
                                    return;
                                  }
                                }
                                if (file.size > 4 * 1024 * 1024) {
                                  alert('File size must be less than 4MB');
                                  return;
                                }

                                try {
                                  const sourcePlanId = activePlanId;
                                  const sourceNotesCount = planNotes.length;
                                  const sourcePlantsCount = placements.length;

                                  const formData = new FormData();
                                  formData.append('planFile', file);
                                  formData.append('title', file.name.replace(/\\.[^.]+$/, ''));
                                  formData.append('category', 'concept');
                                  formData.append('tags', JSON.stringify([]));
                                  const resp = await projectPlansAPI.create(id, formData);
                                  await loadProjectPlans();
                                  if (resp?.plan?.id) setActivePlanId(resp.plan.id);

                                  // Offer to transfer content from current drawing to new one (optional)
                                  if (resp?.plan?.id && sourcePlanId && (sourceNotesCount > 0 || sourcePlantsCount > 0)) {
                                    setTransferSourcePlanId(sourcePlanId);
                                    setTransferTargetPlanId(resp.plan.id);
                                    setTransferSourceNotesCount(sourceNotesCount);
                                    setTransferSourcePlantsCount(sourcePlantsCount);
                                    setTransferCopyNotes(sourceNotesCount > 0);
                                    setTransferCopyPlants(sourcePlantsCount > 0);
                                    setShowPlanTransferModal(true);
                                  }
                                  setShowDrawingMenu(false);
                                } catch (err) {
                                  console.error('Upload plan error:', err);
                                  alert(`Failed to upload drawing: ${err.message}`);
                                }
                              };
                              input.click();
                            }}
                            className="text-sm px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                            title="Upload a new drawing (JPG/PNG/PDF)"
                          >
                            Upload
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              // Default values for new drawing
                              setNewPlanTitle('');
                              setNewPlanCategory('concept');
                              setNewPlanTagsInput('');
                              setPlanLinkInput('');
                              setShowPlanLinkModal(true);
                              setShowDrawingMenu(false);
                            }}
                            className="text-sm px-3 py-2 rounded-md bg-green-600 text-white hover:bg-green-700"
                            title="Add another drawing by link (concept / working / planting)"
                          >
                            Link
                          </button>
                        </div>

                        <div className="pt-3 mt-2 border-t border-gray-200 flex items-center justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => setShowDrawingMenu(false)}
                            className="text-sm px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                          >
                            Close
                          </button>
                          <button
                            type="button"
                            onClick={deleteActiveDrawing}
                            className="text-sm px-3 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-300"
                            disabled={!activePlanId}
                            title="Delete this drawing"
                          >
                            Delete drawing
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Plants Group */}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-2 px-3 py-2 bg-green-50 rounded-lg border border-green-200 max-w-full min-w-0">
                <span className="text-xs text-green-600 font-medium mr-1 shrink-0">Plants:</span>
                {effectivePlanImageUrl && (
                  <button
                    onClick={handleAutoDetectPlants}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-purple-600"
                    disabled={effectivePlanImageUrlIsPdf}
                    title={
                      effectivePlanImageUrlIsPdf
                        ? 'Auto-detect needs a raster image (JPG/PNG), not PDF'
                        : 'Use AI to detect handwritten plant labels from your plan'
                    }
                  >
                    <svg className="mr-2 -ml-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    🪄 Auto-Detect Plants
                  </button>
                )}
                {effectivePlanImageUrl && !effectivePlanImageUrlIsPdf && (
                  <div className="flex flex-col gap-1 min-w-0 max-w-full">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-700 min-w-0">
                      <label className="inline-flex items-start gap-1.5 cursor-pointer shrink-0 max-w-[200px]">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 mt-0.5 shrink-0"
                          checked={ocrUseProjectListContext}
                          onChange={(e) => setOcrUseProjectListContext(e.target.checked)}
                        />
                        <span>
                          <span className="font-medium text-gray-800 block">Send project list to AI</span>
                          <span
                            className="text-[11px] text-gray-500 block"
                            title="Appends this project’s Project Plant List to the prompt so abbreviations map to your names."
                          >
                            Helps read abbreviations and tie labels to your list names.
                          </span>
                        </span>
                      </label>
                      <label className="inline-flex items-start gap-1.5 cursor-pointer shrink-0 max-w-[200px]">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 mt-0.5 shrink-0"
                          checked={ocrUseProjectListOnly}
                          onChange={(e) => setOcrUseProjectListOnly(e.target.checked)}
                        />
                        <span>
                          <span className="font-medium text-gray-800 block">Match project list only</span>
                          <span
                            className="text-[11px] text-gray-500 block"
                            title="Suggest matches only from this project’s Project Plant List—not the global plant library."
                          >
                            Do not suggest plants from the global library.
                          </span>
                        </span>
                      </label>
                    </div>
                    <p className="text-[11px] text-gray-500 leading-snug max-w-xl">
                      Designer library / all projects is not used here—only this project’s Project Plant List.
                    </p>
                  </div>
                )}
                <button
                  onClick={() => {
                    const qs = activePlanId ? `?planId=${encodeURIComponent(activePlanId)}` : '';
                    navigate(`/plan/${id}/master-list${qs}`);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  title="Open this project's Project Plant List"
                >
                  🌱 Project Plant List
                </button>

                {effectivePlanImageUrl && (
                  <button
                    onClick={() => setShowZoneManager(true)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    title="Draw named zones on this plan. Used when handing off to the homeowner Garden App."
                  >
                    🗺️ Zones
                  </button>
                )}

                <button
                  onClick={() => setShowHandoffModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700"
                  title="Send this project's plants to the homeowner as a prepopulated Garden App"
                >
                  🌿 Send to Homeowner
                </button>

                <button
                  onClick={() => setShowProjectGardenTab(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  title="View handoff status and open the homeowner's Garden App in co-edit mode"
                >
                  🏡 Homeowner Garden
                </button>

                <button
                  onClick={() => setShowPlantSheet(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  title="Open the Plant Sheet (image grid with links to placements)"
                >
                  🧾 Plant Sheet
                </button>

                <button
                  onClick={() => {
                    if (!effectivePlanImageUrl) {
                      alert('Upload or link a drawing first.');
                      return;
                    }
                    setIsPlacingPlant(false);
                    setSelectedPlants([]);
                    setIsPlacingNote(true);
                  }}
                  className={`inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium ${
                    isPlacingNote ? 'border-yellow-300 bg-yellow-50 text-yellow-800' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  title="Add a free-form note to the drawing (click the plan where you want it)"
                >
                  📝 Add Note
                </button>
              </div>
            </div>

            {/* Right Side: Project Files and Collaborate */}
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              {/* Files Button - Opens Collaboration panel to Files tab */}
              <button
                onClick={() => {
                  setShowComments(true);
                  setActiveTab('files');
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                title="Files shared with homeowner and trades (opens the right panel)"
              >
                <File className="mr-2 -ml-1 h-5 w-5" />
                Files
              </button>

              {/* Collaborate Button - Opens sidebar - Larger and more prominent, right-aligned */}
              <button
                onClick={() => {
                  setShowComments(true);
                  setActiveTab('notes');
                }}
                className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-lg text-base font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 transition-all"
              >
                <svg className="mr-2 -ml-1 h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Collaborate
              </button>
            </div>
            <p className="w-full basis-full text-xs text-gray-600 leading-snug mt-0.5 px-1">
              <span className="font-medium text-gray-700">Collaborate</span> invites people to this project (chat, files, plan feedback).
              {' '}
              <span className="font-medium text-gray-700">Send to Homeowner</span> emails them a link to their prepopulated Garden App.
            </p>
          </div>

          {effectivePlanImageUrl && isPlacingPlant && selectedPlants.length > 0 && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-blue-900 font-medium">
                  Click on the plan to place {selectedPlants.length} plant{selectedPlants.length > 1 ? 's' : ''}
                </span>
              </div>
              <button
                onClick={() => {
                  setIsPlacingPlant(false);
                  setSelectedPlants([]);
                }}
                className="text-blue-700 hover:text-blue-900 text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          )}

          {effectivePlanImageUrl && isPlacingNote && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-yellow-900 font-medium">
                  Click on the drawing to place a note
                </span>
              </div>
              <button
                onClick={() => setIsPlacingNote(false)}
                className="text-yellow-700 hover:text-yellow-900 text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} h-[calc(100vh-120px)]`}>
        {/* Canvas Area - On mobile, add padding bottom for the bottom sheet */}
        <div 
          ref={containerRef}
          className={`${isMobile ? 'flex-1' : 'flex-1'} relative overflow-hidden bg-gray-200`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ 
            cursor: draggingPlacement
              ? 'grabbing'
              : isPanning
                ? 'grabbing'
                : (isPlacingPlant || isPlacingNote || isSpotlightPicking)
                  ? 'crosshair'
                  : 'grab',
            userSelect: 'none',
            paddingBottom: isMobile && showPlantSidebar ? (plantSheetExpanded ? '75vh' : '144px') : '0'
          }}
        >
          {!effectivePlanImageUrl ? (
            /* No Plan View - Action Cards */
            <div className="flex items-center justify-center h-full p-8 pt-16">
              <div className="max-w-4xl w-full">
                <div className="text-center mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">No Plan Uploaded</h2>
                  <p className="text-gray-600">This project doesn't have a visual plan yet. You can still collaborate and manage files.</p>
                </div>
                
                {/* Action Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  <button
                    onClick={() => {
                      // Open file input to upload plan
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/jpeg,image/png,image/jpg,image/heic,image/heif,.heic,.heif,application/pdf';
                      input.onchange = async (e) => {
                        let file = e.target.files[0];
                        if (!file) return;
                        
                        const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence', 'application/pdf'];
                        if (!validTypes.includes(file.type) && !isHeicFile(file)) {
                          alert('Please upload a JPG, PNG, HEIC, or PDF file');
                          return;
                        }
                        if (isHeicFile(file)) {
                          try {
                            file = await convertHeicToJpeg(file);
                          } catch (err) {
                            alert(err.message || 'Could not convert HEIC image. Please try a JPG or PNG.');
                            return;
                          }
                        }

                        if (file.size > 4 * 1024 * 1024) {
                          alert('File size must be less than 4MB');
                          return;
                        }
                        
                        // Upload drawing to project (multi-plan)
                        try {
                          const sourcePlanId = activePlanId;
                          const sourceNotesCount = planNotes.length;
                          const sourcePlantsCount = placements.length;

                          const formData = new FormData();
                          formData.append('planFile', file);
                          formData.append('title', file.name || 'Plan');
                          formData.append('category', 'concept');
                          formData.append('tags', JSON.stringify([]));

                          const resp = await projectPlansAPI.create(id, formData);
                          await loadProjectPlans();
                          if (resp?.plan?.id) {
                            setActivePlanId(resp.plan.id);
                            maybeOfferTransferToNewPlan({
                              sourcePlanId,
                              targetPlanId: resp.plan.id,
                              sourceNotesCount,
                              sourcePlantsCount
                            });
                          }
                        } catch (error) {
                          console.error('Error uploading plan:', error);
                          alert(`Failed to upload plan: ${error.message}`);
                        }
                      };
                      input.click();
                    }}
                    className="p-6 bg-white border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left group"
                  >
                    <Upload className="w-8 h-8 text-gray-400 group-hover:text-green-600 mb-3" />
                    <h3 className="font-semibold text-gray-900 mb-1">Upload a Design</h3>
                    <p className="text-sm text-gray-600">
                      Start a collaboration workspace where you can upload a design, add plants or notes, chat with homeowners or trades, and create a Garden App for the homeowner.
                    </p>
                  </button>
                  
                  <button
                    onClick={() => {
                      // Open plan link modal
                      setPlanLinkInput(effectivePlanImageUrl && effectivePlanImageUrl.startsWith('http') ? effectivePlanImageUrl : '');
                      setNewPlanTitle('');
                      setNewPlanCategory('concept');
                      setNewPlanTagsInput('');
                      setShowPlanLinkModal(true);
                    }}
                    className="p-6 bg-white border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left group"
                  >
                    <LinkIcon className="w-8 h-8 text-gray-400 group-hover:text-green-600 mb-3" />
                    <h3 className="font-semibold text-gray-900 mb-1">Link to Plan</h3>
                    <p className="text-sm text-gray-600">Add a link to an external plan or design document</p>
                  </button>
                </div>
                
                {/* Files live in the Collaboration drawer (keeps a single, consistent place) */}
              </div>
            </div>
          ) : effectivePlanImageUrl ? (
            <div
              ref={canvasRef}
              className="absolute inset-0 flex items-center justify-center relative"
              onClick={handleCanvasClick}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleCanvasDrop}
            >
              {/* Drawing selector moved into header (Drawings menu) */}

              <div
                style={{
                  transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                  transformOrigin: 'center center',
                  transition: isPanning ? 'none' : 'transform 0.2s ease-out',
                  width: sheetPx.w,
                  height: sheetPx.h
                }}
                className="relative bg-white shadow"
              >
                {effectivePlanImageUrl && effectivePlanImageUrl.startsWith('http') ? (
                  // If it's a link, try to embed it if it's a PDF or image, otherwise show clickable link
                  (() => {
                    const url = effectivePlanImageUrl;
                    const isPDF = url.toLowerCase().endsWith('.pdf') || url.includes('pdf');
                    const isImage = /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(url) || url.includes('image');
                    
                    if (isPDF) {
                      // Try to embed PDF
                      return (
                        <div className="relative w-full h-full">
                          <iframe
                            src={url}
                            className="w-full"
                            style={{ height: '100%', border: 'none' }}
                            title="Plan PDF"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-lg">
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-green-600 hover:text-green-700 font-medium"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <LinkIcon className="w-4 h-4" />
                              Open in new tab
                            </a>
                          </div>
                        </div>
                      );
                    } else if (isImage) {
                      // If it's an image URL, try to display it
                      return (
                        <img
                          src={url}
                          alt={project.name}
                          className="max-w-none"
                          style={{ width: '100%', height: '100%', userSelect: 'none' }}
                          draggable="false"
                          onError={(e) => {
                            // If image fails to load, show link card instead
                            e.target.style.display = 'none';
                            const parent = e.target.parentElement;
                            if (parent && !parent.querySelector('.link-fallback')) {
                              const fallback = document.createElement('div');
                              fallback.className = 'link-fallback bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center';
                              fallback.innerHTML = `
                                <svg class="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                                <p class="text-lg font-semibold text-gray-900 mb-2">Plan Link</p>
                                <p class="text-sm text-gray-600 mb-4 break-all">${url}</p>
                                <a href="${url}" target="_blank" rel="noopener noreferrer" class="text-sm text-green-600 font-medium hover:text-green-700">Click to open plan in new tab</a>
                              `;
                              parent.appendChild(fallback);
                            }
                          }}
                        />
                      );
                    } else {
                      // For other links, show clickable card
                      return (
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                          onClick={(e) => {
                            // Don't trigger canvas click when clicking the link
                            e.stopPropagation();
                          }}
                        >
                          <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-green-500 hover:bg-green-50 transition-all">
                            <LinkIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <p className="text-lg font-semibold text-gray-900 mb-2">Plan Link</p>
                            <p className="text-sm text-gray-600 mb-4 break-all">{url}</p>
                            <p className="text-sm text-green-600 font-medium">Click to open plan in new tab</p>
                          </div>
                        </a>
                      );
                    }
                  })()
                ) : (
                  // If it's an uploaded image, display it normally
                  <img
                    src={effectivePlanImageUrl}
                    alt={project.name}
                    className="max-w-none"
                    style={{ width: '100%', height: '100%', userSelect: 'none' }}
                    draggable="false"
                  />
                )}

                {showPlanZoneOverlays &&
                  planZoneOverlayItems.length > 0 &&
                  effectivePlanImageUrl &&
                  !effectivePlanImageUrlIsPdf && (
                    <svg
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      viewBox="0 0 100 100"
                      preserveAspectRatio="none"
                      style={{ zIndex: 4 }}
                      aria-hidden
                    >
                      {planZoneOverlayItems.map((r) => {
                        if (r.shape_type === 'polygon') {
                          const pts = r.shape?.points || [];
                          if (pts.length < 3) return null;
                          const pointsAttr = pts.map((p) => `${Number(p.x) || 0},${Number(p.y) || 0}`).join(' ');
                          return (
                            <polygon
                              key={r.id}
                              points={pointsAttr}
                              fill={r.fill}
                              stroke={r.stroke}
                              strokeWidth={0.22}
                              vectorEffect="non-scaling-stroke"
                            />
                          );
                        }
                        const s = r.shape || {};
                        const x = Number(s.x) || 0;
                        const y = Number(s.y) || 0;
                        const w = Number(s.width) || 0;
                        const h = Number(s.height) || 0;
                        return (
                          <rect
                            key={r.id}
                            x={x}
                            y={y}
                            width={w}
                            height={h}
                            rx={0.4}
                            fill={r.fill}
                            stroke={r.stroke}
                            strokeWidth={0.22}
                            vectorEffect="non-scaling-stroke"
                          />
                        );
                      })}
                    </svg>
                  )}

                {/* Spotlight anchor (used to center chosen area) */}
                {spotlightTarget && (
                  <div
                    data-spotlight-target="1"
                    className="absolute"
                    style={{
                      left: `${spotlightTarget.x}%`,
                      top: `${spotlightTarget.y}%`,
                      width: '1px',
                      height: '1px',
                      pointerEvents: 'none'
                    }}
                  />
                )}
                
                {/* Plan Notes (free-form annotations) */}
                {planNotes.map((note) => {
                  const noteId = note.id;
                  const count = comments.filter((c) => c.plan_note_id === noteId).length;
                  const isEditing = editingNoteId === noteId;
                  const w = Number(note.width) || 0;
                  const h = Number(note.height) || 0;
                  const boxW = w > 0 ? `${w}%` : '10%';
                  const boxH = h > 0 ? `${h}%` : '8%';
                  const isDragging = draggingNoteId === noteId;
                  const isResizing = resizingNoteId === noteId;

                  return (
                    <div
                      key={noteId}
                      className="absolute"
                      style={{
                        left: `${note.position_x || 0}%`,
                        top: `${note.position_y || 0}%`,
                        width: boxW,
                        height: boxH,
                        zIndex: isDragging || isResizing ? 1200 : 25
                      }}
                      onPointerMove={(e) => {
                        if (draggingNoteId) handleNotePointerMove(e);
                        if (resizingNoteId) handleNoteResizePointerMove(e);
                      }}
                      onPointerUp={async (e) => {
                        if (draggingNoteId) await handleNotePointerUp(e);
                        if (resizingNoteId) await handleNoteResizePointerUp(e);
                      }}
                    >
                      <div
                        className={`w-full h-full rounded-md border shadow-sm ${
                          selectedNoteForComment?.id === noteId ? 'border-yellow-500 ring-2 ring-yellow-200' : 'border-yellow-200'
                        } bg-yellow-50/95 backdrop-blur-sm`}
                        style={{
                          cursor: isDragging ? 'grabbing' : 'grab',
                          // The whole plan surface is rotated. Counter-rotate the note so the text
                          // stays readable regardless of the plan rotation.
                          transform: `rotate(${-rotation}deg)`,
                          transformOrigin: 'center center'
                        }}
                        onPointerDown={(e) => handleNotePointerDown(e, note)}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedNoteForComment(note);
                          setSelectedPlantForComment(null);
                          setShowComments(true);
                          setActiveTab('notes');
                          setEditingNoteId(noteId);
                          setNoteEditorValue(note.note_text || '');
                        }}
                      >
                        <div className="flex items-start justify-between gap-2 p-2 h-full">
                          <div className="flex-1 min-w-0">
                            {isEditing ? (
                              <textarea
                                autoFocus
                                value={noteEditorValue}
                                onChange={(e) => setNoteEditorValue(e.target.value)}
                                onBlur={async () => {
                                  const text = noteEditorValue || '';
                                  setEditingNoteId(null);
                                  await saveNoteText(noteId, text);
                                }}
                                placeholder="Type note..."
                                className="w-full h-full text-sm bg-transparent border border-yellow-200 rounded p-1 resize-none focus:outline-none focus:ring-2 focus:ring-yellow-300"
                              />
                            ) : (
                              <div className="text-sm text-gray-800 whitespace-pre-wrap overflow-hidden">
                                {note.note_text ? note.note_text : <span className="text-gray-400 italic">Click to type…</span>}
                              </div>
                            )}
                          </div>

                          {/* Comment count badge */}
                          {!hideComments && count > 0 && (
                            <div className="shrink-0 text-[11px] font-semibold text-white bg-blue-600 rounded-full px-2 py-0.5">
                              {count}
                            </div>
                          )}
                        </div>

                        {/* Resize handle */}
                        <div
                          className="absolute right-0 bottom-0 w-3 h-3 bg-yellow-300 border border-yellow-500 rounded-sm cursor-nwse-resize"
                          onPointerDown={(e) => handleNoteResizePointerDown(e, note)}
                          onClick={(e) => e.stopPropagation()}
                          title="Resize"
                        />
                      </div>
                    </div>
                  );
                })}

                {/* Plant Placement Markers */}
                {showPlantMarkers && placements.map(placement => {
                  // Symbol config is stored in placement.notes (JSON string)
                  let symbolType = placement?.symbol_id || 'circle';
                  let symbolSizeIn = Number.isFinite(Number(placement?.symbol_size_in))
                    ? Number(placement?.symbol_size_in)
                    : 0.5;
                  let markerSize = Math.max(10, Math.round(0.5 * (sheetPx.ppi || 40)));
                  try {
                    const notesData = placement.notes ? JSON.parse(placement.notes) : {};
                    // Legacy/back-compat: only pull from notes when explicit columns are absent
                    symbolType = (placement?.symbol_id ? symbolType : (notesData.symbol || symbolType));
                    const n = Number(notesData.symbol_size_in);
                    if (!placement?.symbol_size_in) symbolSizeIn = Number.isFinite(n) ? n : symbolSizeIn;
                    markerSize = Math.max(10, Math.round(symbolSizeIn * (sheetPx.ppi || 40)));
                  } catch (e) {
                    // keep defaults
                  }
                  
                  const isDragging = draggingPlacement === placement.id;
                  
                  return (
                    <div
                      key={placement.id}
                      data-placement-id={placement.id}
                      className="absolute transform -translate-x-1/2 -translate-y-1/2"
                      style={{
                        left: `${(placement.position_x / 100) * 100}%`,
                        top: `${(placement.position_y / 100) * 100}%`,
                        cursor: isDragging ? 'grabbing' : 'grab',
                        zIndex: isDragging ? 1000 : selectedPlacementId === placement.id ? 20 : 10,
                      }}
                      onMouseDown={(e) => handlePlantMouseDown(e, placement)}
                      onTouchStart={(e) => handlePlantTouchStart(e, placement)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setSelectedPlacementId(placement.id);
                        setShowControls(true);
                      }}
                    >
                      {/* Plant Marker */}
                      <div className="relative group">
                        <div 
                          className="relative select-none"
                          style={{
                            width: `${markerSize}px`,
                            height: `${markerSize}px`,
                          }}
                        >
                          {/* Symbol marker (no plant photos on plan) */}
                          <div
                            className="w-full h-full rounded-sm overflow-hidden border-2 border-white shadow-lg bg-white hover:border-green-400 transition-colors"
                            style={{
                              boxShadow: isDragging ? '0 8px 16px rgba(0,0,0,0.3)' : '0 4px 6px rgba(0,0,0,0.1)',
                              transform: isDragging
                                ? `scale(1.1) rotate(${-rotation}deg)`
                                : `rotate(${-rotation}deg) scale(1)`,
                              transition: isDragging ? 'none' : 'all 0.2s ease',
                              cursor: isDragging ? 'grabbing' : 'grab'
                            }}
                            onClick={async (e) => {
                              if (isDragging) return;
                              e.stopPropagation();

                              // Keep the existing plant card workflow (details + edit quantity),
                              // even though the plan no longer shows photo markers.
                              setCurrentPlacementId(placement.id);
                              if (placement.plant_id) {
                                try {
                                  const plantData = await plantsAPI.getById(placement.plant_id);
                                  setSelectedPlantCard(plantData);
                                } catch (error) {
                                  console.error('Error fetching plant:', error);
                                  setSelectedPlantCard(placement);
                                }
                              } else {
                                setSelectedPlantCard(placement);
                              }
                              setShowPlantCard(true);
                            }}
                          >
                            <div className="w-full h-full flex items-center justify-center bg-white">
                              <svg viewBox={getSymbolViewBox(symbolType, customSymbolsById)} className="w-full h-full">
                                {renderSymbolSvgChildren(symbolType, { customSymbolsById })}
                              </svg>
                            </div>
                          </div>
                          
                          {/* Quantity Badge */}
                          {placement.quantity > 1 && (
                            <div 
                              className="absolute -top-1 -right-1 bg-red-500 border-2 border-white rounded-full flex items-center justify-center text-white font-bold shadow-lg pointer-events-none"
                              style={{
                                width: `${Math.max(20, markerSize * 0.4)}px`,
                                height: `${Math.max(20, markerSize * 0.4)}px`,
                                fontSize: `${Math.max(10, markerSize * 0.25)}px`,
                                transform: `rotate(${-rotation}deg)`
                              }}
                            >
                              {placement.quantity}
                            </div>
                          )}
                          
                          {/* Comment Badge - top-left */}
                          {!hideComments && getPlantComments(placement.id).length > 0 && (
                            <div 
                              className="absolute -top-1 -left-1 bg-purple-500 border-2 border-white rounded-full flex items-center justify-center text-white font-bold shadow-lg pointer-events-none"
                              style={{
                                width: `${Math.max(20, markerSize * 0.4)}px`,
                                height: `${Math.max(20, markerSize * 0.4)}px`,
                                fontSize: `${Math.max(10, markerSize * 0.25)}px`,
                                transform: `rotate(${-rotation}deg)`
                              }}
                              title={`${getPlantComments(placement.id).length} comment(s)`}
                            >
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                          
                          {/* Selection ring */}
                          <div className={`absolute inset-0 rounded-sm border-2 ${
                            selectedPlacementId === placement.id ? 'border-blue-500 opacity-100' : 
                            isDragging || resizingPlacement === placement.id ? 'border-green-500 opacity-100' : 
                            'border-green-500 opacity-0 group-hover:opacity-50'
                          } transition-opacity pointer-events-none`}></div>
                          
                          
                          {/* Info label when selected - desktop always shows, mobile only on long-press */}
                          {selectedPlacementId === placement.id && showControls && !isDragging && !resizingPlacement && (
                            <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-3 py-2 rounded-lg font-semibold shadow-lg flex items-center gap-2 text-xs whitespace-nowrap" style={{ pointerEvents: 'auto', transform: `translate(-50%, 0) rotate(${-rotation}deg)` }}>
                              <span>{placement.common_name} • {placement.quantity}x</span>
                              <button
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                onTouchStart={(e) => {
                                  e.stopPropagation();
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setSelectedPlantForComment(placement);
                                  setSelectedNoteForComment(null);
                                  setShowComments(true);
                                }}
                                className="bg-purple-500 hover:bg-purple-600 px-2 py-0.5 rounded text-xs font-normal flex items-center gap-1"
                                title="Add comment about this plant"
                              >
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                                </svg>
                                Comment
                              </button>
                              <button
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                onTouchStart={(e) => {
                                  e.stopPropagation();
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setEditingPlacement(placement);
                                }}
                                className="bg-blue-600 hover:bg-blue-700 px-2 py-0.5 rounded text-xs font-normal"
                                title="Edit quantity"
                              >
                                Edit Qty
                              </button>
                              <button
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                onTouchStart={(e) => {
                                  e.stopPropagation();
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleDeletePlacement(placement.id);
                                }}
                                className="bg-red-500 hover:bg-red-600 px-2 py-0.5 rounded text-xs font-normal"
                                title="Delete (or press Delete key)"
                              >
                                Delete
                              </button>
                              <button
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                onTouchStart={(e) => {
                                  e.stopPropagation();
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setSelectedPlacementId(null);
                                  setShowControls(false);
                                }}
                                className="bg-gray-600 hover:bg-gray-700 px-1.5 py-0.5 rounded text-xs font-normal ml-1"
                                title="Close"
                              >
                                ✕
                              </button>
                            </div>
                          )}
                          
                          {resizingPlacement === placement.id && (
                            <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-xs text-white bg-green-600 px-2 py-1 rounded whitespace-nowrap pointer-events-none font-semibold shadow-lg" style={{ transform: `translate(-50%, 0) rotate(${-rotation}deg)` }}>
                              Size: {markerSize}px
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <svg
                  className="mx-auto h-16 w-16 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <p className="mt-2 text-sm text-gray-500">No plan image uploaded</p>
              </div>
            </div>
          )}

          {/* Instructions overlay */}
          {placements.length === 0 && effectivePlanImageUrl && showInstructionsTip && (
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-white/90 backdrop-blur-sm rounded-lg px-4 py-3 shadow-lg max-w-md">
              <div className="flex items-start gap-3">
                <p className="text-sm text-gray-700 flex-1">
                  💡 <strong>Tip:</strong> Use the Project Plant List to select plants, then click the plan to place them. Once placed, <strong>drag to move</strong> markers on the plan. Use <strong>Zones</strong> when you want to attach plants to areas.
                </p>
                <button
                  type="button"
                  onClick={() => setShowInstructionsTip(false)}
                  className="text-gray-500 hover:text-gray-700"
                  title="Close tip"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
          
          {/* Controls hint when plants exist */}
          {showControlsHint && placements.length > 0 && !draggingPlacement && !isPlacingPlant && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/75 text-white text-xs rounded-lg px-3 py-2 shadow-lg transition-opacity duration-500">
              � Click & drag plants to move • Right-click or hold 500ms for options
            </div>
          )}
        </div>

        {/* Plant Sidebar - Desktop: Fixed sidebar, Mobile: Bottom sheet - Show always */}
        {showPlantSidebar ? (
          <div className={`
            ${isMobile 
              ? `fixed bottom-0 left-0 right-0 bg-white shadow-2xl transition-all duration-300 z-40 rounded-t-2xl
                 ${plantSheetExpanded ? 'h-[75vh]' : 'h-36'}`
              : 'w-80 bg-white shadow-xl border-l border-gray-200'
            } flex flex-col ${!isMobile ? 'h-full' : ''}
          `}>
            {/* Mobile: Drag handle */}
            {isMobile && (
              <div 
                className="p-2 flex justify-center cursor-pointer"
                onClick={() => setPlantSheetExpanded(!plantSheetExpanded)}
              >
                <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
              </div>
            )}
            
            {/* Project plant list: master_plant_list rows for this project only */}
            <div className={`p-4 pt-2 ${!isMobile || plantSheetExpanded ? 'border-b border-gray-200' : ''}`}>
              <div className="flex justify-between items-start gap-2 mb-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900">
                    This project’s plants
                  </h2>
                  <p className="text-[11px] text-gray-500 mt-1 leading-snug">
                    Plants identified, imported, or added for this job — not your full designer library.
                    Manage the full table on{' '}
                    <button
                      type="button"
                      className="text-emerald-700 font-medium underline"
                      onClick={() => navigate(`/plan/${id}/master-list`)}
                    >
                      Project Plant List
                    </button>
                    .
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(`/plan/${id}/master-list`)}
                    className="p-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                    title="Open full Project Plant List"
                  >
                    <FileText size={18} />
                  </button>
                  {isMobile && (
                    <button
                      type="button"
                      onClick={() => setPlantSheetExpanded(!plantSheetExpanded)}
                      className="text-gray-400 hover:text-gray-600"
                      title={plantSheetExpanded ? "Collapse" : "Expand"}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {plantSheetExpanded ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        )}
                      </svg>
                    </button>
                  )}
                  {!isMobile && (
                    <button
                      type="button"
                      onClick={() => setShowPlantSidebar(false)}
                      className="text-gray-400 hover:text-gray-600 p-1"
                      title="Hide Project Plant List"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Project Plant List controls */}
              {(!isMobile || plantSheetExpanded) && (
                <>
                  <input
                    type="text"
                    placeholder="Search project plants…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <div className="mt-2">
                    <label className="block text-[11px] font-semibold text-gray-600 mb-1">Type</label>
                    <select
                      value={plantTypeFilter}
                      onChange={(e) => setPlantTypeFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      title="Filter project plants by Type"
                    >
                      <option value="">All Types</option>
                      <option value="__missing__">Missing Type</option>
                      {availableProjectPlantTypes.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  {selectedPlants.length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-semibold text-blue-900">
                          {selectedPlants.length} selected
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedPlants([]);
                              setIsPlacingPlant(false);
                            }}
                            className="px-3 py-1.5 bg-white border border-blue-200 text-blue-700 rounded-lg text-xs font-bold hover:bg-blue-100"
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            onClick={() => setIsPlacingPlant(true)}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700"
                            title="Tap/click the plan to place"
                          >
                            Place
                          </button>
                        </div>
                      </div>
                      <div className="mt-1 text-[11px] text-blue-900/80">
                        Click Place, then click the plan to drop plants. Drag markers on the plan to move them. Use Zones when you are ready to group areas.
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Plant List - Mobile: Horizontal when collapsed, vertical when expanded */}
            <div className={`flex-1 overflow-auto p-4 ${
              isMobile && !plantSheetExpanded 
                ? 'flex flex-row gap-3 overflow-x-auto' 
                : 'flex flex-col space-y-3 overflow-y-auto'
            }`}>
              {/* Rows from GET /plans/:id/master-list (this project only) */}
              <>
                  {masterListItems.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                      <FileText size={32} className="mx-auto text-gray-300 mb-3" />
                      <p className="text-xs text-gray-500 mb-4">No plants in this project yet.</p>
                      <button onClick={() => navigate(`/plan/${id}/master-list`)} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-bold uppercase transition-colors hover:bg-blue-700">
                        Open Project Plant List
                      </button>
                    </div>
                  ) : (
                    filteredMasterListItems.map(item => {
                      const safeItemImg = coerceImageUrl(item.image_url);
                      const resolvedItemPlantId = item.plant_id || item.plantId || null;
                      // Manual project-list items can be placed even without an image, since the plan
                      // renders markers (not photos). Backend requires a "manual_common_name" value.
                      // Some manual entries only have botanical_name, so treat that as placeable too.
                      const manualCanPlace = !resolvedItemPlantId && !!(item.common_name || item.botanical_name);
                      const canPlace = !!resolvedItemPlantId || manualCanPlace;
                      const placed = placedByMasterListId[item.id] || 0;
                      const plantProxy = {
                        ...item,
                        id: resolvedItemPlantId,
                        plant_id: resolvedItemPlantId,
                        master_list_id: item.id,
                        symbol_id: item.symbol_id || item.symbol || 'circle',
                        symbol: item.symbol_id || item.symbol || 'circle',
                        symbol_size_in: item.symbol_size_in ?? 0.5,
                        // for manual placements
                        manual_common_name: !resolvedItemPlantId ? (item.common_name || item.botanical_name || null) : null,
                        manual_botanical_name: !resolvedItemPlantId ? (item.botanical_name || null) : null,
                        manual_image_url: !resolvedItemPlantId ? (safeItemImg || null) : null
                      };
                      const isSelected = selectedPlants.some(p => p.master_list_id === item.id);
                      return (
                        <div
                          key={item.id}
                          draggable={false}
                          onMouseDownCapture={() => {
                            // #region agent log
                            __agentDebugIngest({
                              runId: 'placeSymbol_run2',
                              hypothesisId: 'H0',
                              location: 'src/PlanDetail.js:plantCard:onMouseDownCapture',
                              message: 'MouseDown on project plant card',
                              data: {
                                canPlace,
                                master_list_id: plantProxy?.master_list_id || null,
                                plant_id: plantProxy?.plant_id || null
                              },
                              timestamp: Date.now()
                            });
                            // #endregion
                            // Avoid "drag selected text" behavior hijacking the drag gesture.
                            try { window.getSelection?.()?.removeAllRanges?.(); } catch {}
                          }}
                          onClick={() => {
                            if (!canPlace) {
                              navigate(`/plan/${id}/master-list`);
                              return;
                            }
                            // #region agent log
                            __agentDebugIngest({
                              runId: 'placeSymbol_run2',
                              hypothesisId: 'H5',
                              location: 'src/PlanDetail.js:plantCard:onClick',
                              message: 'Clicked project plant card',
                              data: {
                                canPlace,
                                master_list_id: plantProxy?.master_list_id || null,
                                plant_id: plantProxy?.plant_id || null,
                                activePlanId: activePlanId || null
                              },
                              timestamp: Date.now()
                            });
                            // #endregion
                            handleSelectPlant(plantProxy);
                          }}
                          className={`border-2 rounded-xl p-3 cursor-pointer transition-all group select-none ${
                            isSelected ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-100 bg-white hover:border-blue-300 hover:shadow-sm'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-14 h-14 bg-gray-50 rounded-lg overflow-hidden relative shrink-0 border border-gray-100">
                              {safeItemImg ? (
                                <img
                                  src={safeItemImg}
                                  alt={item.common_name}
                                  className="w-full h-full object-cover"
                                  draggable={false}
                                  onDragStart={(e) => {
                                    // Prevent native image drag (which produces text/uri-list/html payloads)
                                    // so the parent card drag handler can set our custom "plant" payload.
                                    e.preventDefault();
                                  }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-300">
                                  {!item.plant_id ? <Sparkles size={20} className="text-blue-200" /> : <SearchIcon size={20} />}
                                </div>
                              )}
                              <div className="absolute -top-1 -right-1">
                                <span className="bg-blue-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">{item.quantity}</span>
                              </div>
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start justify-between gap-2">
                                <h4 className="text-xs font-bold text-gray-900 truncate group-hover:text-blue-600">
                                  {item.common_name || item.botanical_name || 'Project plant'}
                                </h4>
                                <div className="shrink-0 flex items-center gap-1">
                                  <button
                                    type="button"
                                    className="px-2 py-1 rounded-md bg-green-600 text-white text-[10px] font-bold hover:bg-green-700 disabled:bg-gray-300"
                                    disabled={!canPlace}
                                    title={canPlace ? 'Arm placement, then click the plan to drop this plant (drag on plan to move)' : 'Missing required fields to place'}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (!canPlace) return;
                                      // #region agent log
                                      __agentDebugIngest({
                                        runId: 'placeSymbol_run2',
                                        hypothesisId: 'H5',
                                        location: 'src/PlanDetail.js:plantCard:placeButton',
                                        message: 'Arm placement for single plant',
                                        data: {
                                          master_list_id: plantProxy?.master_list_id || null,
                                          plant_id: plantProxy?.plant_id || null,
                                          symbol: plantProxy?.symbol || null,
                                          symbol_size_in: plantProxy?.symbol_size_in ?? null,
                                          activePlanId: activePlanId || null
                                        },
                                        timestamp: Date.now()
                                      });
                                      // #endregion
                                      armPlacementWithSinglePlant(plantProxy);
                                    }}
                                  >
                                    Place
                                  </button>
                                </div>
                              </div>
                              <p className="text-[10px] text-gray-500 italic truncate mb-1.5">{item.botanical_name || (!resolvedItemPlantId ? 'Manual Entry' : '')}</p>
                              <div className="flex flex-wrap gap-1">
                                {placed > 0 && (
                                  <span
                                    className="px-1.5 py-0.5 bg-green-50 text-green-700 rounded text-[9px] font-bold border border-green-100"
                                    title="Placed on this drawing (sum of marker quantities)"
                                  >
                                    Placed {placed}
                                  </span>
                                )}
                                {item.pot_size && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[9px] font-bold border border-blue-100 uppercase">{item.pot_size}</span>}
                                {item.manual_plan_zone_id ? (
                                  <span
                                    className="px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded text-[9px] font-bold border border-slate-200 max-w-[140px] truncate inline-block align-bottom"
                                    title={`Garden App zone (from Project Plant List): ${manualGardenZoneName(item.manual_plan_zone_id)}`}
                                  >
                                    {manualGardenZoneName(item.manual_plan_zone_id)}
                                  </span>
                                ) : null}
                                {!resolvedItemPlantId && !(item.common_name || item.botanical_name) && (
                                  <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-[9px] font-bold border border-amber-100">
                                    Needs name
                                  </span>
                                )}
                                {item.image_url && !safeItemImg && (
                                  <span
                                    className="px-1.5 py-0.5 bg-red-50 text-red-700 rounded text-[9px] font-bold border border-red-100"
                                    title="Invalid image link (won't load)"
                                  >
                                    Bad image link
                                  </span>
                                )}
                                {!item.plant_id && !!item.image_url && (
                                  <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[9px] font-bold border border-purple-100">
                                    Manual
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
              </>
            </div>
          </div>
        ) : (
              /* Collapsed state - show button to expand */
              <div className="fixed right-0 top-1/2 transform -translate-y-1/2 z-30">
                <button
                  onClick={() => setShowPlantSidebar(true)}
                  className="bg-white p-3 rounded-l-xl shadow-lg border border-r-0 border-gray-200 hover:bg-gray-50 transition-all text-green-600 flex flex-col items-center gap-2"
                  title="Show Project Plant List"
                >
                  <FileText className="w-6 h-6" />
                  <span className="text-[10px] font-bold uppercase tracking-widest [writing-mode:vertical-lr]">Plants</span>
                </button>
              </div>
            )}
        </div>
      {/* Collaboration Sidebar - Notes, Messages, Schedule */}
      {showComments && (
        <div
          className="fixed right-0 top-0 h-full bg-white shadow-2xl z-40 flex flex-col overflow-x-hidden min-w-0"
          style={{ width: collabDrawerWidth }}
        >
          {/* Resize handle */}
          <div
            className="absolute left-0 top-0 h-full w-2 cursor-col-resize z-50 hover:bg-gray-200/60 active:bg-gray-300/60"
            title="Drag to resize"
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              collabResizeRef.current = { resizing: true, startX: e.clientX, startW: collabDrawerWidth };
              document.body.style.cursor = 'col-resize';
              document.body.style.userSelect = 'none';
            }}
          />
          <div className="p-4 border-b border-gray-200">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold">Project Collaboration</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleCollabDrawerWidth}
                  className="px-2 py-1 text-xs font-medium border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  title={collabDrawerWidth >= 520 ? 'Collapse panel' : 'Expand panel'}
                >
                  {collabDrawerWidth >= 520 ? 'Collapse' : 'Expand'}
                </button>
                <button
                  onClick={() => {
                    setShowComments(false);
                    setSelectedPlantForComment(null);
                    setSelectedPlacementId(null);
                    setShowControls(false);
                    setActiveTab('notes');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  title="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs text-gray-700">
                <span className="font-medium">Designer view.</span>{' '}
                Use <span className="font-medium">Client Chat</span> for homeowner plan feedback, and <span className="font-medium">Trades Chat</span> for trades/suppliers.
              </p>
            </div>
            
            {/* Tabs */}
            <div className="flex flex-wrap gap-2 border-b border-gray-200 -mx-4 px-4 pb-0 mb-3">
              <button
                onClick={() => {
                  setActiveTab('notes');
                  setSelectedPlantForComment(null);
                }}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'notes'
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                🗨️ Client Chat
              </button>
              <button
                onClick={() => setActiveTab('messages')}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'messages'
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                💬 Trades Chat
              </button>
              <button
                onClick={() => {
                  setActiveTab('collaborators');
                  if (invitedCollaborators.length === 0) loadInvitedCollaborators();
                }}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'collaborators'
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                👥 Collaborators
              </button>
              <button
                onClick={() => setActiveTab('schedule')}
                className={`flex-shrink-0 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'schedule'
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                📅 Schedule
              </button>
              <button
                onClick={() => setActiveTab('files')}
                className={`flex-shrink-0 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'files'
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                📁 Files
              </button>
            </div>
          </div>
            
          {/* Tab Content - Notes */}
          {activeTab === 'notes' && (
              <>
                {/* Description */}
                <div className="px-4 pb-2">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-600 italic flex-1">
                      Client Chat: homeowners and designers discuss the plan (plants + notes).
                    </p>
                  </div>
                </div>
                {/* Filter buttons */}
                {!selectedPlantForComment && (
                  <div className="flex gap-2 px-4 pb-3">
                    <button
                      onClick={() => setCommentFilter('all')}
                      className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                        commentFilter === 'all' 
                          ? 'bg-green-600 text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      All ({comments.length})
                    </button>
                    <button
                      onClick={() => setCommentFilter('general')}
                      className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                        commentFilter === 'general' 
                          ? 'bg-green-600 text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      General ({comments.filter(c => !c.placement_id).length})
                    </button>
                    <button
                      onClick={() => setCommentFilter('plants')}
                      className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                        commentFilter === 'plants' 
                          ? 'bg-purple-600 text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Plants ({comments.filter(c => c.placement_id).length})
                    </button>
                  </div>
                )}

                {/* Comments List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ overflowWrap: 'anywhere' }}>
            {loadingComments ? (
              <div className="text-center text-gray-500 py-8">Loading notes...</div>
            ) : (selectedPlantForComment ? getPlantComments(selectedPlantForComment.id) : filteredComments()).length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
                <p>No notes yet</p>
                <p className="text-sm mt-1">Add a note below to get started</p>
              </div>
            ) : (
              (selectedPlantForComment ? getPlantComments(selectedPlantForComment.id) : filteredComments()).map((comment) => {
                const plantInfo = comment.placement_id ? placements.find(p => p.id === comment.placement_id) : null;
                const noteInfo = comment.plan_note_id ? planNotes.find(n => n.id === comment.plan_note_id) : null;
                return (
                  <div 
                    key={comment.id} 
                    className={`rounded-lg p-3 ${
                      comment.placement_id
                        ? 'bg-purple-50 border border-purple-200 cursor-pointer hover:bg-purple-100'
                        : comment.plan_note_id
                          ? 'bg-yellow-50 border border-yellow-200 cursor-pointer hover:bg-yellow-100'
                          : 'bg-gray-50'
                    }`}
                    onClick={() => {
                      if (comment.placement_id && plantInfo) {
                        setSelectedPlacementId(comment.placement_id);
                        setShowControls(true);
                      } else if (comment.plan_note_id && noteInfo) {
                        setSelectedNoteForComment(noteInfo);
                        setSelectedPlantForComment(null);
                        setShowComments(true);
                        setActiveTab('notes');
                      }
                    }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-sm text-gray-900">{comment.user_name}</p>
                          {comment.comment_type && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              comment.comment_type === 'designer' ? 'bg-blue-100 text-blue-700' :
                              comment.comment_type === 'client' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-orange-100 text-orange-700'
                            }`}>
                              {comment.comment_type === 'designer' ? 'Designer' :
                               comment.comment_type === 'client' ? 'Client' : 
                               comment.trade_type ? comment.trade_type.charAt(0).toUpperCase() + comment.trade_type.slice(1) : 'Trade'}
                            </span>
                          )}
                        </div>
                        {plantInfo && !selectedPlantForComment && (
                          <p className="text-xs text-purple-600 font-medium flex items-center gap-1 mt-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                            {plantInfo.common_name}
                          </p>
                        )}
                        {noteInfo && !selectedPlantForComment && (
                          <p className="text-xs text-yellow-800 font-medium flex items-center gap-1 mt-1">
                            📝 Note
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(comment.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    {comment.comment_text && (
                      <p className="text-sm text-gray-700 whitespace-pre-wrap mb-2">{comment.comment_text}</p>
                    )}
                    {comment.attachments && Array.isArray(comment.attachments) && comment.attachments.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {comment.attachments.map((attachment, idx) => (
                          <div key={idx}>
                            {attachment.type && attachment.type.startsWith('image/') ? (
                              <img
                                src={attachment.url}
                                alt={attachment.filename || 'Attachment'}
                                className="max-w-full rounded-lg border border-gray-200 cursor-pointer hover:opacity-90"
                                onClick={() => window.open(attachment.url, '_blank')}
                              />
                            ) : (
                              <a
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                                {attachment.filename || 'Attachment'}
                                {attachment.size && ` (${formatFileSize(attachment.size)})`}
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
                )}
                </div>

                {/* Add Comment Form */}
                <div className={`p-4 border-t border-gray-200 ${
                  selectedPlantForComment ? 'bg-purple-50' : selectedNoteForComment ? 'bg-yellow-50' : ''
                }`}>
            {selectedPlantForComment && (
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-purple-700">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  <span className="font-medium">Commenting on: {selectedPlantForComment.common_name}</span>
                </div>
                <button
                  onClick={() => setSelectedPlantForComment(null)}
                  className="text-gray-400 hover:text-gray-600"
                  title="Switch to general comments"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            {selectedNoteForComment && !selectedPlantForComment && (
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-yellow-800">
                  <span className="font-medium">Commenting on: Note</span>
                </div>
                <button
                  onClick={() => setSelectedNoteForComment(null)}
                  className="text-gray-400 hover:text-gray-600"
                  title="Switch to general comments"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={
                selectedPlantForComment
                  ? `Add a note about ${selectedPlantForComment.common_name}...`
                  : selectedNoteForComment
                    ? 'Ask a question about this note...'
                    : 'Add a note about this project...'
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
              rows="3"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  handlePostComment(selectedPlantForComment?.id || null);
                }
              }}
            />
            
            {/* File Upload */}
            <div className="mt-2">
              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-gray-50 border border-gray-300 rounded-lg hover:bg-gray-100">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {uploadingAttachments ? 'Uploading...' : 'Add Images'}
                <input
                  type="file"
                  multiple
                  accept="image/*,image/heic,image/heif,.heic,.heif"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={uploadingAttachments}
                />
              </label>
            </div>

            {/* Attachments Preview */}
            {commentAttachments.length > 0 && (
              <div className="mt-2 space-y-2">
                {commentAttachments.map((attachment, idx) => (
                  <div key={idx} className="relative inline-block mr-2">
                    {attachment.type && attachment.type.startsWith('image/') ? (
                      <div className="relative">
                        <img
                          src={attachment.url}
                          alt={attachment.filename}
                          className="h-20 w-20 object-cover rounded border border-gray-200"
                        />
                        <button
                          onClick={() => removeAttachment(idx)}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm">
                        <span>{attachment.filename}</span>
                        <button
                          onClick={() => removeAttachment(idx)}
                          className="text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3 flex justify-between items-center">
              <span className="text-xs text-gray-500">Ctrl+Enter to post</span>
              <button
                onClick={() => handlePostComment(selectedPlantForComment?.id || null)}
                disabled={(!newComment.trim() && commentAttachments.length === 0) || uploadingAttachments}
                className={`px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium ${
                  selectedPlantForComment ? 'bg-purple-600' : selectedNoteForComment ? 'bg-yellow-600' : 'bg-green-600'
                }`}
              >
                Post {selectedPlantForComment ? 'Plant' : selectedNoteForComment ? 'Note' : 'Project'} Note
              </button>
            </div>
          </div>
        </>
      )}

          {/* Tab Content - Messages */}
          {activeTab === 'messages' && (
            <div className="flex-1 overflow-hidden">
              <ProjectMessages 
                projectId={id} 
                shareToken={null}
                onNavigateToCollaborators={() => {
                  setActiveTab('collaborators');
                  if (invitedCollaborators.length === 0) loadInvitedCollaborators();
                }}
              />
            </div>
          )}

          {activeTab === 'collaborators' && (
            <div className="flex-1 overflow-y-auto p-4">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold mb-1">Invited Collaborators</h2>
                  <p className="text-sm text-gray-600">
                    Manage who has access to this project. Invite clients and trades, copy their invite links, or remove access.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => {
                      setInviteTypeModal('client');
                      setShowInviteModal(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2 justify-center"
                    title="Invite a client (homeowner)"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Invite Client
                  </button>
                  <button
                    onClick={() => {
                      setInviteTypeModal('trade');
                      setShowInviteModal(true);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-2 justify-center"
                    title="Invite a trade or supplier"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Invite Trade
                  </button>
                </div>
              </div>
              {loadingCollaborators ? (
                <div className="text-center text-gray-500 py-8">Loading collaborators...</div>
              ) : invitedCollaborators.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p>No collaborators invited yet</p>
                  <p className="text-sm mt-1">Click the "Invite" button above to invite people</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-gray-600">{invitedCollaborators.length} collaborator{invitedCollaborators.length !== 1 ? 's' : ''} invited</span>
                  </div>
                  {invitedCollaborators.map((collab, idx) => {
                    const isTrade = collab.type === 'trade' || collab.trade_type || collab.share_type === 'trade';
                    const chatVisibilityLabel =
                      collab.message_visibility === 'none'
                        ? 'New messages only'
                        : collab.message_visibility === 'selected'
                          ? 'Selected conversations'
                          : 'All messages';
                    const scheduleLabel = collab.schedule_access === false ? 'No schedule access' : 'Schedule access';
                    return (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-900">{collab.recipient_name}</span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                !isTrade ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                              }`}>
                                {!isTrade ? 'Client' : collab.trade_type || 'Trade'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 truncate">{collab.recipient_email}</p>
                            {collab.phone_number && (
                              <p className="text-xs text-gray-600 mt-1">
                                📞 {collab.phone_number}
                                {collab.notification_method === 'sms' && (
                                  <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                                    SMS pref (coming soon)
                                  </span>
                                )}
                              </p>
                            )}
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className="text-xs bg-white border border-gray-200 text-gray-700 px-2 py-0.5 rounded-full">
                                💬 Chat: {chatVisibilityLabel}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full border ${
                                collab.schedule_access === false
                                  ? 'bg-white border-gray-200 text-gray-500'
                                  : 'bg-white border-gray-200 text-gray-700'
                              }`}>
                                📅 {scheduleLabel}
                              </span>
                            </div>
                            {collab.created_at && (
                              <p className="text-xs text-gray-500 mt-1">
                                Invited {new Date(collab.created_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => {
                                const shareToken = collab.shareToken;
                                const link = isTrade
                                  ? `${window.location.origin}/shared/${shareToken}/communication`
                                  : `${window.location.origin}/shared/${shareToken}`;
                                const projectName = project?.name || project?.project_name || 'this project';
                                const subject = encodeURIComponent(`Invitation to collaborate on ${projectName}`);
                                const body = encodeURIComponent(
                                  `Hi ${collab.recipient_name},\n\n` +
                                  `You have been invited to collaborate on the project "${projectName}".\n\n` +
                                  `Please click the link below to access the project:\n\n` +
                                  `${link}\n\n` +
                                  `Looking forward to working with you!`
                                );
                                const mailtoLink = `mailto:${collab.recipient_email}?subject=${subject}&body=${body}`;
                                window.location.href = mailtoLink;
                              }}
                              className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                              title="Resend invitation via email"
                            >
                              Resend Invite
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  const authToken = localStorage.getItem('authToken');
                                  if (!authToken) {
                                    alert('You must be logged in to remove collaborators');
                                    return;
                                  }
                                  const API_BASE_URL = process.env.REACT_APP_API_URL ||
                                    (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
                                  if (collab.id) {
                                    await fetch(`${API_BASE_URL}/collaboration/share/${collab.id}`, {
                                      method: 'DELETE',
                                      headers: { 'Authorization': `Bearer ${authToken}` }
                                    });
                                  }
                                  loadInvitedCollaborators();
                                  alert(`${collab.recipient_name} has been removed from the project.`);
                                } catch (error) {
                                  console.error('Error removing collaborator:', error);
                                  alert('Failed to remove collaborator. Please try again.');
                                }
                              }}
                              className="px-3 py-1.5 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
                              title="Remove collaborator"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            }
            </div>
          )}
          
          {/* Tab Content - Schedule */}
          {activeTab === 'schedule' && (
            <div className="flex-1 overflow-hidden">
              <ProjectSchedule projectId={id} shareToken={null} />
            </div>
          )}
          
          {activeTab === 'files' && (
            <div className="flex-1 overflow-y-auto p-4">
              <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs text-gray-700">
                  <span className="font-medium">Shared Files</span>: upload the plan PDF, plant list PDF, care sheets, invoices, etc.
                  <span className="text-gray-600"> Homeowners and trades can view/download.</span>
                </p>
              </div>
              <ProjectFiles projectId={id} canUpload={true} />
            </div>
          )}
        </div>
        )}

      {/* Edit Quantity Modal */}
      {editingPlacement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-80">
            <h3 className="text-lg font-semibold mb-4">Edit Quantity</h3>
            <p className="text-sm text-gray-600 mb-4">{editingPlacement.common_name}</p>
            <input
              type="number"
              min="1"
              defaultValue={editingPlacement.quantity}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleUpdateQuantity(editingPlacement.id, parseInt(e.target.value));
                }
              }}
              id="quantity-input"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  const qty = document.getElementById('quantity-input').value;
                  handleUpdateQuantity(editingPlacement.id, parseInt(qty));
                }}
                className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
              >
                Save
              </button>
              <button
                onClick={() => setEditingPlacement(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Touch drag ghost indicator (mobile) */}
      {touchDraggingPlant && touchDraggingPlant.hasMoved && touchDragPosition && (
        <div
          className="fixed pointer-events-none z-50"
          style={{
            left: touchDragPosition.x - 32,
            top: touchDragPosition.y - 32,
            width: '64px',
            height: '64px'
          }}
        >
          <div className="w-full h-full bg-green-500 rounded-full opacity-70 flex items-center justify-center shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>
        </div>
      )}

      {/* OCR Processing & Results Modal - Positioned on the right */}
      {showOCRModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 p-4 pointer-events-none">
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden ml-auto pointer-events-auto">
            {ocrPlaceConfirm ? (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/35 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-5 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm placement</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Match score is below {(OCR_MATCH_CONFIRM_THRESHOLD * 100).toFixed(0)}%. Place{' '}
                    <span className="font-medium text-gray-900">{ocrPlaceConfirm.match.common_name}</span> for label &quot;
                    {ocrPlaceConfirm.detection.text}&quot;?
                  </p>
                  {ocrPlaceConfirm.detection.inferred_zone_name ? (
                    <p className="text-xs text-slate-600 mb-4">
                      Zone from label position: <span className="font-medium">{ocrPlaceConfirm.detection.inferred_zone_name}</span>
                      {ocrPlaceConfirm.match.master_list_id
                        ? ' — project plant list row will be linked to this zone when you confirm.'
                        : ''}
                    </p>
                  ) : null}
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                      onClick={() => setOcrPlaceConfirm(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                      onClick={async () => {
                        const { detection, match } = ocrPlaceConfirm;
                        setOcrPlaceConfirm(null);
                        try {
                          await applyOcrDetectionPlacement(detection, match);
                          alert(`✅ "${match.common_name}" has been placed on the plan!`);
                        } catch (err) {
                          console.error('Apply detection error:', err);
                          alert(`❌ Failed to place plant: ${err.message}`);
                        }
                      }}
                    >
                      Place on plan
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
            {/* Header */}
            <div className="bg-purple-600 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <h2 className="text-xl font-bold">🪄 AI Plant Recognition</h2>
              </div>
              <button
                onClick={() => {
                  setOcrPlaceConfirm(null);
                  setShowOCRModal(false);
                  setOcrResults(null);
                  setOcrError('');
                }}
                className="text-white hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {ocrProcessing && (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mb-6"></div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Analyzing your plan...</h3>
                  <p className="text-gray-600 mb-4">🔍 Reading handwritten labels with AI</p>
                  <p className="text-sm text-gray-500">This may take 10-30 seconds</p>
                </div>
              )}

              {ocrError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                  <svg className="w-12 h-12 text-red-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-red-900 mb-2">Analysis Failed</h3>
                  <p className="text-red-700">{ocrError}</p>
                  <button
                    onClick={() => {
                      setOcrPlaceConfirm(null);
                      setShowOCRModal(false);
                    }}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Close
                  </button>
                </div>
              )}

              {ocrResults && !ocrProcessing && (
                <div>
                  {/* Orientation info */}
                  {rotation !== 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-blue-900 mb-1">
                            Analyzing rotated image ({rotation}°)
                          </p>
                          <p className="text-xs text-blue-800">
                            The AI will analyze the image as you see it (rotated {rotation}°). Detected plant positions will match your current view.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Show Gemini's notes if available */}
                  {ocrResults.gemini_notes && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-blue-900">
                        <strong>AI Notes:</strong> {ocrResults.gemini_notes}
                      </p>
                    </div>
                  )}

                  {ocrResults.recognize_options && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 text-xs text-slate-700 space-y-1">
                      <p className="font-medium text-slate-800">Detection context</p>
                      <p>
                        Project list: {ocrResults.recognize_options.project_list_size ?? 0} plants
                        {ocrResults.recognize_options.use_project_list_only ? ' · matches restricted to list' : ''}
                        {ocrResults.recognize_options.use_project_list_context === false ? ' · list not sent to AI' : ''}
                      </p>
                      {activePlanId ? (
                        <p>
                          Zones on this drawing used for location hints: {ocrResults.recognize_options.zones_loaded ?? 0}
                        </p>
                      ) : (
                        <p className="text-amber-800">
                          No active drawing selected — zone hints were not applied. Select a drawing and run auto-detect again for zone labels.
                        </p>
                      )}
                    </div>
                  )}

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <svg className="w-6 h-6 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <h3 className="font-semibold text-green-900">
                            Found {ocrResults.detected_plants?.length || 0} plant labels
                          </h3>
                          <p className="text-sm text-green-700">
                            {(() => {
                              const matched = ocrResults.detected_plants?.filter(d => d.matched_plants && d.matched_plants.length > 0).length || 0;
                              const unmatched = (ocrResults.detected_plants?.length || 0) - matched;
                              return `${matched} matched, ${unmatched} unmatched`;
                            })()}
                          </p>
                        </div>
                      </div>
                      
                      {/* Filter Controls */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700 mr-1">Show:</span>
                        <button
                          onClick={() => setOcrPlotFilter('all')}
                          className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                            ocrPlotFilter === 'all' 
                              ? 'bg-purple-600 text-white' 
                              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          }`}
                        >
                          All
                        </button>
                        <button
                          onClick={() => setOcrPlotFilter('matched')}
                          className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                            ocrPlotFilter === 'matched' 
                              ? 'bg-green-600 text-white' 
                              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          }`}
                        >
                          Matched Only
                        </button>
                        <button
                          onClick={() => setOcrPlotFilter('unmatched')}
                          className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                            ocrPlotFilter === 'unmatched' 
                              ? 'bg-orange-600 text-white' 
                              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          }`}
                        >
                          Unmatched Only
                        </button>
                      </div>
                    </div>
                  </div>

                  {ocrResults.detected_plants && ocrResults.detected_plants.length > 0 ? (
                    (() => {
                      const filteredPlants = ocrResults.detected_plants.filter(detection => {
                        if (ocrPlotFilter === 'matched') {
                          return detection.matched_plants && detection.matched_plants.length > 0;
                        } else if (ocrPlotFilter === 'unmatched') {
                          return !detection.matched_plants || detection.matched_plants.length === 0;
                        }
                        return true; // 'all'
                      });
                      
                      if (filteredPlants.length === 0) {
                        return (
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                            <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                            <p className="text-gray-600 font-medium">No {ocrPlotFilter} plants</p>
                            <p className="text-sm text-gray-500 mt-1">Try a different filter option</p>
                          </div>
                        );
                      }
                      
                      return (
                        <div className="space-y-4">
                          {filteredPlants.map((detection, idx) => (
                        <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono text-sm text-gray-600">
                                  Position: {detection.x?.toFixed(1)}%, {detection.y?.toFixed(1)}%
                                </span>
                                <span className={`px-2 py-0.5 text-xs rounded-full ${
                                  detection.confidence > 0.8 ? 'bg-green-100 text-green-800' :
                                  detection.confidence > 0.6 ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-orange-100 text-orange-800'
                                }`}>
                                  {(detection.confidence * 100).toFixed(0)}% confident
                                </span>
                              </div>
                              <p className="text-lg font-semibold text-gray-900 mb-2">
                                "{detection.text}"
                              </p>
                              {detection.inferred_zone_name ? (
                                <p className="text-xs text-slate-600 mb-1">
                                  <span className="font-medium text-slate-700">Zone (by label position):</span>{' '}
                                  {detection.inferred_zone_name}
                                </p>
                              ) : activePlanId && (ocrResults.recognize_options?.zones_loaded ?? 0) > 0 ? (
                                <p className="text-xs text-amber-800 mb-1">Label center not inside any drawn zone</p>
                              ) : null}
                            </div>
                          </div>

                          {Array.isArray(detection.matched_master_list_items) &&
                            detection.matched_master_list_items.some((it) => !it.plant_id) && (
                              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                Some project-list matches have no linked database plant — link a plant on the Project Plant
                                List to place those on the plan.
                              </div>
                            )}

                          {detection.matched_plants && detection.matched_plants.length > 0 ? (
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-2">
                                Top {detection.matched_plants.length} matches:
                              </p>
                              <div className="space-y-2">
                                {detection.matched_plants.map((match, matchIdx) => (
                                  <div
                                    key={matchIdx}
                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-purple-50 transition-colors"
                                  >
                                    <div className="flex-1">
                                      <p className="font-medium text-gray-900">{match.common_name}</p>
                                      <p className="text-sm text-gray-600 italic">{match.botanical_name}</p>
                                      {match.master_list_id ? (
                                        <p className="text-xs text-purple-700 mt-0.5">On project plant list</p>
                                      ) : null}
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span className="text-sm text-gray-500">
                                        {(match.match_score * 100).toFixed(0)}% match
                                      </span>
                                      <button
                                        className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
                                        title="Place this plant on the plan"
                                        onClick={async () => {
                                          try {
                                            const score = Number(match.match_score);
                                            const needsConfirm =
                                              Number.isFinite(score) && score < OCR_MATCH_CONFIRM_THRESHOLD;
                                            if (needsConfirm) {
                                              setOcrPlaceConfirm({ detection, match });
                                              return;
                                            }
                                            await applyOcrDetectionPlacement(detection, match);
                                            alert(`✅ "${match.common_name}" has been placed on the plan!`);
                                          } catch (err) {
                                            console.error('Apply detection error:', err);
                                            alert(`❌ Failed to place plant: ${err.message}`);
                                          }
                                        }}
                                      >
                                        Place on Plan
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                              <p className="text-sm text-yellow-800">
                                ⚠️ No matching plants found. Try searching manually: "{detection.text}"
                              </p>
                            </div>
                          )}
                        </div>
                          ))}
                        </div>
                      );
                    })()
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                      <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-gray-600">No plant labels detected in this image</p>
                      <p className="text-sm text-gray-500 mt-2">Make sure labels are clearly visible</p>
                    </div>
                  )}

                  <div className="mt-6 flex gap-3 justify-end">
                    <button
                      onClick={() => {
                        setOcrPlaceConfirm(null);
                        setShowOCRModal(false);
                        setOcrResults(null);
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Close
                    </button>
                    <button
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('authToken');
                          // Filter detections based on current filter setting
                          const filteredDetections = ocrResults.detected_plants.filter(d => {
                            if (ocrPlotFilter === 'matched') {
                              return d.matched_plants && d.matched_plants.length > 0;
                            } else if (ocrPlotFilter === 'unmatched') {
                              return !d.matched_plants || d.matched_plants.length === 0;
                            }
                            return true; // 'all'
                          });
                          
                          if (filteredDetections.length === 0) {
                            alert('No plants to add with current filter!');
                            return;
                          }
                          
                          // Build detections payload: use best match id if available
                          // Transform coordinates from rotated image space to original image space
                          const payload = {
                            detections: filteredDetections.map(d => {
                              const transformedCoords = transformCoordinates(d.x, d.y, rotation);
                              const top = d.matched_plants && d.matched_plants[0];
                              return {
                                text: d.text,
                                x: transformedCoords.x,
                                y: transformedCoords.y,
                                confidence: d.confidence,
                                chosen_plant_id: (top && top.id) || null,
                                chosen_master_list_id: (top && top.master_list_id) || undefined,
                                quantity: 1
                              };
                            })
                          };

                          // This button now just creates master list (plants should already be on plan)
                          // The "Apply All Plants" button handles saving to plan AND master list
                          const resp = await fetch(`/api/plans/${id}/create-master-list`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify(payload)
                          });

                          const data = await resp.json();
                          if (!resp.ok) throw new Error(data.error || 'Could not add plants to Project Plant List');

                          setShowOCRModal(false);
                          setOcrResults(null);
                          
                          // Show success/failure breakdown
                          const skippedOnly = data.skipped_existing_count || 0;
                          const skipOnlyPart =
                            skippedOnly > 0
                              ? `\n${skippedOnly} row(s) already on the project list (not duplicated).`
                              : '';
                          const successMsg = `✅ Added ${data.created_count} plants to this project's Project Plant List!${skipOnlyPart}`;
                          const failureMsg = data.failed_count > 0 
                            ? `\n⚠️ ${data.failed_count} plants couldn't be matched to the database.\n\nYou can add them manually on the Project Plant List, or pick from your Master Plant List.`
                            : '';
                          
                          if (window.confirm(`${successMsg}${failureMsg}\n\nOpen Project Plant List now?`)) {
                            navigate(`/plan/${id}/master-list`);
                          }
                        } catch (err) {
                          console.error('Create project plant list error:', err);
                          alert('Failed to add plants to Project Plant List');
                        }
                      }}
                    >
                      📋 Add to Project Plant List only
                      {ocrPlotFilter !== 'all' && ` (${ocrPlotFilter === 'matched' ? 'Matched' : 'Unmatched'} Only)`}
                    </button>
                    <button
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('authToken');
                          // Filter detections based on current filter setting
                          const filteredDetections = ocrResults.detected_plants.filter(d => {
                            if (ocrPlotFilter === 'matched') {
                              return d.matched_plants && d.matched_plants.length > 0;
                            } else if (ocrPlotFilter === 'unmatched') {
                              return !d.matched_plants || d.matched_plants.length === 0;
                            }
                            return true; // 'all'
                          });
                          
                          if (filteredDetections.length === 0) {
                            alert('No plants to plot with current filter!');
                            return;
                          }

                          const applyRows = [];
                          let skippedLowScore = 0;
                          for (const d of filteredDetections) {
                            const transformedCoords = transformCoordinates(d.x, d.y, rotation);
                            const top = d.matched_plants && d.matched_plants[0];
                            if (!top || !top.id) continue;
                            const s = Number(top.match_score);
                            if (Number.isFinite(s) && s < OCR_MATCH_CONFIRM_THRESHOLD) {
                              skippedLowScore += 1;
                              continue;
                            }
                            applyRows.push({
                              text: d.text,
                              x: transformedCoords.x,
                              y: transformedCoords.y,
                              confidence: d.confidence,
                              chosen_plant_id: top.id,
                              chosen_master_list_id: top.master_list_id || undefined,
                              inferred_zone_id: d.inferred_zone_id || undefined,
                              quantity: 1,
                            });
                          }

                          if (applyRows.length === 0) {
                            alert(
                              skippedLowScore > 0
                                ? 'No plants meet the 95% match threshold. Place each plant individually to confirm lower-confidence matches.'
                                : 'No plants with a valid top match to place.'
                            );
                            return;
                          }

                          const payload = {
                            plan_id: activePlanId || null,
                            detections: applyRows,
                          };

                          const resp = await fetch(`/api/plans/${id}/apply-detections`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify(payload)
                          });

                          const data = await resp.json();
                          if (!resp.ok) throw new Error(data.error || 'Apply all failed');

                          await loadPlacements();
                          if (Array.isArray(data.manual_zone_updates) && data.manual_zone_updates.length > 0) {
                            await loadMasterList();
                          }

                          // Then add to master list
                          try {
                            const masterListResp = await fetch(`/api/plans/${id}/create-master-list`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                              },
                              body: JSON.stringify(payload)
                            });
                            
                            const masterListData = await masterListResp.json();
                            const skippedM = masterListData.skipped_existing_count || 0;
                            const skipPart =
                              skippedM > 0
                                ? ` (${skippedM} already on project list — not duplicated.)`
                                : '';
                            const masterListMsg = masterListResp.ok
                              ? `\n\n✅ Added ${masterListData.created_count || 0} plants to Project Plant List.${skipPart}`
                              : '\n\n⚠️ Could not add to Project Plant List (you can add manually).';
                            
                            const count = data.created_count || data.placements?.length || 0;
                            const lowPart =
                              skippedLowScore > 0
                                ? `\n\n${skippedLowScore} plant(s) skipped (match below ${(OCR_MATCH_CONFIRM_THRESHOLD * 100).toFixed(0)}% — place those individually).`
                                : '';
                            alert(`✅ Successfully placed ${count} plant${count !== 1 ? 's' : ''} on the plan!${lowPart}${masterListMsg}`);
                          } catch (masterListErr) {
                            console.error('Error adding to project plant list:', masterListErr);
                            const count = data.created_count || data.placements?.length || 0;
                            const lowPart =
                              skippedLowScore > 0
                                ? `\n\n${skippedLowScore} plant(s) skipped (match below ${(OCR_MATCH_CONFIRM_THRESHOLD * 100).toFixed(0)}% — place those individually).`
                                : '';
                            alert(`✅ Successfully placed ${count} plant${count !== 1 ? 's' : ''} on the plan!${lowPart}\n\n⚠️ Could not add to Project Plant List (you can add manually).`);
                          }

                          // Close modal and reset
                          setOcrPlaceConfirm(null);
                          setShowOCRModal(false);
                          setOcrResults(null);
                        } catch (err) {
                          console.error('Apply all error:', err);
                          alert(`❌ Failed to apply detections: ${err.message}`);
                        }
                      }}
                    >
                      ✅ Apply all to plan & Project Plant List
                      {ocrPlotFilter !== 'all' && ` (${ocrPlotFilter === 'matched' ? 'Matched' : 'Unmatched'} Only)`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showPlantCard && selectedPlantCard && (() => {
        // Convert placement to plant format for PlantDetailModal
        const plantForModal = selectedPlantCard.plant_id ? selectedPlantCard : {
          ...selectedPlantCard,
          id: selectedPlantCard.plant_id || selectedPlantCard.id,
          is_public: false, // Placements are always private
          created_by_designer_id: project?.designer_id || null,
        };
        
        // Get current user info
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const isOwner = plantForModal.created_by_designer_id === currentUser.id;
        const isDesigner = currentUser.role === 'designer' || currentUser.role === 'admin';
        
        // Handle setting placement image
        const handleSetPlacementImage = async (imageUrl) => {
          if (!currentPlacementId) return;
          
          try {
            const token = localStorage.getItem('authToken');
            const API_BASE_URL = process.env.REACT_APP_API_URL || 
              (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
            
            // Update the placement's image_url
            const response = await fetch(`${API_BASE_URL}/plant-placements/${currentPlacementId}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                image_url: imageUrl
              })
            });
            
            if (!response.ok) {
              throw new Error('Failed to update placement image');
            }
            
            // Update local state
            setPlacements(prev => prev.map(p => 
              p.id === currentPlacementId ? { ...p, image_url: imageUrl } : p
            ));
            
            // Update selectedPlantCard if it's the same placement
            if (selectedPlantCard.id === currentPlacementId) {
              setSelectedPlantCard(prev => ({ ...prev, image_url: imageUrl }));
            }
            
            alert('Image updated successfully!');
          } catch (error) {
            console.error('Error updating placement image:', error);
            alert('Failed to update image. Please try again.');
          }
        };
        
        return (
          <PlantDetailModal
            plant={plantForModal}
            onClose={() => {
              setShowPlantCard(false);
              setSelectedPlantCard(null);
              setEditingPlantCard(false);
              setPlantCardEdits({});
              setCurrentPlacementId(null);
            }}
            // For placements, we use the two explicit edit actions below.
            onEdit={null}
            onDelete={null} // Don't allow deleting from placement view
            isOwner={isOwner}
            isDesigner={isDesigner}
            onAILookup={null} // Could implement later
            onShareToCommunity={null} // Could implement later
            onLike={null} // Could implement later
            isLiked={false}
            isFromPublicLibrary={false}
            onRemoveFromCollection={null}
            isFromPlacement={true} // Indicate this is from a placement
            onSetPlacementImage={handleSetPlacementImage}
            onEditProjectItem={() => {
              const masterListId = selectedPlantCard?.master_list_id || selectedPlantCard?.masterListId || null;
              setShowPlantCard(false);
              setSelectedPlantCard(null);
              if (masterListId) {
                navigate(`/plan/${id}/master-list?editItem=${encodeURIComponent(masterListId)}`);
              } else {
                // Fallback: open project list (user can search/edit manually)
                navigate(`/plan/${id}/master-list`);
              }
            }}
            onEditLibraryPlant={() => {
              setShowPlantCard(false);
              setSelectedPlantCard(null);
              if (selectedPlantCard?.plant_id) {
                navigate(`/plants?plantId=${encodeURIComponent(selectedPlantCard.plant_id)}`);
                return;
              }
              const name = selectedPlantCard?.common_name || selectedPlantCard?.manual_common_name || '';
              if (name) {
                navigate(`/master-plant-list?search=${encodeURIComponent(name)}`);
              } else {
                navigate('/master-plant-list');
              }
            }}
          />
        );
      })()}
      
      {/* Old modal code - keeping for reference but disabled */}
      {false && selectedPlantCard && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setShowPlantCard(false);
            setSelectedPlantCard(null);
          }}
        >
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Plant Image Hero */}
            <div className="relative h-64 bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
              {coerceImageUrl(editingPlantCard ? (plantCardEdits.image_url || selectedPlantCard.image_url) : selectedPlantCard.image_url) ? (
                <img
                  src={coerceImageUrl(editingPlantCard ? (plantCardEdits.image_url || selectedPlantCard.image_url) : selectedPlantCard.image_url)}
                  alt={selectedPlantCard.common_name}
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <svg className="w-24 h-24 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
              )}
              
              {/* Update Image Button (Edit Mode) */}
              {editingPlantCard && (
                <div className="absolute bottom-4 left-4 right-4 bg-white bg-opacity-95 rounded-lg p-3 shadow-lg">
                  <div className="text-xs text-gray-600 mb-2">Update Plant Image</div>
                  <input
                    type="url"
                    value={plantCardEdits.image_url || selectedPlantCard.image_url || ''}
                    onChange={(e) => setPlantCardEdits(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="Enter image URL..."
                    className="w-full px-2 py-1 text-sm border rounded"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    💡 Tip: Right-click an image online → "Copy image address"
                  </div>
                </div>
              )}
              
              {/* Close Button */}
              <button
                onClick={() => {
                  setShowPlantCard(false);
                  setSelectedPlantCard(null);
                }}
                className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              {/* Quantity Badge */}
              {selectedPlantCard.quantity > 1 && (
                <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-2 rounded-full font-bold shadow-lg">
                  Qty: {selectedPlantCard.quantity}
                </div>
              )}
            </div>
            
            {/* Plant Details */}
            <div className="p-6">
              {/* Edit/Save Buttons */}
              <div className="flex justify-end gap-2 mb-4">
                {!editingPlantCard ? (
                  <button
                    onClick={() => {
                      setEditingPlantCard(true);
                      setPlantCardEdits({
                        quantity: selectedPlantCard.quantity,
                        notes: selectedPlantCard.notes || '',
                        image_url: selectedPlantCard.image_url || ''
                      });
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    ✏️ Edit
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setEditingPlantCard(false);
                        setPlantCardEdits({});
                      }}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={savePlantCardEdits}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      💾 Save Changes
                    </button>
                  </>
                )}
              </div>
              
              {/* Names */}
              <div className="mb-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      {selectedPlantCard.common_name || 'Unknown Plant'}
                    </h2>
                    {selectedPlantCard.botanical_name && (
                      <p className="text-xl italic text-gray-600">
                        {selectedPlantCard.botanical_name}
                      </p>
                    )}
                  </div>
                  {!swappingPlant && (
                    <button
                      onClick={() => setSwappingPlant(true)}
                      className="px-3 py-1 text-sm bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors"
                      title="Wrong plant? Search and swap"
                    >
                      🔄 Change Plant
                    </button>
                  )}
                </div>
                
                {/* Plant Swap Search */}
                {swappingPlant && (
                  <div className="mt-4 p-4 bg-yellow-50 rounded-lg border-2 border-yellow-300">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-yellow-700 font-semibold">🔍 Search for correct plant:</span>
                      <button
                        onClick={() => {
                          setSwappingPlant(false);
                          setPlantSearchTerm('');
                          setPlantSearchResults([]);
                        }}
                        className="ml-auto text-sm text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                    </div>
                    <input
                      type="text"
                      value={plantSearchTerm}
                      onChange={(e) => {
                        setPlantSearchTerm(e.target.value);
                        searchPlantsForSwap(e.target.value);
                      }}
                      placeholder="Search by common or botanical name..."
                      className="w-full px-3 py-2 border rounded-lg mb-2"
                      autoFocus
                    />
                    {plantSearchResults.length > 0 && (
                      <div className="max-h-60 overflow-y-auto border rounded-lg bg-white">
                        {plantSearchResults.map(plant => (
                          <div
                            key={plant.id}
                            onClick={() => {
                              if (window.confirm(`Replace with ${plant.common_name}?`)) {
                                swapPlantInPlacement(plant);
                              }
                            }}
                            className="p-3 hover:bg-green-50 cursor-pointer border-b flex items-center gap-3"
                          >
                            {coerceImageUrl(plant.image_url) && (
                              <img
                                src={coerceImageUrl(plant.image_url)}
                                alt={plant.common_name}
                                className="w-12 h-12 rounded object-cover"
                              />
                            )}
                            <div>
                              <div className="font-semibold">{plant.common_name}</div>
                              <div className="text-sm italic text-gray-600">{plant.botanical_name}</div>
                              {plant.category && (
                                <div className="text-xs text-gray-500 capitalize">{plant.category.replace(/_/g, ' ')}</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Quantity - Editable */}
              {editingPlantCard ? (
                <div className="mb-6 bg-blue-50 rounded-lg p-4">
                  <label className="block text-sm text-gray-600 mb-2">Quantity</label>
                  <input
                    type="number"
                    min="1"
                    value={plantCardEdits.quantity || 1}
                    onChange={(e) => setPlantCardEdits(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              ) : selectedPlantCard.quantity > 1 && (
                <div className="mb-6 bg-blue-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Quantity</div>
                  <div className="font-semibold text-gray-900 text-2xl">
                    {selectedPlantCard.quantity}
                  </div>
                </div>
              )}
              
              {/* Quick Info Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {selectedPlantCard.category && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">Category</div>
                    <div className="font-semibold text-gray-900 capitalize">
                      {selectedPlantCard.category.replace(/_/g, ' ')}
                    </div>
                  </div>
                )}
                
                {selectedPlantCard.size && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">Size</div>
                    <div className="font-semibold text-gray-900 capitalize">
                      {selectedPlantCard.size}
                    </div>
                  </div>
                )}
                
                {selectedPlantCard.auto_detected && (
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">Detected By</div>
                    <div className="font-semibold text-gray-900">
                      AI OCR
                    </div>
                  </div>
                )}
                
                {selectedPlantCard.detection_confidence && (
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">Confidence</div>
                    <div className="font-semibold text-gray-900">
                      {Math.round(selectedPlantCard.detection_confidence * 100)}%
                    </div>
                  </div>
                )}
              </div>
              
              {/* Original Text */}
              {selectedPlantCard.original_text && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="text-sm text-gray-600 mb-1">Original Text from Plan</div>
                  <div className="font-mono text-sm text-gray-900">
                    "{selectedPlantCard.original_text}"
                  </div>
                </div>
              )}
              
              {/* Additional Images */}
              {selectedPlantCard.plant_data && selectedPlantCard.plant_data.images && Array.isArray(selectedPlantCard.plant_data.images) && selectedPlantCard.plant_data.images.length > 0 && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">Additional Images</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedPlantCard.plant_data.images.map((imgUrl, idx) => (
                      <a
                        key={idx}
                        href={imgUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm"
                      >
                        🖼️ Image {idx + 1}
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Plant Data (extended details) */}
              {selectedPlantCard.plant_data && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">Additional Details</h3>
                  <div className="space-y-3">
                    {Object.entries(selectedPlantCard.plant_data).map(([key, value]) => {
                      if (!value || key === 'image' || key === 'images') return null;
                      return (
                        <div key={key} className="flex border-b pb-2">
                          <div className="text-sm text-gray-600 w-1/3 capitalize">
                            {key.replace(/_/g, ' ')}:
                          </div>
                          <div className="text-sm text-gray-900 w-2/3">
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Notes - Editable */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold mb-2 text-gray-900">Notes</h3>
                {editingPlantCard ? (
                  <textarea
                    value={plantCardEdits.notes || ''}
                    onChange={(e) => setPlantCardEdits(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Add notes about this plant placement..."
                    className="w-full px-3 py-2 border rounded-lg min-h-[100px]"
                  />
                ) : (
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {selectedPlantCard.notes || 'No notes yet'}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plan Link Modal */}
      {showPlanLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Drawing</h3>
              <button
                onClick={() => {
                  setShowPlanLinkModal(false);
                  setPlanLinkInput('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title (optional)
                </label>
                <input
                  type="text"
                  value={newPlanTitle}
                  onChange={(e) => setNewPlanTitle(e.target.value)}
                  placeholder="e.g. Concept v1, Working drawing, Planting plan"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={newPlanCategory}
                  onChange={(e) => setNewPlanCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="concept">Concept</option>
                  <option value="working">Working drawing</option>
                  <option value="planting">Planting plan</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags (optional)
                </label>
                <input
                  type="text"
                  value={newPlanTagsInput}
                  onChange={(e) => setNewPlanTagsInput(e.target.value)}
                  placeholder="comma, separated, tags"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plan Link *
              </label>
              <input
                type="url"
                value={planLinkInput}
                onChange={(e) => setPlanLinkInput(e.target.value)}
                placeholder="https://example.com/plan.pdf or https://drive.google.com/..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter a URL to your plan (PDF, image, or shared document link)
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={async () => {
                  if (!planLinkInput.trim()) {
                    alert('Please enter a plan link');
                    return;
                  }

                  // Validate URL format
                  try {
                    new URL(planLinkInput.trim());
                  } catch (e) {
                    alert('Please enter a valid URL');
                    return;
                  }

                  try {
                    setCreatingPlan(true);
                    const sourcePlanId = activePlanId;
                    const sourceNotesCount = planNotes.length;
                    const sourcePlantsCount = placements.length;

                    const formData = new FormData();
                    formData.append('planLink', planLinkInput.trim());
                    formData.append('title', newPlanTitle.trim());
                    formData.append('category', newPlanCategory);
                    const tags = newPlanTagsInput
                      .split(',')
                      .map((t) => t.trim())
                      .filter(Boolean);
                    formData.append('tags', JSON.stringify(tags));

                    const resp = await projectPlansAPI.create(id, formData);
                    
                    setShowPlanLinkModal(false);
                    setPlanLinkInput('');
                    setNewPlanTitle('');
                    setNewPlanTagsInput('');
                    setNewPlanCategory('concept');
                    await loadProjectPlans();
                    if (resp?.plan?.id) {
                      setActivePlanId(resp.plan.id);
                      maybeOfferTransferToNewPlan({
                        sourcePlanId,
                        targetPlanId: resp.plan.id,
                        sourceNotesCount,
                        sourcePlantsCount
                      });
                    }
                  } catch (error) {
                    console.error('Error saving plan link:', error);
                    alert(`Failed to save plan link: ${error.message}`);
                  } finally {
                    setCreatingPlan(false);
                  }
                }}
                disabled={creatingPlan}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {creatingPlan ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => {
                  setShowPlanLinkModal(false);
                  setPlanLinkInput('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Optional Transfer Modal (copy from current drawing) */}
      {showPlanTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Copy from current drawing?</h3>
              <button
                onClick={() => setShowPlanTransferModal(false)}
                disabled={transferringPlanContent}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                title="Close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Your new drawing starts blank. You can optionally copy notes and/or plants from the previous drawing.
              This will <span className="font-semibold">duplicate</span> items (it won’t remove anything from the original).
            </p>

            <div className="space-y-3">
              <label className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={transferCopyNotes}
                  disabled={transferringPlanContent || transferSourceNotesCount <= 0}
                  onChange={(e) => setTransferCopyNotes(e.target.checked)}
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    Copy notes <span className="text-gray-500 font-normal">({transferSourceNotesCount})</span>
                  </div>
                  <div className="text-xs text-gray-600">Copies sticky notes/annotations and their positions.</div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={transferCopyPlants}
                  disabled={transferringPlanContent || transferSourcePlantsCount <= 0}
                  onChange={(e) => setTransferCopyPlants(e.target.checked)}
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    Copy plants <span className="text-gray-500 font-normal">({transferSourcePlantsCount})</span>
                  </div>
                  <div className="text-xs text-gray-600">Copies placed plant markers and their positions.</div>
                </div>
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setShowPlanTransferModal(false)}
                disabled={transferringPlanContent}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-60"
              >
                Keep blank
              </button>
              <button
                onClick={runTransferToNewPlan}
                disabled={transferringPlanContent || (!transferCopyNotes && !transferCopyPlants)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {transferringPlanContent ? 'Copying…' : 'Copy selected'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add From Master Plant List (Designer Library) Modal */}
      {showAddFromMasterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl p-6 overflow-y-auto max-h-[95vh]">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-xl font-semibold">Add from Master Plant List</h3>
                <p className="text-sm text-gray-600">
                  Select plants from your Master Plant List (all projects) to copy onto this project&apos;s Project Plant List. Quantities default to 1; you can edit after.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate('/master-plant-list')}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  title="Open your full Master Plant List (catalog from all projects)"
                >
                  Open library
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddFromMasterModal(false);
                    setLibrarySearch('');
                    setLibraryItems([]);
                    setLibraryTypeFilter('');
                    setLibraryExposureFilter('');
                    setSelectedLibraryItems(new Set());
                  }}
                  className="text-gray-500 hover:text-gray-800"
                  title="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 items-center mb-3">
              <input
                type="text"
                value={librarySearch}
                onChange={(e) => setLibrarySearch(e.target.value)}
                placeholder="Search your library…"
                className="flex-1 min-w-[240px] px-3 py-2 border rounded-lg"
                autoFocus
              />
              <select
                value={libraryTypeFilter}
                onChange={(e) => setLibraryTypeFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg bg-white text-sm"
                title="Filter library by Type"
              >
                <option value="">All Types</option>
                <option value="__missing__">Missing Type</option>
                {availableLibraryTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <select
                value={libraryExposureFilter}
                onChange={(e) => setLibraryExposureFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg bg-white text-sm"
                title="Filter library by Exposure"
              >
                <option value="">All Exposures</option>
                <option value="__missing__">Missing Exposure</option>
                {availableLibraryExposures.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => {
                  setLibrarySearch('');
                  setLibraryTypeFilter('');
                  setLibraryExposureFilter('');
                  fetchDesignerLibrary('');
                }}
                className="px-3 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setSelectedLibraryItems(new Set(visibleLibraryItems.map((i) => i.id)))}
                className="px-3 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                disabled={visibleLibraryItems.length === 0}
              >
                Select all
              </button>
              <button
                type="button"
                onClick={() => setSelectedLibraryItems(new Set())}
                className="px-3 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                disabled={selectedLibraryItems.size === 0}
              >
                Clear selection
              </button>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-[55vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2" style={{ width: '40px' }} />
                      <th className="text-left p-2">Common</th>
                      <th className="text-left p-2">Botanical</th>
                      <th className="text-left p-2">Size</th>
                      <th className="text-left p-2">Code</th>
                      <th className="text-left p-2">Image</th>
                    </tr>
                  </thead>
                  <tbody>
                    {libraryLoading ? (
                      <tr>
                        <td colSpan={6} className="p-4 text-gray-600">Loading…</td>
                      </tr>
                    ) : visibleLibraryItems.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-4 text-gray-600">No items found.</td>
                      </tr>
                    ) : (
                      visibleLibraryItems.map((li) => (
                        <tr key={li.id} className="border-t hover:bg-gray-50">
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={selectedLibraryItems.has(li.id)}
                              onChange={() => toggleSelectLibraryItem(li.id)}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                          <td className="p-2 font-semibold">{li.common_name || <span className="text-red-600">(missing)</span>}</td>
                          <td className="p-2 italic text-gray-700">{li.botanical_name || '-'}</td>
                          <td className="p-2">{li.pot_size || '-'}</td>
                          <td className="p-2">{li.code || '-'}</td>
                          <td className="p-2">
                            {li.image_url ? (
                              <a href={li.image_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                                Link
                              </a>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-600">
                Selected: {selectedLibraryItems.size}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddFromMasterModal(false);
                    setLibrarySearch('');
                    setLibraryItems([]);
                    setSelectedLibraryItems(new Set());
                  }}
                  className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={addSelectedFromLibrary}
                  disabled={selectedLibraryItems.size === 0 || addingFromLibrary}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {addingFromLibrary ? 'Adding…' : 'Add to Project'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Project Settings Modal */}
      {showProjectSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Project Settings</h3>
              <button
                onClick={() => {
                  setShowProjectSettings(false);
                  setEditingProjectName('');
                  setSheetDraft(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Project Title Section */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Title
              </label>
              <input
                type="text"
                value={editingProjectName || project?.name || ''}
                onChange={(e) => setEditingProjectName(e.target.value)}
                placeholder="Enter project title"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={async () => {
                    if (!editingProjectName || editingProjectName.trim() === '') {
                      alert('Project title cannot be empty');
                      return;
                    }
                    try {
                      const token = localStorage.getItem('authToken');
                      const API_BASE_URL = process.env.REACT_APP_API_URL || 
                        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
                      
                      const response = await fetch(`${API_BASE_URL}/projects/${id}`, {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ name: editingProjectName.trim() })
                      });
                      
                      if (!response.ok) throw new Error('Failed to update project name');
                      
                      setProject({ ...project, name: editingProjectName.trim() });
                      alert('Project title updated successfully');
                    } catch (error) {
                      console.error('Error updating project name:', error);
                      alert('Failed to update project title');
                    }
                  }}
                  className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Save Title
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Change the project title to better identify this project
              </p>
            </div>

            {/* Status Section */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Status
              </label>
              <select
                value={projectStatus}
                onChange={(e) => handleUpdateStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="draft">Draft</option>
                <option value="in_progress">In Progress</option>
                <option value="final">Final</option>
                <option value="completed">Completed</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Change the project status to track its progress
              </p>
            </div>

            {/* Client Assignment Section */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assign Client
              </label>
              {assignedClient ? (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mb-3">
                  <p className="text-sm font-medium text-gray-900">{assignedClient.name || 'Client'}</p>
                  <p className="text-xs text-gray-600">{assignedClient.email}</p>
                </div>
              ) : (
                <p className="text-sm text-gray-500 mb-3">No client assigned</p>
              )}
              <ClientAssignmentForm 
                onAssign={handleAssignClient}
                existingClient={assignedClient}
              />
            </div>

            {/* Sheet scale (units + orientation) */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plan Sheet Scale
              </label>
              <div className="space-y-3">
                <div>
                  <div className="text-xs text-gray-600 mb-1">Units</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setSheetDraft((prev) => ({ ...(prev || sheet), units: 'in' }))}
                      className={`px-3 py-2 border rounded-lg text-sm font-semibold ${
                        (sheetDraft?.units || sheet.units) === 'in'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Imperial (in)
                    </button>
                    <button
                      type="button"
                      onClick={() => setSheetDraft((prev) => ({ ...(prev || sheet), units: 'cm' }))}
                      className={`px-3 py-2 border rounded-lg text-sm font-semibold ${
                        (sheetDraft?.units || sheet.units) === 'cm'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Metric (cm)
                    </button>
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-600 mb-1">Orientation</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setSheetDraft((prev) => ({
                          ...(prev || sheet),
                          width_in: 24,
                          height_in: 36
                        }))
                      }
                      className={`px-3 py-2 border rounded-lg text-sm font-semibold ${
                        ((sheetDraft?.width_in ?? sheet.width_in) === 24 && (sheetDraft?.height_in ?? sheet.height_in) === 36)
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                      title="24 in wide × 36 in high"
                    >
                      24×36 Portrait
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setSheetDraft((prev) => ({
                          ...(prev || sheet),
                          width_in: 36,
                          height_in: 24
                        }))
                      }
                      className={`px-3 py-2 border rounded-lg text-sm font-semibold ${
                        ((sheetDraft?.width_in ?? sheet.width_in) === 36 && (sheetDraft?.height_in ?? sheet.height_in) === 24)
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                      title="36 in wide × 24 in high"
                    >
                      36×24 Landscape
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={async () => {
                      const next = sheetDraft || sheet;
                      const ok = await saveSheetToProject(next);
                      if (ok) alert('Sheet settings saved');
                    }}
                    className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Save Sheet
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Grid remains 1-inch on the Base Map; Metric affects labels and symbol sizing inputs.
              </p>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowProjectSettings(false);
                  setEditingProjectName('');
                  setSheetDraft(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plant Sheet (in-app) */}
      {showPlantSheet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold">Plant Sheet</h3>
                <p className="text-xs text-gray-600">
                  Click a plant to jump to its placement on the plan (to scale).
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={generatePlantSheetPDF}
                  className="px-4 py-2 bg-sky-600 text-white rounded hover:bg-sky-700"
                  title="Export Plant Sheet PDF with links back to the plan"
                >
                  Export PDF
                </button>
                <button
                  type="button"
                  onClick={() => setShowPlantSheet(false)}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  ✕ Close
                </button>
              </div>
            </div>

            <div className="p-4 overflow-y-auto">
              {masterListItems.length === 0 ? (
                <div className="text-sm text-gray-600">No plants in this project yet.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {[...masterListItems]
                    .filter((it) => (placedByMasterListId[it?.id] || 0) > 0)
                    .sort((a, b) => String(a.common_name || '').localeCompare(String(b.common_name || ''), undefined, { sensitivity: 'base' }))
                    .map((it) => (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => {
                          setShowPlantSheet(false);
                          focusFirstPlacementForMasterListId(it.id);
                        }}
                        className="text-left border rounded-lg overflow-hidden hover:shadow-md hover:border-sky-300 transition"
                        title="Go to placement"
                      >
                        <div className="aspect-square bg-gray-50 flex items-center justify-center overflow-hidden">
                          {coerceImageUrl(it.image_url) ? (
                            <img src={coerceImageUrl(it.image_url)} alt={(it.common_name || it.botanical_name || 'Unknown')} className="w-full h-full object-cover" />
                          ) : (
                            <div className="text-xs text-gray-400">No image</div>
                          )}
                        </div>
                        <div className="p-2">
                          <div className="text-sm font-semibold text-gray-900 truncate">{it.common_name || it.botanical_name || 'Unknown'}</div>
                          <div className="text-[11px] text-gray-500 italic truncate">{it.botanical_name || ''}</div>
                          {it.manual_plan_zone_id ? (
                            <div
                              className="text-[10px] text-slate-600 mt-0.5 truncate font-semibold"
                              title={`Garden App zone: ${manualGardenZoneName(it.manual_plan_zone_id)}`}
                            >
                              Zone: {manualGardenZoneName(it.manual_plan_zone_id)}
                            </div>
                          ) : null}
                          <div className="text-xs text-gray-700 mt-1 font-semibold">Qty: {placedByMasterListId[it?.id] || 0}</div>
                        </div>
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI plant discovery moved to the Project Plant List page */}

      {/* PDF Preview Modal */}
      {showPdfPreview && pdfBlobUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold">Plant List Preview</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={downloadPDF}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  💾 Save PDF
                </button>
                <button
                  onClick={closePdfPreview}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  ✕ Close
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                src={pdfBlobUrl}
                className="w-full h-full border-0"
                title="PDF Preview"
              />
            </div>
          </div>
        </div>
      )}

      {/* Invite Collaborator Modal */}
      {showInviteModal && (
        <InviteCollaboratorModal
          projects={null} // No project selector - using current project
          selectedProjectId={id}
          setSelectedProjectId={() => {}} // Not used when projects is null
          defaultProjectId={id} // Pre-select current project
          inviteMode={inviteMode}
          setInviteMode={setInviteMode}
          inviteName={inviteName}
          setInviteName={setInviteName}
          inviteEmail={inviteEmail}
          setInviteEmail={setInviteEmail}
          inviteType={inviteTypeModal}
          setInviteType={setInviteTypeModal}
          tradeCategory={tradeCategory}
          setTradeCategory={setTradeCategory}
          messageVisibility={messageVisibility}
          setMessageVisibility={setMessageVisibility}
          scheduleAccess={scheduleAccess}
          setScheduleAccess={setScheduleAccess}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchLocation={searchLocation}
          setSearchLocation={setSearchLocation}
          searchType={searchType}
          setSearchType={setSearchType}
          searchResults={searchResults}
          searching={searching}
          designerLocation={designerLocation}
          onSearch={handleSearchBusinesses}
          onInviteFromSearch={handleInviteFromSearch}
          onAddBusiness={handleAddBusiness}
          onSubmit={handleInviteCollaborator}
          onClose={() => {
            setShowInviteModal(false);
            setInviteName('');
            setInviteEmail('');
            setInviteTypeModal('trade');
            setTradeCategory('contractor');
            setInviteMode('manual');
            setSearchResults([]);
            setSearchQuery('');
            setMessageVisibility('all');
            setScheduleAccess(true);
          }}
        />
      )}

      {showZoneManager && (
        <ZoneManager
          projectId={id}
          planId={activePlanId || null}
          planImageUrl={effectivePlanImageUrl}
          onClose={() => setShowZoneManager(false)}
        />
      )}

      {showHandoffModal && (
        <HandoffModal
          projectId={id}
          planId={activePlanId || null}
          projectName={project?.project_name || project?.name}
          defaultHomeownerEmail={project?.client?.email || project?.homeowner_email || ''}
          defaultHomeownerName={project?.client?.name || project?.homeowner_name || ''}
          onClose={() => setShowHandoffModal(false)}
        />
      )}

      {showProjectGardenTab && (
        <ProjectGardenTab
          projectId={id}
          projectName={project?.project_name || project?.name}
          onClose={() => setShowProjectGardenTab(false)}
        />
      )}
    </div>
  );
};

export default PlanDetail;
